/*********************************************************
 * Imports
 *********************************************************/
//import { ClearAll } from '@mui/icons-material'
import React, { useRef, useState, forwardRef, useImperativeHandle, useCallback, Fragment, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import pointInPolygon from 'point-in-polygon'
import { TypeObjectMap, TypeLayer, EnumIconSize } from './enums'
import { getGeocoder } from './googleGeocoder';
import Overlay from './google/Overlay';
import MarkerOverlay from './google/MarkerOverlay';
import InfoWindow from './google/InfoWindow';
import MapObject from './mapObject';
import MapRoute from './mapRoute';

import { cutNotes, convertJsonObject, assignOwnProps } from '../../../core/data/Helpers';
import _, { isArray } from 'lodash';
import { HasPermission } from "Core/security"
import { useSelector } from "react-redux";
import { getVidFleetEvent, getSwitnessEvent, getLightMetricEvent } from 'Core/data/VideoEvents';

import {
  findColor, convertSecondsToTime, createSquareBoundingboxByMilesDistance, radiusFeetToMeters,
  getEventColor, changeMarker, changePathMarker, clusterSvgString, polygonMarkerPointSvg,
  landmarkSvg, getIconGroupWithAnotherColor, validateLatLng, calcDistanceToPoints, getLatByDistance,
  getLngByDistance, getRouteColorFromSpeed,
  getRouteCoordinatesFromDirectionService, IconStringMarkerRoutePoint, createIconMarkerForRoute, createIconMarkerForRouteTracker,
  returnOptimizeRoutePoints, validateTrailTimeMins, validateTrailTimeMinsCompare, convertDateToSeconds,
  validateDirectionChange, calculateDistanceTraveled, createCircleEnvelop, createPolygonEnvelop,
  getUpdatedMarkerLabel, getGroupLineLabel, isWebGLAvailable /* , validatePolygon */
} from '../../Map/utils'
import { IconMarkerVehicleTrailsPoint, IconMarkerRoutesTrackerSmall } from '../../../components/Map/iconsReact'

import { endpoints } from 'Core/defaultValues';
import { Tooltip } from 'Components';
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { LogManagerConsole } from 'Core/logManager';
import { MarkerCompat } from './markerCompat';
import { WebGLMarkerLayer } from './webglMarkerLayer';
import { WebGLTrailLayer } from './webglTrailLayer';
import { NotificationManager } from 'Components/Notifications';
import TrafficToggle from '../components/TrafficToggle';
import WeatherToggle from '../components/WeatherToggle';
import moment from 'moment';
//import PolygonPoint from 'Components/Map/components/IconsSvg/PolygonPoint'
//import { ExceptionManager } from 'Core/logManager';
import {
  setListUnits,
  selectModuleMapSettings,
  setShowMapSettings,
  addPointers,
  setFollowUnit,
  getMapSpeedLimitsForPaths
} from "Redux/actions";
/*********************************************************
 * Icons
 *********************************************************/

/*********************************************************
 * Global Variables
 *********************************************************/
const mapObjects = []
// Index for O(1) lookups: key = `${type}::${id}` → mapObject reference
const mapObjectsIndex = new Map();

/** Build a lookup key for mapObjectsIndex */
function _indexKey(type, id) {
  return `${type}::${id}`;
}

/** Add an object to the index */
function _indexAdd(obj) {
  if (obj?.type != null && obj?.id != null) {
    mapObjectsIndex.set(_indexKey(obj.type, obj.id), obj);
  }
}

/** Remove an object from the index */
function _indexRemove(obj) {
  if (obj?.type != null && obj?.id != null) {
    mapObjectsIndex.delete(_indexKey(obj.type, obj.id));
  }
}

//const numDeltas = 60;
const zoomToEnableTrails = 13;
const trailsMinDistance = 100;
const trailsMaxDistance = 800;
const CENTER_UPDATE_INTERVAL = 100; // inverval in miliseconds
const clusterId = 'unitCluster';

// Flag: non-null when a followed unit is being animated via animateMarker.
// Tells centerMapAnimated to use instant setCenter instead of animated panTo
// so the viewport update doesn't fight with requestAnimationFrame.
let _animatingFollowUnit = null;

/**
 * Detect whether the browser can create a usable WebGL context.
 * Result is cached at module level — capability does not change during a session.
 * @returns {boolean}
 */
let _webglAvailable = null;
let _webglWarningShown = false;
function _isWebGLAvailable() {
  if (_webglAvailable !== null) return _webglAvailable;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) { _webglAvailable = false; return false; }
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    // Clean up the probing context to free GPU resources
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    _webglAvailable = maxTex > 0;
  } catch (_) {
    _webglAvailable = false;
  }
  return _webglAvailable;
}

/**
 * Toggle WebGL-based rendering for unit markers and trail polylines.
 * When true, unit markers are rendered in a single WebGL draw call via deck.gl
 * instead of individual google.maps.Marker instances.
 * This dramatically improves performance with 10,000+ markers.
 * Switched to `let` so it can be disabled at runtime when WebGL is unavailable.
 */
let USE_WEBGL_MARKERS = true;

let mapItem = null;
let customOverlay = null;
let kmlList = [];
let instance = null
let realStateInfoWindow = false;

let mapClickEvent = null;
let mapClusterEvent = null;
let mapClickRouteEvent = null;
let mapBoundsChangedEvent = null;
let mapClusterUpdateEvent = null; //walter paz
let mapClickItems = 0;
let onCallBackZoom = null;
let mapMouseUpEvent = null;
let mapMouseDownEvent = null;
const maxClusterZoom = 18;
let webglLayer = null;
let webglTrailLayer = null;     // WebGL-accelerated trail polyline renderer (deck.gl PathLayer)
let webglClickHandler = null;   // capture-phase click handler for manual deck.gl picking
let webglHoverRafId = null;     // rAF id for throttled hover picking
let webglHoverHandler = null;   // mousemove handler for manual deck.gl hover picking
let webglInteractionHandler = null; // user gesture handler (wheel/mousedown/touch) for centering pause
let webglMouseDownHandler = null;   // mousedown handler for drag-vs-click detection
let _mouseDownPos = null;           // { x, y } at mousedown — used to suppress click after drag

// Timestamp of last user map interaction (scroll, drag, pinch).
// While recent (< USER_INTERACTION_PAUSE_MS), per-frame centering is paused
// so the user can freely zoom/pan without the animation fighting their gesture.
let _lastUserMapInteraction = 0;

// Timestamp of last modal close — geofence/landmark click handlers suppress
// clicks within 300ms of closing to prevent the "click-through" re-open.
let _lastModalCloseAt = 0;
const USER_INTERACTION_PAUSE_MS = 1500; // ms to pause centering after a user gesture
let webglClusterLabelOverlays = []; // MarkerOverlay instances for cluster labels ("Label Clusters" feature)
let webglUnitLabelGroupOverlays = []; // MarkerOverlay instances for 5+ unit label groups in noOverlap WebGL mode
let webglRefreshTimer = null;
const WEBGL_REFRESH_INTERVAL = 250; // ms between WebGL re-renders

/** Schedule a throttled WebGL refresh (coalesces rapid updates). */
const scheduleWebGLRefresh = (updateFn) => {
  if (webglRefreshTimer) return; // already scheduled
  webglRefreshTimer = setTimeout(() => {
    webglRefreshTimer = null;
    updateFn();
  }, WEBGL_REFRESH_INTERVAL);
}; // deck.gl WebGL overlay for unit markers
const zoomLabelsPositions = 8;
let valueZoomNoOverlap = 0;
let lat1, lat2, lng1, lng2;
let eventMouseOverFirstPoint = null;
let eventMouseOutFirstPoint = null;
let eventMouseOverFirstMarker = null;
let eventMouseOutFirstMarker = null;
let startPointMarkerDragend = null;
let endPointMarkerDragend = null;
let eventInfoWindow = null;
let infoWindowMarker = null;
let durationRoute = 0;
const minimunSizeSquare = 0.0001;
let squareLatMin = 0, squareLngMin = 0;
let isWeatherEnabled = false;
let onEnableWeatherBounds = true;
let weatherLayersOnShow = null;
let weatherPermision = false;

/********************************************************
 * Global var to timer
 ********************************************************/
let timerRefreshWeather = null
let zoomChangedDebounceTimer = null; // debounce timer for zoom_changed label refresh
let mapZoomChangedEvent = null;      // zoom_changed listener reference

/*********************************************************
 * Css files / kml files
 *********************************************************/
import './style.scss'
import { useIntl } from 'react-intl';
import { useVideo } from '../../../modules/.common/hooks/useVideo';
import DOMPurify from 'dompurify';

/*********************************************************
 * Author:  Walter Paz Londoño
 * Date:    30 Nov 2020
 * Module:  Realtime Maps
 * 
 * Class associated to the google map provider
 ********************************************************/
const GoogleProvider = forwardRef((props, ref) => {

  const { messages, locale } = useIntl()

  //-------------- Ref Elements ----------------
  const divMap = useRef();
  const dispatch = useDispatch()
  instance = ref;

  //-------------- Declare the references ----------------
  //const [mapItem, setMapItem] = useState();
  const [traffic, setTraffic] = useState(null);
  const [aerisWeather, setAerisWeather] = useState(null);
  // ── InfoBubble rendering state (all refs, no useState) ──
  // Storing visibility, content, and overlay in refs and triggering InfoWindow
  // directly via forceUpdate() avoids re-rendering the entire 9k-line
  // GoogleProvider component on every modal open/close/switch.
  const infoBubbleContentRef = useRef(null);
  // Callback that triggers ONLY InfoWindow to re-render (registered by
  // InfoWindow via the registerUpdate prop in componentDidMount).
  const infoBubbleUpdateRef = useRef(null);
  // Tracks whether InfoBubble is loading marker details — read by InfoWindow
  // to render a top-bar LinearProgress indicator inside the modal.
  const infoBubbleLoadingRef = useRef(false);
  // Always-current reference to the CustomOverlay instance.
  const overlayRef = useRef(null);
  // Ref instead of state — only used for the identity guard in showMarkerModal,
  // never read during render. Avoids an extra setState per marker click.
  const currentMarkerModalRef = useRef(null);
  // Live-tracking indicator — shown when a followed unit is animating.
  // Uses direct DOM manipulation (ref) to avoid re-rendering the entire component.
  const liveIndicatorRef = useRef(null);
  // Follow-active indicator — shown when follow mode is ON (separate from LIVE).
  const followActiveIndicatorRef = useRef(null);
  // Animation-zone indicator — shown when zoom >= threshold + animation setting is ON.
  const animZoneIndicatorRef = useRef(null);
  const animationTrailPermissionRef = useRef(!!props?.animationTrailPermission);
  const showVehicleTrailsRef = useRef(!!props?.showVehicleTrails);
  // Hardware-acceleration user preference.  Defaults to true so existing
  // installations keep WebGL rendering until the user opts out.
  const _normalizeHwAccel = (v) => (v === undefined || v === null) ? true : Boolean(v);
  const hardwareAccelerationRef = useRef(_normalizeHwAccel(props?.hardwareAcceleration));
  const { clientWithVideo } = useVideo();

  
  // Create reference for followUnit and CenterUnitId, its necesary to update the value when the props change
  // because the props are not updated in the animation and this process need this variables
  const followUnitRef = useRef(props?.followUnit);
  const centerUnitIdRef = useRef(props?.centerUnitId);
  const openInfoWindowRef = useRef(false);

  // Stores the id of the last clicked polyline (does not trigger re-render)
  const selectedPolylineIdRef = useRef(null);

  // Always-current translated "View Detail..." text.
  // Read from this ref inside idle-handler closures so the text
  // stays in sync even when useCallback captures a stale `messages`.
  const viewDetailTextRef = useRef(messages['map_view_detail'] || 'View Detail...');

  // Update the reference then the props have changes
  useEffect(() => {
    followUnitRef.current = props?.followUnit;
    // Hide live indicator when follow mode is turned off
    if (!props?.followUnit) {
      _hideLiveIndicator();
      _hideFollowActiveIndicator();
    } else if (props?.centerUnitId && mapItem) {
      // Show follow-active indicator immediately when follow is turned on
      _showFollowActiveIndicator();
      const unitKey = _indexKey(TypeObjectMap.Marker, props.centerUnitId);
      const mapObj = mapObjectsIndex.get(unitKey);
      let lat, lng;
      const latestPosition = mapObj?.endPosition || mapObj?.position;
      const parsedLat = parseFloat(latestPosition?.lat);
      const parsedLng = parseFloat(latestPosition?.lng);
      if (!isNaN(parsedLat) && !isNaN(parsedLng) && !(parsedLat === 0 && parsedLng === 0)) {
        lat = parsedLat;
        lng = parsedLng;
      } else if (mapObj?.instance?.getPosition) {
        const pos = mapObj.instance.getPosition();
        lat = pos?.lat();
        lng = pos?.lng();
      }
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng) && !(Number(lat) === 0 && Number(lng) === 0)) {
        // If the current zoom is below the animation threshold, zoom in
        // so the user can see the marker animating (Follow implies watching
        // the unit move).  centerPointOnMap applies setZoom + panTo
        // atomically so the map reaches the correct level.
        const currentZoom = mapItem.getZoom?.() ?? 0;
        if (currentZoom < zoomToEnableTrails) {
          centerPointOnMap(lat, lng, 16, -190);
        } else {
          centerMapAnimated(lat, lng);
        }
      } else {
        // Early follow clicks can happen while the modal is still loading and
        // before this effect can resolve the marker coordinates from
        // mapObjectsIndex. Fall back to the canonical centering helper so the
        // click still zooms/centers once the marker data is available.
        centerObjectOnMap(TypeObjectMap.Marker, props.centerUnitId, 16, -190);
      }

      // Show LIVE indicator immediately if the unit is currently animating
      // AND we're at a zoom where animations are visible.
      // (otherwise it only appears on the next _startNextWebGLAnim call)
      const afterZoom = mapItem.getZoom?.() ?? 0;
      if (mapObj?._webglAnim?.stopAnim && afterZoom >= zoomToEnableTrails) {
        _showLiveIndicator(props.centerUnitId);
      }
    }
  }, [props?.followUnit, mapItem]);

  useEffect(() => {
    centerUnitIdRef.current = props?.centerUnitId;
  }, [props?.centerUnitId]);

  // Keep animation-trail permission ref in sync and update indicator immediately.
  useEffect(() => {
    animationTrailPermissionRef.current = !!props?.animationTrailPermission;
    if (mapItem) {
      _updateAnimZoneIndicator(mapItem.getZoom?.() ?? 0);
    }
  }, [props?.animationTrailPermission, mapItem]);

  // Keep showVehicleTrails ref in sync — hide animation-zone indicator
  // when Vehicle Trails view is active (animations don't apply there).
  useEffect(() => {
    showVehicleTrailsRef.current = !!props?.showVehicleTrails;
    if (mapItem) {
      _updateAnimZoneIndicator(mapItem.getZoom?.() ?? 0);
    }
  }, [props?.showVehicleTrails, mapItem]);

  // Keep hardware-acceleration ref in sync.  Switching this preference
  // requires recreating the google.maps.Map (mapId can't change at
  // runtime), which the settings panel handles via window.location.reload().
  useEffect(() => {
    hardwareAccelerationRef.current = _normalizeHwAccel(props?.hardwareAcceleration);
  }, [props?.hardwareAcceleration]);

  /**
   * Show the live-tracking indicator badge on the map.
   * @param {string} unitId - The unit being tracked (for future use).
   */
  function _showLiveIndicator(unitId) { // eslint-disable-line no-unused-vars
    // Only show at zoom levels where animations actually run.
    // Below zoomToEnableTrails, markers snap instead of animating,
    // so the LIVE badge would be misleading.
    const currentZoom = mapItem?.getZoom?.() ?? 0;
    if (currentZoom < zoomToEnableTrails) return;
    const el = liveIndicatorRef.current;
    if (el) el.style.display = 'flex';
  }

  /** Hide the live-tracking indicator badge. */
  function _hideLiveIndicator() {
    const el = liveIndicatorRef.current;
    if (el) el.style.display = 'none';
  }

  /** Show the follow-active indicator badge on the map. */
  function _showFollowActiveIndicator() {
    const el = followActiveIndicatorRef.current;
    if (el) el.style.display = 'flex';
  }

  /** Hide the follow-active indicator badge. */
  function _hideFollowActiveIndicator() {
    const el = followActiveIndicatorRef.current;
    if (el) el.style.display = 'none';
  }

  /**
   * Update the animation-zone indicator visibility.
   * Shows when: animation setting is ON + zoom >= zoomToEnableTrails.
   * Uses direct DOM to avoid re-rendering the 9k-line component on every zoom.
   */
  function _updateAnimZoneIndicator(currentZoom) {
    const el = animZoneIndicatorRef.current;
    if (!el) return;
    const animEnabled = animationTrailPermissionRef.current;
    // Hide when Vehicle Trails is active — trail playback is a different
    // feature from real-time animation and showing the badge is misleading.
    const isVehicleTrails = showVehicleTrailsRef.current;
    const shouldShow = animEnabled && !isVehicleTrails && currentZoom >= zoomToEnableTrails;
    el.style.display = shouldShow ? 'flex' : 'none';
  }

  // When the locale changes, keep the ref current and patch existing overlays.
  const viewDetailTranslation = messages['map_view_detail'] || 'View Detail...';
  viewDetailTextRef.current = viewDetailTranslation;

  useEffect(() => {
    // Update the WebGL layer's cached text for future overlay creation
    if (USE_WEBGL_MARKERS && webglLayer) {
      webglLayer.setViewDetailText(viewDetailTranslation);
    }

    // Patch all "View Detail..." links already on the DOM
    try {
      document.querySelectorAll('.buttonModalGroupsText').forEach((el) => {
        el.innerText = viewDetailTranslation;
      });
    } catch (e) { /* non-fatal */ }
  }, [viewDetailTranslation]);

  //Global state
  const user = useSelector((state) => state.securityRedux.user);
  const pathFile = `https://s3.amazonaws.com/${endpoints.PUBLIC_REPOSITORY_CODE}`
  weatherPermision = props?.weatherPermision || false

  //-------------- Global Vars ----------------
  useImperativeHandle(ref, () => ({
    initMap: initMap,
    restartMap: restartMap,
    addMapObjects: addMapObjects,
    addMapObject: addMapObject,
    addMapObjectsInstancebyLayer: addMapObjectsInstancebyLayer,
    addMapObjectsWihoutInstance: addMapObjectsWihoutInstance,
    removeMapObject: removeMapObject,
    removeMapObjects: removeMapObjects,
    showMapObject: showMapObject,
    showMapObjectByLayer: showMapObjectByLayer,
    removeAll: removeAll,
    removeAllbyItemObject: removeAllbyItemObject,
    removeItemsByIdAndType: removeItemsByIdAndType,
    showLayer: showLayer,
    removeLayer: removeLayer,
    updateObject: updateObject,
    updateMarkerOfflineDevice: updateMarkerOfflineDevice,
    updateMarkerIconMap: updateMarkerIconMap,
    showMarkerCluster: showMarkerCluster,
    showNormalMarkers: showNormalMarkers,
    setSizeMarkerIcon: setSizeMarkerIcon,
    mapInstance: mapInstance,
    showMarkerModal: showMarkerModal,
    hideMarkerModal: hideMarkerModal,
    getCurrentMarkerModal: () => currentMarkerModalRef.current,
    getGeocoder: getGeocoder,
    getObject: getObject,
    getObjectsByType: getObjectsByType,
    getObjectsByTypeByLayer: getObjectsByTypeByLayer,
    centerObjectOnMap: centerObjectOnMap,
    updateMapType: updateMapType,
    countObject: countObject,
    addKml: addKml,
    addKmlObject: addKmlObject,
    showVehicleTrails: showVehicleTrails,
    centerPointOnMap: centerPointOnMap,
    showStreetView: showStreetView,
    centerByFitBounds: centerByFitBounds,
    centerVehicles: centerVehicles,
    initMapClick: initMapClick,
    removeMapClick: removeMapClick,
    AddPolygonPoint: AddPolygonPoint,
    getPolygonCoordinates: getPolygonCoordinates,
    getCircleCoordinates: getCircleCoordinates,
    reduceMapClickItems: reduceMapClickItems,
    movePolygonPoint: movePolygonPoint,
    removePolygonPoint: removePolygonPoint,
    updateCirclePoint: updateCirclePoint,
    updateSquarePoint: updateSquarePoint,
    setUpdateSquareDiagonally: setUpdateSquareDiagonally,
    updateStyleToCreateObject: updateStyleToCreateObject,
    drawRoute: drawRoute,
    getPositionMarker, getPositionMarker,
    getCurrentZoom: getCurrentZoom,
    setZoom: setZoom,
    setZoomOut: setZoomOut,
    getZoom: getZoom,
    drawLandmark: drawLandmark,
    getOpenInfoWindow: getOpenInfoWindow,
    centerMapAnimated: centerMapAnimated,
    addPolylinePoint: addPolylinePoint,
    addCircleOnMapToCreate: addCircleOnMapToCreate,
    addSquareOnMapToCreate: addSquareOnMapToCreate,
    removeLabelOverlay, removeLabelOverlay,
    addLabelMarkers: addLabelMarkers,
    showOrHideLabelsOnCluster: showOrHideLabelsOnCluster,
    removeMapClusterListener: removeMapClusterListener,
    addMapObjectSettings: addMapObjectSettings,
    setEnableObjectsByIds: setEnableObjectsByIds,
    setEnableObjectsByType: setEnableObjectsByType,
    drawPolygon: drawPolygon,
    drawShapeObjectTempOnMap: drawShapeObjectTempOnMap,
    removeMapDraw: removeMapDraw,
    removeRouteListener: removeRouteListener,
    onSuscribeZoom: onSuscribeZoom,
    mapItem: mapItem,
    dataUnits: props.dataUnits,
    updateModalPosition: updateModalPosition,
    getDurationRoute: getDurationRoute,
    updateTypeSquareToFreeRectangle: updateTypeSquareToFreeRectangle,
    updateRouteStyles: updateRouteStyles,
    getGeometryEncoded: getGeometryEncoded,
    drawPolygonWithEncodedPath: drawPolygonWithEncodedPath,
    showTraffic: showTraffic,
    hideTraffic: hideTraffic,
    showWeather: showWeather,
    hideWeather: hideWeather,
    addMapClickRouteListener: addMapClickRouteListener,
    removeMapClickRouteListener: removeMapClickRouteListener,
    drawHistoryTrailOnMap: drawHistoryTrailOnMap,
    updateMarkersVisible: updateMarkersVisible,
    updateUnitsAndClusterInIdle: updateUnitsAndClusterInIdle,
    updateWebGLMarkers: updateWebGLMarkers,
    ensureWebGLVisible: ensureWebGLVisible,
    reproduceTrailEvents: reproduceTrailEvents,
    focusStopTrailMarkersAnimations, focusStopTrailMarkersAnimations,
    focusStartTrailMarkerAnimations, focusStartTrailMarkerAnimations,
    // Shared refs so InfoBubble can signal loading state to InfoWindow
    infoBubbleLoadingRef,
    infoBubbleUpdateRef,
    // Debug utility: call from console via mapRef.current.debugMarker('unitId')
    debugMarker: (id) => webglLayer?.debugMarker?.(id),
  }))

  /*********************************************************
   * Return the instance of the map
   *********************************************************/
  const mapInstance = () => {
    return (mapItem) ? true : false;
  }

  const onSuscribeZoom = (paramFunction) => {
    onCallBackZoom = paramFunction;
  }

  /*********************************************************
   * Add new mapObject into the realtime maps
   *********************************************************/
  const addMapObject = useCallback((mapObject, createInstance = true, evalInstance = false) => {
    switch (mapObject.type) {
      case TypeObjectMap.Marker:
        mapObject.instance = addMarker(mapObject);
        break;

      case TypeObjectMap.Polyline:
        // Route VehicleTrails polylines to the WebGL trail layer for
        // GPU-accelerated rendering (single draw call vs N DOM polylines).
        if (webglTrailLayer && mapObject.layerName === TypeLayer.VehicleTrails) {
          webglTrailLayer.addSegment(mapObject);
          // Store a lightweight stub so removeLayer / showMapObjectByLayer
          // still find this object in the mapObjects array.
          mapObject.instance = { setMap: () => {}, _webglTrail: true };
        } else {
          mapObject.instance = addPolyline(mapObject, evalInstance);
        }
        break;
      case TypeObjectMap.Polygon:
        mapObject.instance = addPolygon(mapObject);
        break;

      case TypeObjectMap.Circle:
        mapObject.instance = addCircle(mapObject, true);
        break;

      case TypeObjectMap.Rectangle:
        mapObject.instance = addRectangle(mapObject, true);
        break;

      case TypeObjectMap.Square:
        mapObject.instance = addSquare(mapObject, true);
        break;

      case TypeObjectMap.KmlFile:
        mapObject.instance = addKml(mapObject);
        break;

      case TypeObjectMap.Routes:
        mapObject.instance = addRoute();
        break;

      case TypeObjectMap.Label:
        mapObject.instance = addLabel(mapObject);
        break;

      case TypeObjectMap.Landmark:
        mapObject.instance = addImageMapType(mapObject, createInstance);
        if (mapObject.childType == TypeObjectMap.Circle) {
          mapObject.childInstance = addChildLandmarkCircle(mapObject, createInstance)
        }

        if (mapObject.childType == TypeObjectMap.Polygon) {
          mapObject.childInstance = addChildLandmarkPolygon(mapObject, createInstance)
        }
        break;

      case TypeObjectMap.Geofence:
        if (mapObject.childType == TypeObjectMap.Circle) {
          mapObject.instance = addChildLandmarkCircle(mapObject, createInstance)
        }

        if (mapObject.childType == TypeObjectMap.Polygon) {
          mapObject.instance = addChildLandmarkPolygon(mapObject, createInstance)
        }
        break;
    }

    if (mapObject.instance && createInstance) {
      mapObjects.push(mapObject)
      _indexAdd(mapObject);

      // Trigger WebGL refresh when a single unit marker is added
      if (USE_WEBGL_MARKERS && !props.isEtaLink && webglLayer && mapObject.layerName === TypeLayer.Units) {
        scheduleWebGLRefresh(updateWebGLMarkers);
      }

      return mapObject //return actual object created
    }
    return null
  })

  /*********************************************************
   * Add new mapObject into the realtime maps
   *********************************************************/
  const addMapObjects = useCallback((elements) => {

    elements.forEach((item) => {
      if (validateNewObject(item) == true) {
        addMapObject(item)
      }
    })

    // After batch add, refresh WebGL layer if any units or route stops were added
    if (USE_WEBGL_MARKERS && webglLayer) {
      const hasWebGLItems = elements.some(item =>
        item.layerName === TypeLayer.Units || item.layerName === TypeLayer.FSMRoute
      );
      if (hasWebGLItems) {
        updateWebGLMarkers();
      }
    }
  })

  /*********************************************************
   * Remove the object from the map and array MapObjects
   * @param {int} id of mapObject 
   * @param {TypeObjectMap} type for TypeObjectMap of mapObject 
   *********************************************************/
  const removeMapObject = useCallback((id, type) => {
    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      if (mapObject.id == id && mapObject.type == type) {
        try {
          // Remove from WebGL trail layer if it's a trail polyline
          if (mapObject.instance?._webglTrail && webglTrailLayer) {
            webglTrailLayer.removeSegment(id);
          }
          if (mapObject.instance?.setMap) {
            mapObject.instance.setMap(null);
          }
          mapObject.instance = null;
          if (mapObject.childInstance?.setMap) {
            mapObject.childInstance.setMap(null);
          }
          mapObject.childInstance = null;
        }
        catch (err) {
          console.log("remov:::: ", err);
        }
        _indexRemove(mapObject);
        mapObjects.splice(i, 1);
      }
    }
  })

  /*********************************************************
   * Remove the object from the map and array MapObjects
   *********************************************************/
  const removeMapObjects = useCallback((type) => {
    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      if (mapObject.type == type) {
        try {
          if (mapObject.instance?.setMap) {
            mapObject.instance.setMap(null);
          }
          mapObject.instance = null;
          if (mapObject.childInstance?.setMap) {
            mapObject.childInstance.setMap(null);
          }
          mapObject.childInstance = null;
        }
        catch (err) {
          console.log("remov:::: " , err);
        }
        _indexRemove(mapObject);
        mapObjects.splice(i, 1);
      }
    }
  })

  /*********************************************************
   * Show or Hide the layer elements into the realtime maps
   *********************************************************/
  const showMapObject = useCallback((id, type, show = false) => {
    mapObjects.forEach((item) => {
      if (item.id == id && item.type == type) {
        item.hideMarker = !show;
        // In WebGL mode, unit markers stay hidden (setMap(null)) — WebGL layer renders them.
        if (USE_WEBGL_MARKERS && !props.isEtaLink && webglLayer && item.layerName === TypeLayer.Units) {
          item.instance?.setMap(null);
        } else {
          item.instance.setMap(show && item?.enable ? mapItem : null)
          if (item.hasChild) {
            item.childInstance.setMap(show && item?.enable ? mapItem : null)
          }
        }
      }
    })
    // Refresh WebGL so visibility flags take effect
    if (USE_WEBGL_MARKERS && webglLayer) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  })

  /*********************************************************
   * Show or Hide the layer elements into the realtime maps with object and layer
   *********************************************************/
  const showMapObjectByLayer = useCallback((type, layer, show = false) => {
    // WebGL trail layer handles VehicleTrails polyline visibility
    if (webglTrailLayer && type === TypeObjectMap.Polyline && layer === TypeLayer.VehicleTrails) {
      webglTrailLayer.setSegmentsVisible(show);
    }

    mapObjects.forEach((item) => {
      if (item.type == type && layer == item?.layerName) {
        item.hideMarker = !show;
        // In WebGL mode, unit markers stay hidden — WebGL layer renders them.
        if (USE_WEBGL_MARKERS && !props.isEtaLink && webglLayer && item.layerName === TypeLayer.Units) {
          item.instance?.setMap(null);
        } else if (item.instance?._webglTrail) {
          // WebGL trail stub — visibility handled by webglTrailLayer above
        } else {
          item.instance.setMap(show ? mapItem : null)
          if (item.hasChild) {
            item.childInstance.setMap(show ? mapItem : null)
          }
        }
      }
    })
    if (USE_WEBGL_MARKERS && webglLayer) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  })

  /*********************************************************
   * Counst the object from type element
   *********************************************************/
  const countObject = useCallback((type, layer) => {
    const elements = mapObjects.filter((item) => item.type == type && item.layerName == layer)
    return elements ? elements.length : 0
  })

  /*********************************************************
   * Add mapObjects into array wihtout instance
   *********************************************************/
  const addMapObjectsWihoutInstance = useCallback((elements) => {
    elements.forEach((item) => {
      if (validateNewObject(item) == true) {
        mapObjects.push(item)
        _indexAdd(item);
      }
    })
  })

  /*********************************************************
  * Add Instance to landmarks objects
  *********************************************************/
  const addMapObjectsInstancebyLayer = useCallback((id, layer) => {
    if (!layer) return;
    //get elements to create instance.
    const elementsToInstance = mapObjects.filter(item => item.layerName == layer && !item.instance);

    if (elementsToInstance && elementsToInstance.length > 0) {
      elementsToInstance.forEach((item) => {
        addMapObject(item, false);
      })

      // Refresh WebGL layer when unit instances are created
      if (USE_WEBGL_MARKERS && webglLayer && layer === TypeLayer.Units) {
        updateWebGLMarkers();
      }
    }
    showLandmarksGeofenceByZoom(id, mapItem.getZoom());

  })

  /*********************************************************
   * Update the icon  to the offline devices
   *********************************************************/
  const updateMarkerOfflineDevice = useCallback((id, offlineStatus) => {
    let mapObject = mapObjects.find((item) => {
      if (item.id == id && item.type == TypeObjectMap.Marker) {
        return item;
      }
    })
    if (!mapObject) {
      return
    }

    mapObject.offline = offlineStatus;
    if (offlineStatus) {
      mapObject.offline = offlineStatus;
      const styleOffline = {
        // This marker is 20 pixels wide by 32 pixels high.
        scaledSize: new google.maps.Size(40, 40), // eslint-disable-line no-undef
        // The origin for this image is (0, 0).
        origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
        // The anchor for this image is the base of the flagpole at (0, 32).
        anchor: new google.maps.Point(20, 20), // eslint-disable-line no-undef
      }

      const iconMarker = getIconAttributes(mapObject.color, 0, '/assets/icons/offlineimage.png', styleOffline)
      mapObject.instance.setIcon(iconMarker)

    } else {
      if (!mapObject.icon) {
        const iconMarker = getIconAttributes(mapObject.color, mapObject.heading, null, null, mapObject)
        mapObject.instance.setIcon(iconMarker)
      }else{
        //load the icon status saved into the vehicle
        const iconMarker = getIconAttributes(mapObject.color, 0, mapObject.icon, false)
        mapObject.instance.setIcon(iconMarker)
      }
    }

    // Refresh WebGL layer so the offline/online icon change is visible
    if (USE_WEBGL_MARKERS && webglLayer) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  })

  /*********************************************************
   * Change the icon at the marker on map
   *********************************************************/
  const updateMarkerIconMap = useCallback((id, iconPath) => {
    let mapObject = mapObjects.find((item) => {
      if (item.id == id && item.type == TypeObjectMap.Marker) {
        return item;
      }
    })
    if (!mapObject) {
      return
    }

    if (!iconPath) {
      mapObject.icon = ''
      // Refresh WebGL so the marker reverts from custom icon to default arrow
      if (USE_WEBGL_MARKERS && webglLayer) {
        scheduleWebGLRefresh(updateWebGLMarkers);
      }
      return
    }

    const styleOffline = {
      // This marker is 20 pixels wide by 32 pixels high.
      scaledSize: new google.maps.Size(28, 28), // eslint-disable-line no-undef
      // The origin for this image is (0, 0).
      origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
      // The anchor for this image is the base of the flagpole at (0, 32).
      anchor: new google.maps.Point(14, 14), // eslint-disable-line no-undef
    }

    mapObject.icon = `/assets/icons/${iconPath}`;
    const iconMarker = getIconAttributes(mapObject.color, 0, mapObject.icon, styleOffline)
    mapObject.instance.setIcon(iconMarker)

    // Refresh WebGL layer so the custom icon change is visible
    if (USE_WEBGL_MARKERS && webglLayer) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  })

  const sameArraysTrail = (array1, array2) => {
    // Step 1: Verify longitude to the arrays
    if (array1.length !== array2.length) {
        return false;
    }

    // Step 2: Comparar las posiciones
    for (let i = 0; i < array1.length; i++) {
        const pos1 = array1[i];
        const pos2 = array2[i];

        if (pos1.lat !== pos2.lat || pos1.lng !== pos2.lng) {
            return false; // Find the diferent position
        }
    }

    // the arrays is the same
    return true;
  }

  const compareArrayTrailAndReturnPosition = (trailArray, element) => {
    trailArray.forEach((item, index) => {
      if(item.lat == element.lat && item.lng == element.lng)
        containsData = index;
    })
    return -1
  }

  function removeDupply(array) {
    const uniqueCoordinates = new Set();
    return array.filter(obj => {
        const clave = `${obj.lng}-${obj.lat}`;
        if (!uniqueCoordinates.has(clave)) {
            uniqueCoordinates.add(clave);
            return true;
        }
        return false;
    });
  }

  const reproduceTrailEvents = useCallback(()  => {

    //get the events with events in stack
    let mapElements = mapObjects.filter((item) => item?.newEvents?.length > 0 && item?.isAnimated == false)
    mapElements.forEach((mapElement) => {
      let firstEvent = mapElement.newEvents.shift();
      updateObject(firstEvent , TypeObjectMap.Marker);
    })
  });


  /**********************************************************
   * If window lose focus, stop the animation
   **********************************************************/
  const focusStopTrailMarkersAnimations = useCallback(()=> {

    // ── Legacy DOM animations ──
    let mapElementsWithArrays = mapObjects.filter((item) => item.isAnimated == true)
    mapElementsWithArrays?.forEach((item) => {
      item.isAnimated = false;
      item.stopAnimation = true;
      if(item.stopAnimationFunction){
        item.stopAnimationFunction()
      }
    })

    // ── WebGL rAF animations ──
    // Cancel all running WebGL animations so markers stop moving immediately.
    // Snap each animated marker to its endPosition so it doesn't stay at a
    // mid-interpolation coordinate. Only markers with active animations are
    // touched — all other markers remain exactly where they are.
    if (USE_WEBGL_MARKERS && webglLayer) {
      mapObjects.forEach((item) => {
        const anim = item._webglAnim;
        if (anim?.stopAnim) {
          anim.stopAnim();    // cancel the rAF loop
          anim.stopAnim = null;
          anim.queue.length = 0;
          anim.lastAnimTime = 0;
          anim.isTrailSequence = false;
          item.stopAnimation = true;

          // Snap to endPosition immediately so the marker isn't stuck
          // at a mid-animation coordinate until focus restores.
          const snapPos = item.endPosition || item.position;
          if (snapPos?.lat != null && snapPos?.lng != null) {
            item.position = { lat: parseFloat(snapPos.lat), lng: parseFloat(snapPos.lng) };
            if (item.instance?.setPosition) {
              try {
                item.instance.setPosition(new google.maps.LatLng(item.position.lat, item.position.lng)); // eslint-disable-line no-undef
              } catch (_) { /* */ }
            }
          }
        }
      });
      // Clear all animation tracking in the WebGL layer (labels become visible)
      webglLayer.clearStaleAnimations();
      // Pause cluster reconciliation timer while the tab is hidden —
      // no point recalculating clusters when the map isn't visible.
      webglLayer.pauseClusterReconcile();
      _hideLiveIndicator();
    }

    return mapElementsWithArrays
  })

  /**********************************************************
   * Update the marker to the last position of the unit
   * Called when the browser tab regains focus. Snaps all
   * markers to their most recent position without animation.
   **********************************************************/
  const focusStartTrailMarkerAnimations = useCallback(()=> {
    let firstEvent = null;

    // ── WebGL mode: targeted snap ──
    // Only update markers that had active animations (stopAnimation) or
    // received new events while the tab was hidden. Leave all other markers
    // completely untouched so they remain visible on the map.
    // Avoids calling updateObject() which replaces the mapObjects entry via
    // spread and runs through the full event pipeline — that path can cause
    // position corruption and marker hiding on tab restore.
    if (USE_WEBGL_MARKERS && webglLayer) {
      mapObjects.forEach((item) => {
        if (item.type !== TypeObjectMap.Marker) return;

        const hadAnimation = item.stopAnimation === true;
        const hasNewEvents = item?.newEvents?.length > 0;

        // Skip markers that had no animation and no pending events —
        // they are already at their correct position and should not be touched.
        if (!hadAnimation && !hasNewEvents) return;

        // Determine the most recent position:
        // 1. From the newest queued event (arrived while tab was hidden)
        // 2. From endPosition (the animation's target when it was cancelled)
        // 3. Current position (fallback, should already be correct)
        let latestPosition = null;

        if (hasNewEvents) {
          const mostRecent = item.newEvents.reduce((max, current) => {
            if (!max || parseInt(current.unitTime) > parseInt(max.unitTime)) {
              return current;
            }
            return max;
          }, null);

          if (mostRecent?.position?.lat != null && mostRecent?.position?.lng != null) {
            latestPosition = mostRecent.position;
          }

          // Apply non-position data from the most recent event (eventName, color, heading, etc.)
          if (mostRecent) {
            const { trail, newEvents, position, instance, _webglAnim, ...safeProps } = mostRecent;
            assignOwnProps(item, safeProps);
          }

          // Clear the events queue
          item.newEvents = [];
        }

        // Fall back to endPosition (animation target) or current position
        if (!latestPosition) {
          latestPosition = item.endPosition || item.position;
        }

        // Snap position directly on the mapObject and its MarkerCompat instance
        if (latestPosition?.lat != null && latestPosition?.lng != null) {
          item.position = { lat: parseFloat(latestPosition.lat), lng: parseFloat(latestPosition.lng) };
          if (item.instance?.setPosition) {
            try {
              item.instance.setPosition(new google.maps.LatLng(item.position.lat, item.position.lng)); // eslint-disable-line no-undef
            } catch (_) { /* */ }
          }
        }

        // Clean up animation and stop state
        item.stopAnimation = false;
        if (item._webglAnim) {
          item._webglAnim.stopAnim = null;
          item._webglAnim.queue.length = 0;
          item._webglAnim.lastAnimTime = 0;
          item._webglAnim.isTrailSequence = false;
        }
      });

      // Resume cluster reconciliation now that the tab is visible again
      webglLayer.resumeClusterReconcile();

      // Force TextLayer font atlas rebuild — the GPU may have evicted
      // SDF atlas textures while the tab was hidden, causing white label
      // boxes with no text.  Must be called BEFORE updateWebGLMarkers()
      // so the render creates fresh TextLayer instances with new IDs.
      webglLayer.invalidateFontAtlas();

      // Single WebGL refresh renders all markers at their correct positions
      updateWebGLMarkers();
      return; // Skip legacy DOM logic below — not needed in WebGL mode
    }

    // ── Legacy DOM path (non-WebGL) ──
    //update the marker to last position.
    let mapElementsStopedArray = mapObjects.filter((item) => item.stopAnimation)
    mapElementsStopedArray.forEach((item) => {
      item.stopAnimation = false;
      if(item?.newEvents?.length > 0){

        firstEvent = item?.newEvents?.reduce((maxObject, currentObject) => {
          // Get the most recent data for the queue
          if (!maxObject || parseInt(currentObject.unitTime) > parseInt(maxObject.unitTime)) {
            return currentObject;
          }
          // Otherwise, keep the current maxObject
          return maxObject;
        }, null);

        firstEvent = {
          ...firstEvent,
          trail: [] //create a automatic update without the movement
        }

        //update the objects to the last position
        item.newEvents = []
        updateObject(firstEvent , TypeObjectMap.Marker);

      }else{
        firstEvent = {
          ...item,
          position: item?.endPosition || item?.position,
          trail: [] //create a automatic update without the movement
        }
        //update the objects to the last position
        updateObject(firstEvent , TypeObjectMap.Marker);
      }
    })


    //clear the stack for trails and shows more recent.
    let mapElementsTrailsArray = mapObjects.filter((item) => item?.newEvents?.length > 0)
    mapElementsTrailsArray.forEach((item) => {
      item.stopAnimation = false;
      firstEvent = item?.newEvents?.reduce((maxObject, currentObject) => {
        // Get the most recent data for the queue
        if (!maxObject || parseInt(currentObject.unitTime) > parseInt(maxObject.unitTime)) {
          return currentObject;
        }
        // Otherwise, keep the current maxObject
        return maxObject;
      }, null);

      item.newEvents = []

      firstEvent = {
        ...firstEvent,
        trail: [] //create a automatic update without the movement
      }

      //update the objects to the last position
      updateObject(firstEvent , TypeObjectMap.Marker);
    })

  })

  /***********************************************************
  * Updates the visibility of a map element based on its position relative to the current map bounds.
  * If the element is outside the bounds, it is hidden. If it is inside, it is displayed.
  *
  * @param {Object} mapElement - The map object to evaluate (e.g., marker, label).
  * @param {Object} mapObjectParam - Additional parameters for the map object, including position.
  **********************************************************/
  const createInstanceWithBoundaryOnMap = useCallback((mapElement, mapObjectParam) => {
    // In WebGL mode the GPU handles viewport culling — always report visible
    if (USE_WEBGL_MARKERS && webglLayer) {
      return true;
    }

    // Get the current map bounds
    const currentBounds = mapItem.getBounds();
    if (!mapItem || !currentBounds) return false;

    let isValidPosition = false, isValidLastPosition = false, isValidNewPosition = false;
    let latInstance, lngInstance;

    // Check if the map element is a marker and belongs to the "Units" layer
    if (mapElement.type === TypeObjectMap.Marker && mapElement.layerName === TypeLayer.Units && mapElement?.instance && mapElement?.enable && !mapElement?.hideMarker) {
      // Get the marker's current position or fallback to its default position
      latInstance = mapElement?.instance?.getPosition()?.lat() || mapElement?.position?.lat;
      lngInstance = mapElement?.instance?.getPosition()?.lng() || mapElement?.position?.lng;

      const position = new google.maps.LatLng(latInstance, lngInstance); // eslint-disable-line no-undef

      // Check if the position is within the current map bounds
      isValidLastPosition = currentBounds.contains(position);

      // If the position is invalid, check the fallback position from mapObjectParam
      latInstance = mapObjectParam?.position?.lat || 0;
      lngInstance = mapObjectParam?.position?.lng || 0;
      isValidNewPosition = currentBounds.contains(new google.maps.LatLng(latInstance, lngInstance)); // eslint-disable-line no-undef

      isValidPosition = isValidLastPosition || isValidNewPosition;

      // if the cluster is active and the previous position is valid and the new position is valid, the marker should not be displayed, isValidPosition must be false
      //get the active cluster
      const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
      if (objectCluster && objectCluster?.instance && isValidLastPosition && isValidNewPosition) {
        return;
      }

      // Update the visibility of the marker based on its position
      mapElement?.instance?.setMap(isValidPosition ? mapItem : null);

      // Update the visibility of any child elements (e.g., labels)
      if (mapElement?.hasChild) {
        mapElement?.childInstance?.setMap(isValidPosition ? mapItem : null);
      }

      // Handle the visibility of the associated label
      const objectLabel = getObject(TypeObjectMap.Label, `label_${mapElement?.id}`);
      if (objectLabel) {
        if (isValidPosition) {
          objectLabel.instance.onDisplayObject(true, mapItem);
        } else {
          objectLabel?.instance?.onDisplay(false);
          objectLabel?.instance?.setMap(null);
        }
      }
    }
    return isValidPosition;
  }, [mapItem]);

  /*********************************************************
   * Update objects into the realtime maps
   *********************************************************/
  const updateObject = useCallback((mapObjectParam, type) => {
    let index = -1;
    let mapElement = mapObjects.find((item, i) => {
      if (item.id == mapObjectParam.id && item.type == type) {
        index = i;
        return item;
      }
    })
    if (!mapElement || index < 0) {
      return
    }

    //validate unitTime
    if(mapObjectParam?.unitTime && convertDateToSeconds(mapElement.unitTime || 0) > convertDateToSeconds(mapObjectParam.unitTime || 0)){
      //the new event is old
      return
    }

    //eval old or new position with boundary on map
    const theUnitIsNotVisible = createInstanceWithBoundaryOnMap(mapElement, mapObjectParam);

    // ── WebGL early exit: skip units whose old AND new positions are out of viewport ──
    if (USE_WEBGL_MARKERS && webglLayer && lat1 !== undefined) {
      const oldLat = mapElement.position?.lat;
      const oldLng = mapElement.position?.lng;
      const newLat = mapObjectParam.position?.lat;
      const newLng = mapObjectParam.position?.lng;
      const oldInBounds = oldLat >= lat1 && oldLat <= lat2 && oldLng >= lng1 && oldLng <= lng2;
      const newInBounds = newLat >= lat1 && newLat <= lat2 && newLng >= lng1 && newLng <= lng2;
      if (!oldInBounds && !newInBounds) {
        // Neither position is visible — just update data silently
        assignOwnProps(mapElement, mapObjectParam);
        // Keep endPosition in sync so centerObjectOnMap (vehicle list click)
        // reads the latest coordinates, not a stale value from the last
        // time the unit was inside the viewport.
        mapElement.endPosition = mapObjectParam.position;
        // Clear stale offline flag — a position update means the unit is online.
        // Without this, out-of-viewport units keep offline: true and show the
        // offline icon when they become visible (e.g., after disabling clusters).
        if (mapElement.offline) {
          mapElement.offline = false;
        }
        return;
      }
    }

    mapElement.endPosition = mapObjectParam?.position;
    mapElement.trailTimestamp = convertDateToSeconds(mapElement?.trailTimestamp || 0)
    if(mapObjectParam?.trailPlaybackSpeed > 0){
      mapElement.trailPlaybackSpeed = mapObjectParam?.trailPlaybackSpeed
    }

    if(mapElement.isAnimated == true){

      //If the event queue is larger than 10, only keep the most recent one and delete the rest.
      if(mapElement?.newEvents?.length > 9){
        const objectWithGreatestUnitTime = mapElement?.newEvents?.reduce((maxObject, currentObject) => {
          // Get the most recent data for the queue
          if (!maxObject || parseInt(currentObject.unitTime) > parseInt(maxObject.unitTime)) {
            return parseInt(currentObject);
          }
          // Otherwise, keep the current maxObject
          return parseInt(maxObject);
        }, null);
        mapElement.newEvents = []
        mapElement.newEvents.push(objectWithGreatestUnitTime)
      }else{
        mapElement.newEvents.push(mapObjectParam)

        //order new events by unitTime
        mapElement.newEvents.sort((a, b) => parseInt(a?.unitTime || 0) - parseInt(b?.unitTime || 0));

      }
      // Keep endPosition in sync so centerObjectOnMap reads the latest
      // coordinates even when events are queued during animation.
      mapElement.endPosition = mapObjectParam.position;
      // Clear stale offline flag — queued position events mean the unit is online
      if (mapElement.offline) {
        mapElement.offline = false;
      }
      //return the object animation in next step
      return
    }
    mapElement.stopAnimation = false;

    let noMovement = false, isTrail = false, newUnitTime = 0, firstEventReproduce = true;
    if(
      (mapElement.eventName == 'Ignition Off' && mapObjectParam.eventName == 'Ignition Off') || 
      (mapElement.eventName == 'Ignition Off' && mapObjectParam.eventName == 'Ignition On'))
      {
        noMovement = true;

        //2. Update the most recent date into the date atribute
        mapElement.trailTimestamp =  mapElement.trailTimestamp > convertDateToSeconds(mapObjectParam?.timestamp) ?  mapElement.trailTimestamp:  convertDateToSeconds(mapObjectParam?.timestamp)
    }

    //if diferent the time of unit for 3 mins not animate.
    newUnitTime = mapObjectParam.unitTime ? convertDateToSeconds(mapObjectParam.unitTime) : 0;

    /* if(!validateTrailTimeMins(newUnitTime, 3) && !mapObjectParam?.isVehicleTrail){
      noMovement = true;
    } */
    
    //start the array for trails new objects
    let newTrailOnObject = []

    //check the trail attribute is comming
    if(Array.isArray(mapObjectParam?.trail) && mapObjectParam?.trail?.length > 0){
      isTrail = true
      let newTrails = Array.isArray(mapObjectParam.trail) ? [...mapObjectParam.trail] : [];

      // Evaluate if the time of the main event differs significantly from the one provided in the trails.
      // Only the most recent position should be displayed.

      //add the last event to the list of reproduction
      if(newUnitTime > 0 && (validateTrailTimeMins(newUnitTime, 3) || mapObjectParam?.isVehicleTrail)){

        
        const maxTimestampTrailComes = newTrails.reduce((prev, current) => {
          return parseInt(prev.timestamp) > parseInt(current.timestamp) ? prev : current;
        });
        //if the most current trail date is more than 1 minute different from the event date it will not play.

        if(validateTrailTimeMinsCompare(newUnitTime, parseInt(maxTimestampTrailComes?.timestamp || 0), 1) || mapObjectParam?.isVehicleTrail){
          newTrails.unshift({
            isPlayed: false,
            timestamp: newUnitTime,
            lat: mapObjectParam.position.lat,
            lon: mapObjectParam.position.lng,
            lng: mapObjectParam.position.lng,
          })
        }else{
          //2. Update the most recent date into the date atribute
          mapElement.trailTimestamp =  mapElement.trailTimestamp > convertDateToSeconds(mapObjectParam?.timestamp) ? mapElement.trailTimestamp : convertDateToSeconds(mapObjectParam?.timestamp)

          //no reproduce trail
          noMovement = true;
        }
      }else{
        //2. Update the most recent date into the date atribute
        mapElement.trailTimestamp =  mapElement.trailTimestamp > convertDateToSeconds(mapObjectParam?.timestamp) ? mapElement.trailTimestamp : convertDateToSeconds(mapObjectParam?.timestamp)

        //no reproduce trail
        noMovement = true;
      }

      //convert all days to seconds
      if(newTrails && newTrails.length > 0){
        newTrails = newTrails.map((item) => {
          return{
            ...item,
            timestamp: parseInt(convertDateToSeconds(item.timestamp))
          }
        })
      }

      //1. Get the last date 
      const maxTimestampObject = newTrails.reduce((prev, current) => {
        return parseInt(prev.timestamp) > parseInt(current.timestamp) ? prev : current;
      });

      //1.1 Eval is the date is max to the actual data
      if(convertDateToSeconds(maxTimestampObject?.timestamp) > convertDateToSeconds(mapElement?.trailTimestamp)){

        //1.2 Get the new positions relative with the date
        let filterTrails = newTrails.filter((item) => convertDateToSeconds(item.timestamp) > mapElement.trailTimestamp)

        //comented duply remove
        //filterTrails = removeDupply(filterTrails)

        //1.3 Sort the array asc to add the new 
        filterTrails.sort((a, b) => convertDateToSeconds(a.timestamp) - convertDateToSeconds(b.timestamp));

        //1.4 Add new objects to trail array
        //add the mark in the contents to be reproduced
        filterTrails.forEach((item, indexTrail) => {
          let secondsToReproduce = 0
          let currentTimestampForUnit = 0
          if(indexTrail == 0){

            currentTimestampForUnit = mapElement?.trailTimestamp
            if(currentTimestampForUnit == 0 && filterTrails.length > 1){
              secondsToReproduce = getDifferenceInSeconds(filterTrails[0].timestamp, filterTrails[1].timestamp)
            }else if(currentTimestampForUnit == 0){
              currentTimestampForUnit = item?.timestamp
              secondsToReproduce = getDifferenceInSeconds(currentTimestampForUnit, item?.timestamp)
            }else {
              secondsToReproduce = getDifferenceInSeconds(currentTimestampForUnit, item?.timestamp)
            }
          }else {
            secondsToReproduce = getDifferenceInSeconds(item.timestamp, newTrailOnObject[0].timestamp)
          }

          secondsToReproduce = secondsToReproduce < 1 ? 1 : secondsToReproduce

          //if the diference time to the last event is mayor to 30 seconds, set the start position to start trails.
          if(secondsToReproduce > 30 && indexTrail == 0){
            firstEventReproduce = false;
          }

          if(indexTrail == 0 && filterTrails.length > 1){

            const itemToCompare = filterTrails[1]
            const compareSeconds = getDifferenceInSeconds(convertDateToSeconds(mapObjectParam.unitTime), itemToCompare?.timestamp)

            //validate distance 
            const maxDistanceTraveled = compareSeconds > 1 ? calculateDistanceTraveled(180, compareSeconds) : 1000; //1000 metters to 140 km per hour for speed units (every 30 seconds to come data form the unit)

            if(compareSeconds > 60 && !mapObjectParam?.isVehicleTrail) noMovement = true;

            //if speed is mayor o equal to 120km per hour for TRUCKS
            //value of distance is in Metters
            const distanceValidationInMetters = calcDistanceToPoints(mapObjectParam.position.lat, mapObjectParam.position.lng, parseFloat(itemToCompare?.lat), parseFloat(itemToCompare?.lng || 0) || parseFloat(itemToCompare?.lon || 0));

            if(distanceValidationInMetters < maxDistanceTraveled){
              // made the validation when have error in trails
              // the trails come same in all rows
              if(isArray(mapElement?.trail) && mapElement.trail.length > 0 && filterTrails.length > 0 ){
                if(JSON.stringify(mapElement.trail) == JSON.stringify(filterTrails) && !mapObjectParam?.isVehicleTrail){
                  //not move because the trail 
                  noMovement = true;
                }
              }
            }else if(!mapObjectParam?.isVehicleTrail){
              noMovement = true;
            }
          }


          newTrailOnObject.unshift({
            ...item,
            lng: item.lon,
            isPlayed: false, 
            secondsToReproduce: secondsToReproduce > 30 ? 1 : Math.max(secondsToReproduce - 1, 1)
          })
        })

        //max trail queue is 20 items
        if (newTrailOnObject.length > 20) {
          newTrailOnObject.splice(20, newTrailOnObject.length - 20);
        }


        //2. Update the most recent date into the date atribute
        mapElement.trailTimestamp = parseInt(maxTimestampObject?.timestamp || 0)
      }else{
        //No moved when the old data comes to the new array on trails
        noMovement = true;
      }
    }

    //Remove the array the isPlayed is true, only reproduce the new elements to trails
    newTrailOnObject = newTrailOnObject.filter((item) => !item.isPlayed)

    //update the element reference
    let cloneMapElement = { ...mapElement, firstEventReproduce: firstEventReproduce, isVehicleTrail: mapObjectParam?.isVehicleTrail || false }
    cloneMapElement.trail = newTrailOnObject;
    cloneMapElement.originalTrail = newTrailOnObject;
    //Update the aray of objects
    _indexRemove(mapElement);
    mapObjects[index] = { ...mapElement, ...mapObjectParam, trail: newTrailOnObject };
    // Active position reports mean the unit is online — clear any stale offline flag.
    // The explicit isOffline change event (handled by updateMarkerOfflineDevice) runs
    // AFTER this forEach and will re-set the flag if the unit is truly offline.
    if (mapObjects[index].offline) {
      mapObjects[index].offline = false;
      // Immediately schedule a WebGL refresh so the offline icon disappears
      // within 250ms, even if the movement path (animation) hasn't finished.
      if (USE_WEBGL_MARKERS && webglLayer) {
        scheduleWebGLRefresh(updateWebGLMarkers);
      }
    }
    _indexAdd(mapObjects[index]);

    // Preserve the marker's current on-screen position after the spread.
    // The spread overwrites .position with mapObjectParam.position (the server's
    // latest/final position), but if the marker is partway through a trail
    // animation — or just finished one — its displayed position differs.
    // Reading from MarkerCompat gives the true on-screen location and prevents
    // brief visual jumps when updateWebGLMarkers reads mapObjects
    // between animation frames via the idle handler or scheduleWebGLRefresh.
    if (mapObjects[index]?.instance?.getPosition) {
      const currentPos = mapObjects[index].instance.getPosition();
      if (currentPos) {
        mapObjects[index].position = { lat: currentPos.lat(), lng: currentPos.lng() };
      }
    }

    // Use the LIVE mapObjects[index] reference (the spread above created a new
    // object). Passing the stale `mapElement` caused color / state updates
    // inside updateMoveMarker to be written to the detached old object instead
    // of the one actually stored in the array and later read by the WebGL layer.
    const liveMapElement = mapObjects[index];

    //Pint new position into the realtime maps
    switch (type) {
      case TypeObjectMap.Marker:
        if(isTrail && cloneMapElement.trail.length > 0 && !noMovement && theUnitIsNotVisible){
          if (USE_WEBGL_MARKERS && webglLayer) {
            // Match legacy zoom gate: only animate trails when zoomed in enough
            // for the animation to be visible. At low zoom, snap to final position.
            const trailZoom = mapItem?.getZoom?.() ?? 0;
            const isCenterUnit = mapObjectParam?.id?.toString() === centerUnitIdRef?.current?.toString();
            if (trailZoom >= zoomToEnableTrails || isCenterUnit || mapObjectParam?.isVehicleTrail) {
              _enqueueTrailWebGL(liveMapElement, cloneMapElement.trail, mapObjectParam);
            } else {
              // Low zoom — snap to the newest position without animation
              updateMoveMarker(liveMapElement, mapObjectParam);
            }
          } else {
            updateMoveMarkerByTrail(cloneMapElement, mapObjectParam, index)
          }
        }else if(isTrail && noMovement){
          // In WebGL mode, if a trail animation is already running, skip —
          // the marker will reach this position naturally via the queue.
          if (USE_WEBGL_MARKERS && webglLayer && liveMapElement._webglAnim?.stopAnim) {
            // Do nothing — let the current animation sequence continue
          } else if (!USE_WEBGL_MARKERS || !webglLayer) {
            if (liveMapElement.isAnimated == false) {
              updateMoveMarker(liveMapElement, mapObjectParam);
            }
          } else {
            updateMoveMarker(liveMapElement, mapObjectParam);
          }
        }else if(!isTrail) {
          updateMoveMarker(liveMapElement, mapObjectParam);
        }

        // ── Label updates (DOM-only — skipped in WebGL mode where TextLayer handles labels) ──
        if (!USE_WEBGL_MARKERS || !webglLayer) {
          const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
          const labelType = objectShowLabelSettings?.labelType;
          
          let newLabel = getUpdatedMarkerLabel({
            labelType,
            mapElement,
            mapObjectParam,
          });

          //is prevent overlap Labels is active.
          const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
          if (objectNoOverlapSettings && objectNoOverlapSettings?.enable && mapItem.getZoom() > zoomLabelsPositions
            && mapElement.position.lat >= lat1 && mapElement.position.lat <= lat2
            && mapElement.position.lng >= lng1 && mapElement.position.lng <= lng2) {


            if (updateLabelsAndAreas(mapElement, mapObjectParam, index)) {
              const {
                newPositionLabel,
                labelPositionSpace,
                labelGroupArea,
                labelGroup
              } = evaluateLabelsPosition(mapElement, mapItem.getZoom(), true);

              mapElement.labelPositionSpace = labelPositionSpace;
              mapElement.labelGroupArea = labelGroupArea;
              mapElement.labelGroup = labelGroup;

              updateMarkerLabel(mapElement, '', newPositionLabel, true, newLabel || mapElement.label);

              const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
              const isCluster = objectCluster && objectCluster.instance

              if (!isCluster) {
                removeMapObject(`temporalLabel_poly_${mapElement.id}`, TypeObjectMap.Polyline);
                addMapObject(new MapObject().buildPolyLine({
                  id: `temporalLabel_poly_${mapElement.id}`,
                  coordinates: [mapObjectParam.position, newPositionLabel],
                  layerName: TypeLayer.UnitLabel,
                  heading: 0,
                  color: '#B0B0B0'
                }), true, true);
              }
            }
          } else {
            //normal label update
            updateMarkerLabel(mapObjectParam, newLabel);
          }
        }

        //mapObjects[index] = { ...mapElement, ...mapObjectParam };
        break;

      case TypeObjectMap.Polygon:

        break;
    }
  })

  /*********************************************************
   * Eval to unit enter or left the area.
  *********************************************************/
  const updateLabelsAndAreas = (mapElement, mapObjectParam, index) => {

    //is the new unit in a group?
    let arrayLabelsStack = null;
    if (mapElement.labelGroup && mapElement.labelGroupArea) {

      //verify that you are in the same area 
      //do not move label - continue in the group
      if (mapElement.labelGroupArea
        && ((mapObjectParam.position.lat >= mapElement.labelGroupArea.pointLatFirst)
          && (mapObjectParam.position.lat <= mapElement.labelGroupArea.pointLatSecond)
          && (mapObjectParam.position.lng >= mapElement.labelGroupArea.pointLngFirst)
          && (mapObjectParam.position.lng <= mapElement.labelGroupArea.pointLngSecond))) {
        return false;
      } else {
        //The unit was previously in a group but left that area or group.
        //UPDATING!! the units that are in the group from which the unit came from
        //check the group still has the number of units needed to form a group
        labelUpdateUnitsOnGroup(mapElement, mapObjectParam)


        //evaluate whether the unit can be in another group.
        let otherLabelsStack = mapObjects.filter((item) =>
          item.labelGroup
          && item.labelGroupArea
          && ((mapObjectParam.position.lat >= item.labelGroupArea.pointLatFirst)
            && (mapObjectParam.position.lat <= item.labelGroupArea.pointLatSecond)
            && (mapObjectParam.position.lng >= item.labelGroupArea.pointLngFirst)
            && (mapObjectParam.position.lng <= item.labelGroupArea.pointLngSecond)))

        //If the unit is in another group
        if (otherLabelsStack && otherLabelsStack.length > 0) {

          labelEvalUnitIntoAnotherGroup(otherLabelsStack, mapElement, mapObjectParam, index);
          return false;

        } else {

          //IMPORTANT: You are NOT in any group, but you can form a new group.
          //with other units that are NOT in a group
          arrayLabelsStack = mapObjects.filter((item) =>
            item.type == TypeObjectMap.Marker
            && (item.instance && item.instance.getMap())
            && item.layerName == TypeLayer.Units
            && (item.position
              && item.position.lat >= lat1 && item.position.lat <= lat2
              && item.position.lng >= lng1 && item.position.lng <= lng2));

          //get the units into the cuadrant
          arrayLabelsStack = arrayLabelsStack.filter((item) =>
            item.position
            && !item.labelGroup
            && mapElement.labelGroupArea
            && ((item.position.lat >= mapElement.labelGroupArea.pointLatFirst)
              && (item.position.lat <= mapElement.labelGroupArea.pointLatSecond)
              && (item.position.lng >= mapElement.labelGroupArea.pointLngFirst)
              && (item.position.lng <= mapElement.labelGroupArea.pointLngSecond)))

          if (arrayLabelsStack && arrayLabelsStack.length > 5) {

            labelCreateGroupUnitsWithoutGroup(mapElement, mapObjectParam, arrayLabelsStack, index);
            return false

          } else {
            //Will NOT form any group, remains alone
            //indicate that the unit is no longer in a group     
            //Create label
            labelExitFromGroupToCreateLabel(mapElement, mapObjectParam, index)
            return true
          }
        }
      }
    } else {
      //The unit is not in any group, a new space must be found for it.
      //Find if the unit is entering a new group

      let otherLabelsStack = mapObjects.filter((item) =>
        item.labelGroup
        && item.labelGroupArea
        && ((mapObjectParam.position.lat >= item.labelGroupArea.pointLatFirst)
          && (mapObjectParam.position.lat <= item.labelGroupArea.pointLatSecond)
          && (mapObjectParam.position.lng >= item.labelGroupArea.pointLngFirst)
          && (mapObjectParam.position.lng <= item.labelGroupArea.pointLngSecond)))

      //find in another group
      if (otherLabelsStack && otherLabelsStack.length > 0) {

        labelWithoutGroupEnterInGroup(mapElement, mapObjectParam, otherLabelsStack, index)
        return false;
      }

      //TO SEE IF THE UNIT CAN FORM A NEW GROUP
      arrayLabelsStack = mapObjects.filter((item) =>
        item.type == TypeObjectMap.Marker
        && (item.instance && item.instance.getMap())
        && item.layerName == TypeLayer.Units
        && (item.position
          && item.position.lat >= lat1 && item.position.lat <= lat2
          && item.position.lng >= lng1 && item.position.lng <= lng2));

      //get the units into the cuadrant
      arrayLabelsStack = arrayLabelsStack.filter((item) =>
        item.position
        && !item.labelGroup
        && mapElement.labelGroupArea
        && ((item.position.lat >= mapElement.labelGroupArea.pointLatFirst)
          && (item.position.lat <= mapElement.labelGroupArea.pointLatSecond)
          && (item.position.lng >= mapElement.labelGroupArea.pointLngFirst)
          && (item.position.lng <= mapElement.labelGroupArea.pointLngSecond)))

      if (arrayLabelsStack && arrayLabelsStack.length > 5) {

        labelCreateGroupUnitsWithoutGroup(mapElement, mapObjectParam, arrayLabelsStack, index);
        return false
      }

      //THE UNIT IS ALONE UPDATE THE LABEL
      return true
    }
    //IMPORTANT!:
    //Make logic to avoid duplicity of polygons
  }
  

  /*********************************************************
   * When the unit enter in a group, is necesary to remove the old label
   * @param {Upd} mapElement 
   * @param {*} mapObjectParam 
   *********************************************************/
  const labelWithoutGroupEnterInGroup = (mapElement, mapObjectParam, otherLabelsStack, paramIndex) => {

    //obtain the units of that group
    const unitsOnGroup = []
    otherLabelsStack[0].labelGroup.forEach((unitId) => {
      unitsOnGroup.push(getObject(TypeObjectMap.Marker, unitId.toString()))
    })
    //add the new unit to the group
    unitsOnGroup.push(getObject(TypeObjectMap.Marker, mapObjectParam.id))
    //obtain the main object of the group associated with the group label
    let mainObjectUnitGroup = getObject(TypeObjectMap.Marker, unitsOnGroup[0].labelGroupIdMain)

    //SEARCH! - why it arrives empty!!!
    if (!mainObjectUnitGroup) {
      mainObjectUnitGroup = getObject(TypeObjectMap.Marker, unitsOnGroup[1].labelGroupIdMain)
      if (!mainObjectUnitGroup) return
    }

    //all objects are updated
    const valueLabelGroup = unitsOnGroup.map((group) => group.id)
    let label = '';
    unitsOnGroup.forEach((unit, index) => {
      if (index <= 4) {
        label = `${label}${unit.label} </br>`;

      }
      //add the new unit to the group
      unit.labelGroup = valueLabelGroup;
      unit.labelGroupArea = mainObjectUnitGroup.labelGroupArea;
      unit.labelGroupIdMain = mainObjectUnitGroup.labelGroupIdMain;
    });


    let mainObjectLabel = getObject(TypeObjectMap.Label, `label_${mainObjectUnitGroup.labelGroupIdMain}`)
    //update elements into the info bubble
    if (mainObjectLabel) updateMarkerLabel(mainObjectLabel, '', mainObjectLabel.position, true, label);

    //remove label
    removeMapObject(`label_${mapElement.id}`, TypeObjectMap.Label);
    //remove polyline
    removeMapObject(`temporalLabel_poly_${mapElement.id}`, TypeObjectMap.Polyline);

    //update mapObjects with new values
    _indexRemove(mapElement);
    mapObjects[paramIndex] = { ...mapElement, ...mapObjectParam };
    _indexAdd(mapObjects[paramIndex]);
  }

  /*********************************************************
   * Create a label when unit exit from area
   * @param {Upd} mapElement 
   * @param {*} mapObjectParam 
   *********************************************************/
  const labelExitFromGroupToCreateLabel = (mapElement, mapObjectParam, index) => {
    mapElement.labelGroup = null;
    mapElement.labelGroupArea = null;
    mapElement.labelGroupIdMain = null;
    //update mapObjects with new values
    _indexRemove(mapElement);
    mapObjects[index] = { ...mapElement, ...mapObjectParam };
    _indexAdd(mapObjects[index]);

    //remove the previus object
    removeMapObject(`label_${mapObjectParam.id}`, TypeObjectMap.Label);

    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    const labelType = objectShowLabelSettings?.labelType;
        
    let newLabel = getUpdatedMarkerLabel({
      labelType,
      mapElement,
      mapObjectParam,
    });

    //create the independent label for this new unit.
    const objectLabel = new MapObject().buildLabel({
      id: `label_${mapObjectParam.id}`,
      label: newLabel || '',
      position: { lat: mapElement.position.lat, lng: mapElement.position.lng },
      layerName: TypeLayer.UnitLabel
    })
    //add the object into the map
    addMapObject(objectLabel);
  }

  /*********************************************************
   * Update the units into the labels Group on map,
   * is important to update the area.
   * @param {Upd} mapElement 
   * @param {*} mapObjectParam 
   *********************************************************/
  const labelUpdateUnitsOnGroup = (mapElement, mapObjectParam) => {

    //vars for show UnitLabel or DriverLabel
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    const labelType = objectShowLabelSettings?.labelType;
    let arrayLabelsStack = mapObjects.filter((item) =>
      item.type == TypeObjectMap.Marker
      && (item.instance && item.instance.getMap())
      && item.layerName == TypeLayer.Units
      && (item.position
        && item.position.lat >= lat1 && item.position.lat <= lat2
        && item.position.lng >= lng1 && item.position.lng <= lng2)
        && (getGroupLineLabel(labelType, item)?.length > 0)
      );

    //get the units into the cuadrant
    arrayLabelsStack = arrayLabelsStack.filter((item) =>
      item.id.toString() != mapObjectParam.id.toString()
      && item.position
      && item.labelGroup
      && mapElement.labelGroupArea
      && item.labelGroupIdMain == item.labelGroupIdMain
      && ((item.position.lat >= mapElement.labelGroupArea.pointLatFirst)
        && (item.position.lat <= mapElement.labelGroupArea.pointLatSecond)
        && (item.position.lng >= mapElement.labelGroupArea.pointLngFirst)
        && (item.position.lng <= mapElement.labelGroupArea.pointLngSecond)))

    if (arrayLabelsStack && arrayLabelsStack.length > 5) {

      //update the all objects
      const valueLabelGroup = arrayLabelsStack.map((group) => group.id)
      let label = '';
      arrayLabelsStack.forEach((unit, index) => {
        if (unit.id != mapObjectParam.id) {
          unit.labelGroup = valueLabelGroup;
          //Get the label and update the group and unit
          if (index <= 4) {
            label = `${label}${unit.label} </br>`;
          }
        }
      });

      let mainObjectMarker = getObject(TypeObjectMap.Marker, mapElement.labelGroupIdMain)
      if (mainObjectMarker) mainObjectMarker.labelGroup = valueLabelGroup;
      let mainObjectLabel = getObject(TypeObjectMap.Label, `label_${mapElement.labelGroupIdMain}`)
      if (mainObjectLabel) {
        mainObjectLabel.labelGroup = valueLabelGroup;
        //update elements into the info bubble
        updateMarkerLabel(mainObjectLabel, '', mainObjectLabel.position, true, label);
      }

      //UPDATE THE CIRCLE AREA!! - MAYBE IS MORE SMALL
      if (mainObjectMarker)
        labelDrawGroupAreaOnMap(mainObjectMarker, arrayLabelsStack)
    }
  }

  /*********************************************************
   * when the unit is in another group
   * @param {*} otherLabelsStack 
   * @param {*} mapElement 
   * @param {*} mapObjectParam 
   * @returns 
   *********************************************************/
  const labelEvalUnitIntoAnotherGroup = (otherLabelsStack, mapElement, mapObjectParam, indexParam) => {
    let mainObjectUnitGroup = getObject(TypeObjectMap.Marker, otherLabelsStack[0].labelGroupIdMain)

    if (!mainObjectUnitGroup || (mainObjectUnitGroup && !mainObjectUnitGroup.labelGroup)) return false;

    //vars for show UnitLabel or DriverLabel
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    const labelType = objectShowLabelSettings?.labelType;

    //obtain the units of that group
    const unitsOnGroup = []
    mainObjectUnitGroup.labelGroup.forEach((unitId) => {
      unitsOnGroup.push(getObject(TypeObjectMap.Marker, unitId.toString()))
    })
    //add the new unit to the group
    unitsOnGroup.push(getObject(TypeObjectMap.Marker, mapObjectParam.id))
    //obtain the main object of the group associated with the group label

    //Update all objects
    const valueLabelGroup = unitsOnGroup.map((group) => group.id)
    let label = '', newLabel = '';
    unitsOnGroup.forEach((unit, index) => {
      if (index <= 4) {
        newLabel = getGroupLineLabel(labelType, unit);
        label = `${label}${newLabel} </br>`;
      }
      //add new unit to the group
      unit.labelGroup = valueLabelGroup;
      unit.labelPosition = mainObjectUnitGroup.labelPosition
      unit.labelGroupArea = mainObjectUnitGroup.labelGroupArea;
      unit.labelGroupIdMain = mainObjectUnitGroup.labelGroupIdMain;
    });

    //update mapObjects with new values
    _indexRemove(mapElement);
    mapObjects[indexParam] = { ...mapElement, ...mapObjectParam };
    _indexAdd(mapObjects[indexParam]);

    const mainObjectLabel = getObject(TypeObjectMap.Label, `label_${mainObjectUnitGroup.labelGroupIdMain}`)
    //update elements into the info bubble
    if (mainObjectLabel) updateMarkerLabel(mainObjectLabel, '', mainObjectLabel.position, true, label);

    //Redraw area....
    //UPDATE THE CIRCLE AREA!! - MAYBE IS MORE SMALL
    labelDrawGroupAreaOnMap(mainObjectUnitGroup, unitsOnGroup);
  }

  /*********************************************************
   * Create the group labels for units without group
   * @param {*} otherLabelsStack 
   * @param {*} mapElement 
   * @param {*} mapObjectParam 
   * @returns 
   *********************************************************/
  const labelCreateGroupUnitsWithoutGroup = (mapElement, mapObjectParam, arrayLabelsStack, paramIndex) => {
    _indexRemove(mapElement);
    mapObjects[paramIndex] = { ...mapElement, ...mapObjectParam };
    _indexAdd(mapObjects[paramIndex]);
    let markerToAdd = getObject(TypeObjectMap.Marker, mapElement.id)
    arrayLabelsStack.unshift(markerToAdd);

    //get first element to center object.
    let label = ''

    //delete the old label object check this...

    const objectOverlayObject = getObject(TypeObjectMap.Label, `label_${markerToAdd.id}`);
    //eval no update to labels
    markerToAdd.labelPosition = false;
    markerToAdd.labelPositionSpace = null;
    markerToAdd.labelGroupArea = null;
    markerToAdd.labelGroupIdMain = null;

    const {
      newPositionLabel,
      labelPositionSpace,
      groupElements,
      labelGroupArea,
      labelGroupIdMain
    } = evaluateLabelsPosition(mapObjects[paramIndex], mapItem.getZoom());

    markerToAdd = getObject(TypeObjectMap.Marker, labelGroupIdMain)

    markerToAdd.labelPositionSpace = labelPositionSpace;
    markerToAdd.labelGroupArea = labelGroupArea;
    markerToAdd.labelGroupIdMain = labelGroupIdMain;

    if (objectOverlayObject) objectOverlayObject.labelPositionSpace = labelPositionSpace;

    let groupsArray = []
    if (groupElements.arrayElements) {
      groupsArray = groupElements.elements
      const valueLabelGroup = groupsArray.map((group) => group.id)
      mapObject.labelGroup = valueLabelGroup;

      groupsArray.forEach((unit, index) => {
        unit.labelGroup = valueLabelGroup;
        unit.labelPositionSpace = labelPositionSpace;
        unit.labelGroupArea = labelGroupArea;
        unit.labelGroupIdMain = labelGroupIdMain;

        if (index <= 4) {
          label = `${label}${unit.label} </br>`;
        }

        //show all elements
        const showLabel = unit.id == markerToAdd.id
        const objectOverlay = getObject(TypeObjectMap.Label, `label_${unit.id}`);
        if (objectOverlay) {
          objectOverlay.labelGroup = valueLabelGroup;
          objectOverlay.labelPosition = true;
          objectOverlay.labelPositionSpace = labelPositionSpace;
          objectOverlay.labelGroupArea = labelGroupArea;
          objectOverlay.labelGroupIdMain = labelGroupIdMain;
          objectOverlay.instance.onDisplayObject(showLabel, mapItem);
        }

        if (!showLabel) {
          removeMapObject(`temporalLabel_poly_${unit.id}`, TypeObjectMap.Polyline);
        }
      });

      //update elements
      updateMarkerLabel(markerToAdd, '', newPositionLabel, true, label);
    }
    _indexRemove(mapElement);
    mapObjects[paramIndex] = { ...mapElement, ...markerToAdd, ...mapObjectParam };
    _indexAdd(mapObjects[paramIndex]);
  }

  /*********************************************************
   * update the marker position into the realtime maps
   * @param {JSON} mapObjectParam 
   * @param {JSON} mapElement 
   *********************************************************/
  const updateMoveMarker = (mapElement, mapObjectParam) => {
    // In WebGL mode: always use WebGL path. Clustering is now handled entirely
    // by supercluster inside WebGLMarkerLayer — no need to fall through to
    // legacy setPosition() even when clusters are active.
    if (USE_WEBGL_MARKERS && webglLayer) {
      // Update color from event so WebGL arrows reflect the correct event color
      const evtColor = validateEventColor(mapObjectParam);
      if (evtColor && evtColor !== '#d7d7d6') {
        mapElement.color = evtColor.toString().replace('##', '#');
      }

      const newLat = parseFloat(mapObjectParam.position.lat);
      const newLng = parseFloat(mapObjectParam.position.lng);
      const targetLatLng = new google.maps.LatLng(newLat, newLng); // eslint-disable-line no-undef

      // Ensure shared animation state object exists (survives mapObjects spread)
      if (!mapElement._webglAnim) {
        mapElement._webglAnim = { stopAnim: null, queue: [], lastAnimTime: 0, isTrailSequence: false };
      }
      const anim = mapElement._webglAnim;

      // ── ZOOM GATE ──
      // At low zoom levels (< zoomToEnableTrails), smooth rAF animation is
      // invisible (sub-pixel movement) and creates massive overhead with
      // hundreds of concurrent requestAnimationFrame loops, each triggering
      // _renderPositionUpdate at 60fps. Snap instantly instead.
      const currentZoom = mapItem?.getZoom?.() ?? 0;
      const canAnimate = currentZoom >= zoomToEnableTrails;

      // ── QUEUE GUARD ──
      // If an animation (trail or non-trail) is already running, ALWAYS
      // enqueue. Never snap or skip — the queue will play this item when
      // its turn comes, keeping the movement fluid and uninterrupted.
      if (canAnimate && anim.stopAnim) {
        // Cap the queue to avoid unbounded growth
        if (anim.queue.length < 20) {
          const eventTime = mapObjectParam.unitTime ? convertDateToSeconds(mapObjectParam.unitTime) : 0;
          anim.queue.push({
            lat: newLat,
            lng: newLng,
            eventTime,
            mapObjectParam,
            isTrailPoint: false,
          });
          // FIFO order — do NOT sort. Trail points and events must play
          // in the exact sequence they were enqueued to simulate the real
          // vehicle route.
        }
        return;
      }

      // If zoom dropped below threshold while an animation was running,
      // cancel it so the marker snaps to its final position cleanly.
      if (!canAnimate && anim.stopAnim) {
        anim.stopAnim();
        anim.stopAnim = null;
        anim.queue.length = 0;
        anim.lastAnimTime = 0;
      }

      // No animation running — decide whether to animate or snap.
      const currentPos = mapElement.instance?.getPosition?.();
      let distance = 0;
      if (currentPos) {
        try {
          distance = google.maps.geometry.spherical.computeDistanceBetween(currentPos, targetLatLng); // eslint-disable-line no-undef
        } catch (_) { /* fallback: snap */ }
      }

      // Smooth rAF animation only when zoom is high enough AND distance
      // is reasonable (5m–50km). At low zoom, always snap.
      if (canAnimate && distance > 5 && distance < 50000) {
        const eventTime = mapObjectParam.unitTime ? convertDateToSeconds(mapObjectParam.unitTime) : 0;
        const queueItem = {
          lat: newLat,
          lng: newLng,
          eventTime,
          mapObjectParam,
          isTrailPoint: false,
        };

        // Non-trail animation: labels stay visible (no fade effect)
        anim.isTrailSequence = false;

        // Record the baseline time and start the first animation
        if (!anim.lastAnimTime) {
          anim.lastAnimTime = mapElement.unitTime ? convertDateToSeconds(mapElement.unitTime) : 0;
        }
        _startNextWebGLAnim(mapElement, queueItem);
        return;
      }

      // Snap instantly — either low zoom, small distance, or anomaly
      mapElement.position = { lat: newLat, lng: newLng };

      // Keep the MarkerCompat instance in sync so next time the
      // position-preservation block reads the correct location.
      if (mapElement.instance?.setPosition) {
        mapElement.instance.setPosition(targetLatLng);
      }

      if (mapObjectParam.id == centerUnitIdRef?.current) {
        centerMapAnimated(newLat, newLng);
      }
      // Update modal position so it follows the marker when open
      updateModalPositionTrails({
        id: mapObjectParam.id,
        position: mapObjectParam.position
      });
      scheduleWebGLRefresh(updateWebGLMarkers);
      return;
    }

    const color = validateEventColor(mapObjectParam)
    if (!mapElement.icon) {
      mapElement.instance.setIcon(getIconAttributes('#' + color, mapObjectParam.heading, null, null, mapObjectParam));
    }else{      
      const iconMarker = getIconAttributes(mapElement.color, 0, mapElement.icon, false)
      mapElement.instance.setIcon(iconMarker)
    }
    const position = new google.maps.LatLng( // eslint-disable-line no-undef
      parseFloat(mapObjectParam.position.lat),
      parseFloat(mapObjectParam.position.lng));
    mapElement.instance.setPosition(position)

    if(mapObjectParam.id == centerUnitIdRef?.current){
      centerMapAnimated(mapObjectParam.position.lat, mapObjectParam.position.lng);
    }
    // Update modal position so it follows the marker when open
    updateModalPositionTrails({
      id: mapObjectParam.id,
      position: mapObjectParam.position
    });
  }

  /*********************************************************
   * update the marker position into the realtime maps
   * @param {JSON} mapObjectParam 
   * @param {JSON} mapElement 
   *********************************************************/
  const updateMoveMarkerByTrail = (cloneMapElement, newMapElementParam, index) => {    
    if(cloneMapElement?.trail && Array.isArray(cloneMapElement.trail) && cloneMapElement.trail?.length > 0){
      if(newMapElementParam.eventName == 'Drive' && cloneMapElement.eventName != newMapElementParam.eventName){
        const color = validateEventColor(newMapElementParam)
        //update the name of event to show the correct marker
        cloneMapElement.eventName = newMapElementParam.eventName;
        if (!cloneMapElement.icon) {
          cloneMapElement.instance.setIcon(getIconAttributes('#' + color, cloneMapElement.heading, null, null, newMapElementParam));
        }else{      
          const iconMarker = getIconAttributes(cloneMapElement.color, 0, cloneMapElement.icon, false)
          cloneMapElement.instance.setIcon(iconMarker)
        }
      }


      var bounds = mapItem.getBounds();

      // Get the coordinates for bounds on map
      if (bounds && bounds.getSouthWest() && bounds.getNorthEast()) {
        lat1 = bounds.getSouthWest().lat(), lat2 = bounds.getNorthEast().lat();
        lng1 = bounds.getSouthWest().lng(), lng2 = bounds.getNorthEast().lng();
      }

      //Validate the bounds into the last position on array
      const lastLat = parseFloat(cloneMapElement?.trail[0].lat)
      const lastLng = parseFloat(cloneMapElement?.trail[0]?.lon || cloneMapElement?.trail[0]?.lng)

      //validate the bounds into the first position on array
      const firstLat = parseFloat(cloneMapElement?.trail[cloneMapElement?.trail.length - 1].lat)
      const firstLng = parseFloat(cloneMapElement?.trail[cloneMapElement?.trail.length - 1]?.lon || cloneMapElement?.trail[cloneMapElement?.trail.length - 1]?.lng)

      let isValidPosition = false
      if ((firstLat >= lat1 && firstLat <= lat2
        && firstLng >= lng1 && firstLng <= lng2) ||
        (lastLat >= lat1 && lastLat <= lat2
          && lastLng >= lng1 && lastLng <= lng2)
        ){
          isValidPosition = true;
      }

      const isCenterUnit = props?.centerUnitId?.toString() == cloneMapElement?.id?.toString() || false
      
      let mapElement = mapObjects.find((item) => {
        if (item.id == cloneMapElement.id && item.type == TypeObjectMap.Marker) {
          return item;
        }
      })

      if(mapItem.getZoom() >= zoomToEnableTrails && isValidPosition || isCenterUnit || newMapElementParam?.isVehicleTrail){

        const lat = cloneMapElement?.trail[cloneMapElement?.trail.length - 1].lat
        const lng = cloneMapElement?.trail[cloneMapElement?.trail.length - 1].lon || cloneMapElement?.trail[cloneMapElement?.trail.length - 1].lng
        
        const newPositionLatLng = new google.maps.LatLng( // eslint-disable-line no-undef
        parseFloat(lat),
        parseFloat(lng));

        const distanceOnMetters = google.maps.geometry.spherical.computeDistanceBetween(cloneMapElement.instance.getPosition(), newPositionLatLng);

        //Validate the distance diferent to 50 metters -- last validation 4500
        if(distanceOnMetters > trailsMaxDistance && !newMapElementParam?.isVehicleTrail){
          cloneMapElement.instance.setPosition(newPositionLatLng) 
          mapElement.position = {lat: lat, lng: lng}
          if(cloneMapElement.id == props.centerUnitId){
            centerMapAnimated(lat, lng);
          }
        }else if(distanceOnMetters > trailsMinDistance && !newMapElementParam?.isVehicleTrail) {
          const newDirection = calcDirection(cloneMapElement.instance.getPosition().lat(), cloneMapElement.instance.getPosition().lng(),
          newPositionLatLng.lat(), newPositionLatLng.lng())

          //if have change of direction - not animate onside the streets
          if(validateDirectionChange(cloneMapElement.heading, newDirection)){
            cloneMapElement.instance.setPosition(newPositionLatLng) 
            mapElement.position = {lat: lat, lng: lng}
            if(cloneMapElement.id == props.centerUnitId){
              centerMapAnimated(lat, lng);
            }
          }
        }else if(cloneMapElement?.firstEventReproduce && !newMapElementParam?.isVehicleTrail){
          cloneMapElement.instance.setPosition(newPositionLatLng) 
          mapElement.position = {lat: lat, lng: lng}
          if(cloneMapElement.id == props.centerUnitId){
            centerMapAnimated(lat, lng);
          }
        }

        if(cloneMapElement.isAnimated == false){
          
          //update the flag for animation in original object on marker
          if(mapElement){
            mapElement.isAnimated = true;
          }
          moveMarkerAnimation(cloneMapElement, getMoreElementsInTrails.bind(null, cloneMapElement))

        }

      } else if(mapElement.isAnimated == false) {
        
        
        const color = validateEventColor(newMapElementParam)
        if (!mapElement.icon) {
          mapElement.instance.setIcon(getIconAttributes('#' + color, newMapElementParam.heading, null, null, newMapElementParam));
        }else{      
          const iconMarker = getIconAttributes(mapElement.color, 0, mapElement.icon, false)
          mapElement.instance.setIcon(iconMarker)
        }
        

        //set the position to the marker at the normal for without the animations.
        const lat = parseFloat(cloneMapElement?.trail[0].lat)
        const lng = parseFloat(cloneMapElement?.trail[0].lon || cloneMapElement?.trail[0].lng)

        if(!isNaN(lat) && !isNaN(lng)){
          const position = new google.maps.LatLng( // eslint-disable-line no-undef
          parseFloat(lat),
          parseFloat(lng));
          mapElement.instance.setPosition(position)
          mapElement.position = {lat: lat, lng: lng}
        }
        if(cloneMapElement.id == centerUnitIdRef?.current){
          centerMapAnimated(lat, lng);
        }
         
        // Update modal position so it follows the marker when open
        updateModalPositionTrails({
          id: cloneMapElement.id,
          position: { 
            lat: parseFloat(lat), 
            lng: parseFloat(lng) 
          } 
        });
      } 

    }
  }

  const getMoreElementsInTrails = (cloneMapElement) => {
    let mapElement = mapObjects.find((item) => {
      if (item.id == cloneMapElement.id && item.type == TypeObjectMap.Marker) {
        return item;
      }
    })

    if(!mapElement) return

    const color = validateEventColor(mapElement)

    if (!mapElement?.icon) {
      mapElement.instance.setIcon(getIconAttributes('#' + color, mapElement.heading, null, null, mapElement));
    }else{      
      const iconMarker = getIconAttributes(mapElement.color, 0, mapElement.icon, false)
      mapElement.instance.setIcon(iconMarker)
    }

    //update the status of animation to false, to start to reproduce again
    mapElement.isAnimated = false;
  }



  /**********************************************************************
   * Animation for unit on maps, move the unit to start and end position
   * @param {*} mapElement 
   * @param {*} callbackAnimation 
   **********************************************************************/
  const moveMarkerAnimation = (mapElement, callbackAnimation) => {
    
    let mapElementRef = mapObjects.find((item) => {
      if (item.id == mapElement.id && item.type == TypeObjectMap.Marker) {
        return item;
      }
    })

    mapElement.trail = mapElement.trail.filter((item) => !item?.isPlayed)

    if (mapElement.trail.length > 0) {
      
        const newPosition = mapElement.trail[mapElement.trail.length - 1];

        const lat = newPosition.lat
        const lng = newPosition?.lng || newPosition?.lon
        const newPositionLatLng = new google.maps.LatLng(lat, lng);

        mapElement.position = {lat: lat, lng: lng};
        
        const duration =  newPosition.secondsToReproduce;
        
        // Make a new calc with the distance to old and new position
        //const distance = google.maps.geometry.spherical.computeDistanceBetween(mapElement.instance.getPosition(), newPositionLatLng);
        
        // make a calc of duration depens of the distance to the new position
        //const duration = Math.max(distance / 100, 1); 
        mapElement.trail[mapElement.trail.length - 1].isPlayed = true;

        //Only with the new position is diferent with the new make a animation
        //else continue with the new element of the array
        if(mapElement.instance.getPosition().lat() != newPositionLatLng.lat() && mapElement.instance.getPosition().lng() != newPositionLatLng.lng() || mapElement?.isVehicleTrail){
          const direction = calcDirection(mapElement.instance.getPosition().lat(), mapElement.instance.getPosition().lng(),
          newPositionLatLng.lat(), newPositionLatLng.lng())

          let distance = 0;
          if(mapElement?.instance){
            distance = google.maps.geometry.spherical.computeDistanceBetween(mapElement?.instance?.getPosition(), newPositionLatLng);
          }
          //if distance is mayor for 10 metters
          if(distance > 5){
            const color = validateEventColor(mapElement)
            if (!mapElement.icon) {
              mapElement.instance.setIcon(getIconAttributes('#' + color, direction, null, null, mapElement));
            }
          }
          //make animation
          if(distance > 0 || mapElement?.isVehicleTrail){
            // Marker animation
            const stopAnimationFunction = animateMarker(mapElement, newPositionLatLng, duration, function() {
              if(mapElement){
                moveMarkerAnimation(mapElement, callbackAnimation);
              }
            });

            if(mapElementRef && stopAnimationFunction) mapElementRef.stopAnimationFunction = stopAnimationFunction
          }else{
            //finish animation
            callbackAnimation(mapElement)
          }
          
        }else{
          //Continew with the new element of the array
          //reproduce the new element on array
          moveMarkerAnimation(mapElement, callbackAnimation);
          
        }

    }else{
      
      callbackAnimation(mapElement)

    }
    
}

//this method always return the diferences of the dates in seconds.
function getDifferenceInSeconds(timestamp1, timestamp2) {

  // Check if the timestamps are in milliseconds or seconds
  if (timestamp1 > 1000000000000) {
    // If both timestamps are greater than 1000000000000, then assume they are in milliseconds
    timestamp1 = Math.floor(timestamp1 / 1000);
  } 

  // Check if the timestamps are in milliseconds or seconds
  if (timestamp2 > 1000000000000) {
    // If both timestamps are greater than 1000000000000, then assume they are in milliseconds
    timestamp2 = Math.floor(timestamp2 / 1000);
  } 

  // Ensure the larger timestamp is placed first
  if (timestamp1 < timestamp2) {
    var temp = timestamp1;
    timestamp1 = timestamp2;
    timestamp2 = temp;
  }
  
  // Calculate the difference in seconds
  var differenceInSeconds = Math.abs(timestamp1 - timestamp2);
  return differenceInSeconds;
}

function calcDirection(paramLat1, paramLon1, paramLat2, paramLon2) {
  const toRadians = (deg) => deg * Math.PI / 180;

  // Convert degrees to radians
  const lat1 = toRadians(paramLat1);
  const lon1 = toRadians(paramLon1);
  const lat2 = toRadians(paramLat2);
  const lon2 = toRadians(paramLon2);

  // Normalize longitudes - new evaluation
  const dLon = (lon2 - lon1 + Math.PI) % (2 * Math.PI) - Math.PI;

  // Calculate direction using atan2
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  let angleRad = Math.atan2(y, x);

  // Convert radians to degrees
  let angleDeg = angleRad * 180 / Math.PI;

  // Normalize angle to range [0, 360)
  angleDeg = (angleDeg + 360) % 360;

  //if (angleDeg < 0) {
  //  angleDeg += 360;
  //} 

  return angleDeg;
}


/**
 * Enqueue trail points for WebGL animation.
 * Converts the trail array (intermediate GPS positions with pre-computed
 * durations) into sequential queue items so the marker follows the full
 * route instead of jumping directly to the final event position.
 *
 * Trail is stored newest-first (index 0 = newest via unshift), so we
 * iterate backwards (oldest → newest) to replay in chronological order.
 * The main event position is appended as the final waypoint.
 */
function _enqueueTrailWebGL(liveMapElement, trail, mapObjectParam) {
  // Update color from event so WebGL arrows reflect the correct event color
  const evtColor = validateEventColor(mapObjectParam);
  if (evtColor && evtColor !== '#d7d7d6') {
    liveMapElement.color = evtColor.toString().replace('##', '#');
  }

  // Ensure shared animation state object exists (survives mapObjects spread)
  if (!liveMapElement._webglAnim) {
    liveMapElement._webglAnim = { stopAnim: null, queue: [], lastAnimTime: 0, isTrailSequence: false };
  }
  const anim = liveMapElement._webglAnim;

  // Trail animation: labels should fade (hide during animation, fade-in after)
  anim.isTrailSequence = true;

  // Build queue items from trail points (oldest → newest)
  const trailItems = [];
  for (let i = trail.length - 1; i >= 0; i--) {
    const point = trail[i];
    if (point.isPlayed) continue;

    const lat = parseFloat(point.lat);
    const lng = parseFloat(point.lng || point.lon);

    if (isNaN(lat) || isNaN(lng)) continue;

    trailItems.push({
      lat,
      lng,
      eventTime: point.timestamp ? convertDateToSeconds(point.timestamp) : 0,
      duration: point.secondsToReproduce || 1,
      mapObjectParam,
      isTrailPoint: true,
    });

    // Mark as played so it won't be replayed on next event
    point.isPlayed = true;
  }

  // Append the main event position as the final waypoint so the marker
  // ends exactly where the server says the unit is.
  const finalLat = parseFloat(mapObjectParam.position.lat);
  const finalLng = parseFloat(mapObjectParam.position.lng);
  if (!isNaN(finalLat) && !isNaN(finalLng)) {
    const lastTrail = trailItems[trailItems.length - 1];
    // Only add if noticeably different from the last trail point
    if (!lastTrail || Math.abs(lastTrail.lat - finalLat) > 0.00001 || Math.abs(lastTrail.lng - finalLng) > 0.00001) {
      trailItems.push({
        lat: finalLat,
        lng: finalLng,
        eventTime: mapObjectParam.unitTime ? convertDateToSeconds(mapObjectParam.unitTime) : 0,
        mapObjectParam,
        isTrailPoint: false,
      });
    }
  }

  if (trailItems.length === 0) return;

  // If an animation is already running, append trail items to the queue
  if (anim.stopAnim) {
    const available = 20 - anim.queue.length;
    if (available > 0) {
      anim.queue.push(...trailItems.slice(0, available));
    }
    return;
  }

  // No animation running — start with the first trail point, queue the rest
  if (!anim.lastAnimTime) {
    anim.lastAnimTime = liveMapElement.unitTime ? convertDateToSeconds(liveMapElement.unitTime) : 0;
  }

  const [first, ...rest] = trailItems;
  if (rest.length > 0) {
    anim.queue.push(...rest);
  }

  _startNextWebGLAnim(liveMapElement, first);
}


/**
 * Start the next queued WebGL animation for a marker.
 * Computes duration at playback time using the gap between the previous
 * segment's event time and this segment's event time, so the marker
 * travels at the real speed of the vehicle.
 */
function _startNextWebGLAnim(mapElement, queueItem) {
  const { lat, lng, eventTime, mapObjectParam } = queueItem;
  const targetLatLng = new google.maps.LatLng(lat, lng); // eslint-disable-line no-undef
  const unitId = mapObjectParam.id;

  // Animation state lives in a shared object that survives mapObjects spread.
  // Both the old (closure-captured) and new (spread-created) mapElement
  // point to the SAME _webglAnim object, so mutations are always visible.
  if (!mapElement._webglAnim) {
    mapElement._webglAnim = { stopAnim: null, queue: [], lastAnimTime: 0, isTrailSequence: false };
  }
  const anim = mapElement._webglAnim;

  // Track the most recent mapObjectParam so the post-animation callback
  // can recover the server's authoritative heading.
  anim._lastMapObjectParam = mapObjectParam;

  let animDuration;

  // Trail points carry a pre-computed duration (secondsToReproduce) — use it directly
  if (queueItem.isTrailPoint && queueItem.duration > 0) {
    animDuration = queueItem.duration;
  } else {
    // Compute duration from the time gap between the previous segment and this one
    const prevTime = anim.lastAnimTime || 0;
    if (prevTime > 0 && eventTime > 0 && eventTime > prevTime) {
      const realSeconds = eventTime - prevTime;
      // If the gap is > 30s, use a short fixed duration (2s) instead of
      // real-time playback to avoid leaving the map stale for too long.
      if (realSeconds > 30) {
        animDuration = 2;
      } else {
        // Subtract 1s for slightly faster playback, clamp 1–30s
        animDuration = Math.min(Math.max(realSeconds - 1, 1), 30);
      }
    } else {
      // Fallback: compute distance-based duration
      const currentPos = mapElement.instance?.getPosition?.();
      let dist = 0;
      if (currentPos) {
        try {
          dist = google.maps.geometry.spherical.computeDistanceBetween(currentPos, targetLatLng); // eslint-disable-line no-undef
        } catch (_) { /* */ }
      }
      animDuration = Math.min(Math.max(dist / 200, 1), 4);
    }
  }

  // Update the baseline time for the next segment
  anim.lastAnimTime = eventTime || anim.lastAnimTime || 0;

  // Only hide labels (startAnimation) and show live indicator for trail
  // animations. Non-trail position updates should keep labels visible.
  if (anim.isTrailSequence) {
    if (USE_WEBGL_MARKERS && webglLayer) webglLayer.startAnimation(unitId);

    // Show live-tracking indicator when the followed unit starts animating
    if (followUnitRef?.current && centerUnitIdRef?.current == unitId) {
      _showLiveIndicator(unitId);
    }
  }

  anim.stopAnim = animateMarker(mapElement, targetLatLng, animDuration, () => {
    anim.stopAnim = null;

    // Check if there are more events in the queue
    if (anim.queue.length > 0) {
      const next = anim.queue.shift();
      // Continue animating — keep label hidden, don't refresh WebGL yet
      _startNextWebGLAnim(mapElement, next);
    } else {
      // Queue empty — animation sequence is done
      anim.lastAnimTime = 0;

      // ── Final heading recovery ──
      // Restore the server's authoritative heading on the mapObject.
      // During animation, realMapObj.heading was set to segmentHeading
      // (a geometric bearing). The last segment may have been nearly
      // zero-distance, producing a degenerate heading of 0° (North).
      // Use the server's heading from mapObjectParam as the ground truth.
      const finalMapObj = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, unitId));
      if (finalMapObj && mapElement._webglAnim?._lastMapObjectParam) {
        const serverHeading = mapElement._webglAnim._lastMapObjectParam.heading;
        if (serverHeading != null && Number.isFinite(parseInt(serverHeading, 10))) {
          finalMapObj.heading = parseInt(serverHeading, 10);
        }
      }

      if (anim.isTrailSequence) {
        if (USE_WEBGL_MARKERS && webglLayer) {
          // Pass the recovered heading to stopAnimation's render
          if (finalMapObj && Number.isFinite(finalMapObj.heading)) {
            const fPos = finalMapObj.position;
            const fLng = typeof fPos?.lng === 'function' ? fPos.lng() : parseFloat(fPos?.lng);
            const fLat = typeof fPos?.lat === 'function' ? fPos.lat() : parseFloat(fPos?.lat);
            if (Number.isFinite(fLng) && Number.isFinite(fLat)) {
              webglLayer.updateMarkerPosition(unitId, fLng, fLat, finalMapObj.heading);
            }
          }
          webglLayer.stopAnimation(unitId);
        }
        _hideLiveIndicator();
        anim.isTrailSequence = false;
      }
      // Mark clusters as dirty so the next reconciliation cycle picks up
      // this marker's final position (may have left or entered a cluster).
      if (webglLayer?.isClusteringEnabled?.()) {
        webglLayer._clustersDirty = true;
      }
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  });
}


/** Easing: smooth acceleration + deceleration */
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateMarker(mapElement, targetPosition, duration, callback) {
    const marker = mapElement.instance;
    const idElement = mapElement.id;
    const startPosition = marker.getPosition();
    const startTime = performance.now();
    let animationCancelled = false; // var to control the animation
    let animationFrameId;

    // Compute heading once per segment (straight-line interpolation).
    // When start ≈ target (< ~1m apart), the geometric bearing degenerates
    // to 0° (North) via atan2(0,0).  In that case, preserve the marker's
    // existing heading so the arrow doesn't snap to North on the final
    // (often near-zero-distance) trail segment.
    const startLat = startPosition.lat();
    const startLng = startPosition.lng();
    const endLat = targetPosition.lat();
    const endLng = targetPosition.lng();
    const dLat = Math.abs(endLat - startLat);
    const dLng = Math.abs(endLng - startLng);
    const isNearlyCoincident = dLat < 0.00001 && dLng < 0.00001; // ~1 meter

    let segmentHeading;
    if (isNearlyCoincident) {
      // Fall back to the marker's current heading (from the real mapObject
      // or the server data) rather than a meaningless geometric bearing.
      const realMapObjHeading = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, idElement));
      segmentHeading = realMapObjHeading?.heading || mapElement.heading || 0;
    } else {
      segmentHeading = calcDirection(startLat, startLng, endLat, endLng);
    }

    // Detect follow-mode for this unit — switch to instant centering
    const isFollowing = followUnitRef?.current && centerUnitIdRef?.current == idElement;
    if (isFollowing) _animatingFollowUnit = idElement;

    // Immediately sync mapObject position to START (not target) so that
    // if updateWebGLMarkers fires before the first rAF frame, it reads
    // the current position instead of the final destination → no ghost.
    const realMapObjInit = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, idElement));
    if (realMapObjInit) {
      realMapObjInit.position = { lat: startPosition.lat(), lng: startPosition.lng() };
    }

    function animate() {
        if (animationCancelled) return; // Si la animación fue cancelada, salimos de la función

        // ── BOUNDARY GATE ──
        // If the marker has moved outside the visible map bounds and this
        // unit is NOT being followed, stop the animation immediately and
        // snap to the most up-to-date position.  This prevents the scenario
        // where the user navigates to a unit (via vehicle list) and sees
        // the InfoBubble at the correct spot but the marker is still
        // "catching up" via an old animation queue.
        // Follow-mode is exempt because the map tracks the marker.
        // Read follow state from refs (not captured isFollowing) so that
        // if the user switches follow to a different unit mid-animation,
        // this unit's animation stops on the next frame.
        const isFollowedNow = followUnitRef?.current && centerUnitIdRef?.current == idElement;
        if (!isFollowedNow && lat1 !== undefined) {
          const curPos = marker.getPosition();
          const cLat = curPos?.lat();
          const cLng = curPos?.lng();
          if (cLat != null && cLng != null) {
            const inBounds = cLat >= lat1 && cLat <= lat2 && cLng >= lng1 && cLng <= lng2;
            if (!inBounds) {
              // Determine the most recent position: last queue item > target of current segment
              const anim = mapElement._webglAnim;
              let snapLat = targetPosition.lat();
              let snapLng = targetPosition.lng();
              if (anim?.queue?.length > 0) {
                const last = anim.queue[anim.queue.length - 1];
                snapLat = last.lat;
                snapLng = last.lng;
              }

              // Snap marker to newest position
              const snapPos = new google.maps.LatLng(snapLat, snapLng); // eslint-disable-line no-undef
              marker.setPosition(snapPos);

              const realMapObj = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, idElement));
              if (realMapObj) {
                realMapObj.position = { lat: snapLat, lng: snapLng };
                realMapObj.endPosition = { lat: snapLat, lng: snapLng };
              }
              if (USE_WEBGL_MARKERS && webglLayer) {
                webglLayer.updateMarkerPosition(idElement, snapLng, snapLat, segmentHeading);
              }

              // Kill the queue and clean up animation state
              if (anim) {
                anim.stopAnim = null;
                anim.queue.length = 0;
                anim.lastAnimTime = 0;
                if (anim.isTrailSequence) {
                  if (USE_WEBGL_MARKERS && webglLayer) webglLayer.stopAnimation(idElement);
                  _hideLiveIndicator();
                  anim.isTrailSequence = false;
                }
              }

              // Mark clusters dirty and refresh WebGL layer
              if (webglLayer?.isClusteringEnabled?.()) {
                webglLayer._clustersDirty = true;
              }
              scheduleWebGLRefresh(updateWebGLMarkers);

              animationCancelled = true;
              if (isFollowing) _animatingFollowUnit = null;
              return;
            }
          }
        }

        let speedPlayback =  mapElement?.trailPlaybackSpeed || 1;

        const currentTime = performance.now();
        const elapsed = ((currentTime - startTime) / 1000) * (speedPlayback); // segundos

        if (elapsed < duration) {
            const fraction = Math.min(elapsed / duration, 1);
            const newPosition = google.maps.geometry.spherical.interpolate(startPosition, targetPosition, fraction);
            marker.setPosition(newPosition);
            
            const lat = parseFloat(newPosition.lat());
            const lng = parseFloat(newPosition.lng());

            // Sync position and heading to real mapObject so WebGL layer reads updated coords
            const realMapObj = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, idElement));
            if (realMapObj) {
              realMapObj.position = { lat: lat, lng: lng };
              realMapObj.heading = segmentHeading;
            }
            if (USE_WEBGL_MARKERS && webglLayer) {
              webglLayer.updateMarkerPosition(idElement, lng, lat, segmentHeading);
            }

            //update position for the modal
            updateModalPositionTrails({
              id: idElement,
              position: { lat: lat, lng: lng} 
            });

            // Per-frame map centering for follow mode (avoids jump-ahead)
            // Pause centering briefly when user is interacting (scroll/zoom/drag)
            // Read centerUnitIdRef live (not the captured shouldCenter) so that
            // switching to a different marker immediately stops this animation
            // from panning the map back.
            if (centerUnitIdRef?.current == idElement && (Date.now() - _lastUserMapInteraction > USER_INTERACTION_PAUSE_MS)) {
              centerMapAnimated(lat, lng);
            }

            animationFrameId = requestAnimationFrame(animate);

            //update label position
            const newLabel = mapElement?.label || '';
            updateMarkerLabel(mapElement, newLabel);


            //update the vehicle trails polyline
            // Only append points during actual trail-sequence animations.
            // Non-trail moves (e.g. onNext snapping the marker to its final
            // position) must NOT write to the polyline or they draw a second
            // straight line on top of the GPS-accurate path.
            if(mapElement && mapElement?.layerName == TypeLayer.VehicleTrails && mapElement._webglAnim?.isTrailSequence){
              const elementIdArray = mapElement?.id ? mapElement.id.split('_') : '';//`marker_${item.id}`
              if(elementIdArray && elementIdArray?.length > 0){
                const namePolyline = `polyline_${elementIdArray[1]}`
                addPolylinePoint(namePolyline, { lat: lat, lng: lng} )
              }
              
            }

        } else {
            marker.setPosition(targetPosition);

            const lat = parseFloat(targetPosition.lat());
            const lng = parseFloat(targetPosition.lng());

            // Sync final position and heading to real mapObject for WebGL rendering
            const realMapObj = mapObjectsIndex.get(_indexKey(TypeObjectMap.Marker, idElement));
            if (realMapObj) {
              realMapObj.position = { lat: lat, lng: lng };
              realMapObj.heading = segmentHeading;
            }
            if (USE_WEBGL_MARKERS && webglLayer) {
              webglLayer.updateMarkerPosition(idElement, lng, lat, segmentHeading);
            }

            if(mapElement && mapElement?.layerName == TypeLayer.VehicleTrails && mapElement._webglAnim?.isTrailSequence){
              const elementIdArray = mapElement?.id ? mapElement.id.split('_') : '';//`marker_${item.id}`
              if(elementIdArray && elementIdArray?.length > 0){
                const namePolyline = `polyline_${elementIdArray[1]}`
                addPolylinePoint(namePolyline, { lat: lat, lng: lng} )
              } 
            }
            if (isFollowing) _animatingFollowUnit = null;
            if(callback) callback();
        }
    }

    animationFrameId = requestAnimationFrame(animate);

    function stopAnimation() {
      animationCancelled = true;
      cancelAnimationFrame(animationFrameId); // Stops the animation
      if (isFollowing) _animatingFollowUnit = null;
      // Ensure WebGL layer removes this marker from _animatingIds so
      // its label becomes visible again. Without this, cancelled
      // animations leave stale IDs and labels stay transparent.
      // Only call stopAnimation if this was a trail sequence (startAnimation was called).
      if (USE_WEBGL_MARKERS && webglLayer && mapElement._webglAnim?.isTrailSequence) {
        webglLayer.stopAnimation(idElement);
        mapElement._webglAnim.isTrailSequence = false;
      }
    }

    return stopAnimation;
  }


  /*********************************************************
   * update the name of marker in realtime maps
   * @param {JSON} mapObject 
   * @param {string} label 
   *********************************************************/
  const updateMarkerLabel = (mapObject, label = '', newPosition = null, isLabelPosition = false, labelNames = '') => {
    // In WebGL mode, labels are rendered via deck.gl TextLayer — skip DOM updates
    if (USE_WEBGL_MARKERS && webglLayer) return;

    let googlePosition = null
    if (isLabelPosition) {
      googlePosition = new google.maps.LatLng(newPosition.lat, newPosition.lng)
    } else if(mapObject?.instance) {
      googlePosition = mapObject.instance.getPosition()
    } else {
      googlePosition = new google.maps.LatLng(mapObject.position.lat, mapObject.position.lng)
    }

    const objectMarkerOverlay = getObject(TypeObjectMap.Label, `label_${mapObject.id}`);
    if (objectMarkerOverlay && objectMarkerOverlay.instance != null) {
      objectMarkerOverlay.instance.onMove(googlePosition, isLabelPosition);
      //update the start position
      objectMarkerOverlay.startPosition = mapObject.position;
      objectMarkerOverlay.labelPosition = isLabelPosition;

      if (isLabelPosition) {
        objectMarkerOverlay.position = newPosition
      } else {
        objectMarkerOverlay.position = mapObject.position
      }

      if (labelNames != '') {
        if (Array.isArray(mapObject?.labelGroup) && mapObject?.labelGroup?.length > 5)
          objectMarkerOverlay.instance.onChangeLabel(labelNames, mapObject?.labelGroup, onCallbackClickLabel, messages['map_view_detail']); //mapObject.label
        else
          objectMarkerOverlay.instance.onChangeLabel(labelNames, null, null, messages['map_view_detail']); //mapObject.label

      } else if (label != '') {
        objectMarkerOverlay.instance.onChangeLabel(label, null, null, messages['map_view_detail']); //mapObject.label
      }
    }
  }

  /*********************************************************
 * Show the units into the right panel area
 *********************************************************/
  const onCallbackClickLabel = (unitSelection) => {
    dispatch(setShowMapSettings(true));
    dispatch(selectModuleMapSettings(15));
    dispatch(setListUnits([], false));
    setTimeout(() => {
      dispatch(setListUnits(unitSelection.map((item) => +item), false));
    }, 500);
  };

  /*********************************************************
   * Remove all WebGL cluster label overlays from the map
   *********************************************************/
  const clearWebGLClusterLabels = () => {
    for (let i = 0; i < webglClusterLabelOverlays.length; i++) {
      try {
        webglClusterLabelOverlays[i].setMap(null);
      } catch (e) { /* already removed */ }
    }
    webglClusterLabelOverlays = [];
  };

  /*********************************************************
   * Create MarkerOverlay labels for each visible cluster
   * when "Label Clusters" (noOverlapSettings) is active.
   * Called from updateUnitsAndClusterInIdle after marker refresh.
   *********************************************************/
  const updateWebGLClusterLabels = () => {
    // Always clear previous overlays first
    clearWebGLClusterLabels();

    if (!USE_WEBGL_MARKERS || !webglLayer || !webglLayer.isClusteringEnabled()) return;

    // Check if "Label Clusters" is active
    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (!objectNoOverlapSettings || !objectNoOverlapSettings.enable) return;

    // Check if labels are enabled
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    if (!objectShowLabelSettings || !objectShowLabelSettings.enable) return;
    const labelType = objectShowLabelSettings.labelType;

    // Get visible clusters from WebGL layer
    const clusters = webglLayer.getClusters();
    if (!clusters || clusters.length === 0) return;

    // Get map bounds for visibility filtering
    const bounds = mapItem?.getBounds();
    const viewDetailText = viewDetailTextRef.current;
    const maxDisplay = 5;

    for (let c = 0; c < clusters.length; c++) {
      const cluster = clusters[c];
      if (cluster.pointCount <= 1) continue;

      // Filter to visible viewport
      const [lng, lat] = cluster.coordinates;
      const pos = new google.maps.LatLng(lat, lng);
      if (bounds && !bounds.contains(pos)) continue;

      // Get unit data from cluster leaves
      const leaves = webglLayer.getClusterLeaves(cluster.clusterId);
      if (!leaves || leaves.length === 0) continue;

      // Build label lines (up to 5 unit names)
      const unitNames = [];
      const unitIds = [];
      for (let i = 0; i < leaves.length; i++) {
        unitIds.push(leaves[i].id);
        const name = getGroupLineLabel(labelType, leaves[i]);
        if (name) unitNames.push(name);
      }

      if (unitNames.length === 0) continue;

      // Sort alphabetically for consistent display
      unitNames.sort((a, b) => a.localeCompare(b));

      // Build modern styled HTML (show up to maxDisplay after sorting)
      const hasMore = leaves.length > maxDisplay;
      const displayNames = unitNames.slice(0, maxDisplay);
      const namesMarkup = displayNames
        .map(name => `<div class="webgl-cluster-label-row">${name}</div>`)
        .join('');

      // Create the DOM element with the cluster label class
      const bubbleAnchor = document.createElement('div');
      bubbleAnchor.classList.add('webgl-cluster-label');
      // Apply light theme if darkLabels is OFF (default)
      if (!webglLayer._labelSettings.darkLabels) {
        bubbleAnchor.classList.add('webgl-cluster-label--light');
        bubbleAnchor.classList.add('popup-bubble--light');
      }
      bubbleAnchor.innerHTML = DOMPurify.sanitize(namesMarkup);

      const overlay = new MarkerOverlay(pos, bubbleAnchor);
      overlay.setMap(mapItem);

      // If more than 5 units, add "View Detail..." link
      if (hasMore) {
        overlay.onChangeLabel(
          namesMarkup,
          unitIds,
          onCallbackClickLabel,
          viewDetailText
        );
      }

      webglClusterLabelOverlays.push(overlay);
    }
  };

  /*********************************************************
   * Remove all WebGL unit-label-group overlays from the map
   *********************************************************/
  const clearWebGLUnitLabelGroups = () => {
    for (let i = 0; i < webglUnitLabelGroupOverlays.length; i++) {
      try {
        webglUnitLabelGroupOverlays[i].setMap(null);
      } catch (e) { /* already removed */ }
    }
    webglUnitLabelGroupOverlays = [];
  };

  /*********************************************************
   * Create MarkerOverlay labels for each label group when
   * clusters are OFF but "Label Clusters" is active.
   * Groups are computed inside WebGLMarkerLayer._groupOverlappingLabels
   * and exposed via getLabelGroups().
   * Called from updateUnitsAndClusterInIdle after marker refresh.
   *********************************************************/
  const updateWebGLUnitLabelGroups = () => {
    // Use the same DOM bubble UI as cluster labels, but only for a small
    // number of 5+ groups when clusters are OFF (noOverlap mode).
    clearWebGLUnitLabelGroups();

    if (!USE_WEBGL_MARKERS || !webglLayer) return;
    if (webglLayer.isClusteringEnabled()) return; // clustering ON stays unchanged

    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (!objectNoOverlapSettings?.enable) return;

    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    if (!objectShowLabelSettings?.enable) return;

    // Only show group bubbles starting at the same zoom threshold as before
    if ((mapItem?.getZoom?.() ?? 0) <= zoomLabelsPositions) return;

    const groups = webglLayer.getNoOverlapGroupLabels?.() || [];
    if (!groups || groups.length === 0) return;

    const bounds = mapItem?.getBounds();
    const viewDetailText = viewDetailTextRef.current;
    const maxDisplay = 5;

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const [lng, lat] = group.coordinates || [];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;

      const pos = new google.maps.LatLng(lat, lng);
      if (bounds && !bounds.contains(pos)) continue;

      const displayNames = Array.isArray(group.displayNames)
        ? group.displayNames.slice(0, maxDisplay)
        : [];
      if (displayNames.length === 0) continue;

      const namesMarkup = displayNames
        .map(name => `<div class="webgl-cluster-label-row">${name}</div>`)
        .join('');

      const bubbleAnchor = document.createElement('div');
      bubbleAnchor.classList.add('webgl-cluster-label');
      // Apply light theme if darkLabels is OFF (default)
      if (!webglLayer._labelSettings.darkLabels) {
        bubbleAnchor.classList.add('webgl-cluster-label--light');
        bubbleAnchor.classList.add('popup-bubble--light');
      }
      bubbleAnchor.innerHTML = DOMPurify.sanitize(namesMarkup);

      const overlay = new MarkerOverlay(pos, bubbleAnchor);
      overlay.setMap(mapItem);

      const unitIds = Array.isArray(group.unitIds) ? group.unitIds : [];
      const hasMore = Boolean(group.hasMore) || (group.count > maxDisplay);
      if (hasMore) {
        overlay.onChangeLabel(
          namesMarkup,
          unitIds,
          onCallbackClickLabel,
          viewDetailText
        );
      }

      webglUnitLabelGroupOverlays.push(overlay);
    }
  };

  /*********************************************************
   * Remove the All objects
   *********************************************************/
  const removeAll = useCallback(() => {
    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      try {
        if (mapObject.instance) {
          mapObject.instance.setMap(null);
          mapObject.instance = null;
        }
        if (mapObject.childInstance) {
          mapObject.childInstance.setMap(null);
          mapObject.childInstance = null;
        }
        _indexRemove(mapObject);
        mapObjects.splice(i, 1);
      }
      catch (err) {
        console.log("remov:::: ", err);
      }
    }
    mapObjectsIndex.clear();
  })

  /*********************************************************
   * Remove the all items by TypeObjectMap
   *********************************************************/
  const removeAllbyItemObject = useCallback((type = TypeObjectMap.Marker) => {
    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      try {
        if (mapObject.type == type) {
          if (mapObject.instance?.setMap) {
            mapObject.instance.setMap(null);
          }
          mapObject.instance = null;
          if (mapObject.childInstance?.setMap) {
            mapObject.childInstance.setMap(null);
          }
          mapObject.childInstance = null;
          _indexRemove(mapObject);
          mapObjects.splice(i, 1);
        }
      }
      catch (err) {
        console.log("remov:::: ", err);
      }
    }
  })

  /*********************************************************
   * Remove the all items by Type object and id
   *********************************************************/
  const removeItemsByIdAndType = useCallback((arrayIds, type = TypeObjectMap.Marker) => {
    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      try {
        if (mapObject.type == type && arrayIds.indexOf(mapObject.id + '') > -1) {
          if (mapObject.instance?.setMap) {
            mapObject.instance.setMap(null);
          }
          mapObject.instance = null;
          if (mapObject.childInstance?.setMap) {
            mapObject.childInstance.setMap(null);
          }
          mapObject.childInstance = null;
          _indexRemove(mapObject);
          mapObjects.splice(i, 1);
        }
      }
      catch (err) {
        console.log("remov:::: ", err);
      }
    }
  })

  /*********************************************************
   * Show or Hide the layer elements into the realtime maps
   *********************************************************/
  const showLayer = useCallback((layerName, show = false) => {
    mapObjects.forEach((item) => {
      if (item.layerName == layerName && item.instance) {
        item.hideMarker = !show;
        // In WebGL mode, unit markers stay hidden — WebGL layer renders them.
        if (USE_WEBGL_MARKERS && !props.isEtaLink && webglLayer && item.layerName === TypeLayer.Units) {
          item.instance.setMap(null);
        } else {
          item.instance.setMap(show && item?.enable ? mapItem : null)
          if (item.hasChild && item.childInstance) {
            item.childInstance.setMap(show && item?.enable ? mapItem : null)
          }
        }
      }
    })
    if (USE_WEBGL_MARKERS && webglLayer && layerName === TypeLayer.Units) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
  })

  /*********************************************************
   * Show or Hide the layer elements into the realtime maps
  *********************************************************/
  const showLayerMainOrChild = useCallback((layerName, show = false, main = true) => {
    mapObjects.forEach((item) => {
      if (item.layerName == layerName) {
        item.hideMarker = !show;
        if (main && item.instance) {
          item.instance.setMap(show && item?.enable ? mapItem : null)
        } else {
          if (item.hasChild && item.childInstance) {
            item.childInstance.setMap(show && item?.enable ? mapItem : null)
          }
        }
      }
    })
  })

  /*********************************************************
   * Remove the layer object
   *********************************************************/
  const removeLayer = useCallback((layerName, typeFilter = null) => {
    // Clear WebGL trail layer when removing VehicleTrails
    if (webglTrailLayer && layerName === TypeLayer.VehicleTrails) {
      webglTrailLayer.clear();
    }

    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      if (mapObject.layerName == layerName && (!typeFilter || mapObject.type === typeFilter)) {
        try {
          mapObject.hideMarker = true;
          mapObject.instance.setMap(null);
          mapObject.instance = null;
          if (mapObject.childInstance) {
            mapObject.childInstance.setMap(null);
            mapObject.childInstance = null;
          }
        }
        catch (err) {
          //ExceptionManager(err, 'Map/providers/googleProvider', 'removeLayer()');
        }
        _indexRemove(mapObject);
        mapObjects.splice(i, 1);
      }
    }

    // Refresh WebGL layer when route stops are removed so the
    // route-stop IconLayer clears its data immediately.
    if (USE_WEBGL_MARKERS && webglLayer && layerName === TypeLayer.FSMRoute) {
      scheduleWebGLRefresh(updateWebGLMarkers);
    }
    // Restore landmark/geofence area clickability when circle/square creation
    // layers are removed (cancel, save, or reposition).
    if (layerName === TypeLayer.CircleCreate || layerName === TypeLayer.SquareCreate) {
      setLandmarkGeofenceAreasClickable(true);
    }
  })

  /******************************************************
   * Validate is layer is KML element
   ******************************************************/
  const validateTypeLayerKml = (item) => {
    if (item.isMarker) {
      return TypeLayer.KmlMarker
    }
    return item.type == "geofence" ? TypeLayer.KmlGeofence : TypeLayer.KmlLandmark
  }

  /******************************************************
   * Add the kml objects into the realtime maps
   ******************************************************/
  const addKmlObject = useCallback((kmlListParam) => {
    //remove the old items into the map
    if (kmlList && kmlList.length > 0) {
      kmlList.forEach((item) => {
        const typeLayer = validateTypeLayerKml(item)
        showKmlObject(item.id, false, '', typeLayer)
      })
    }

    kmlList = [...kmlListParam]
    if (mapItem) {
      showsKmlObjectsByZoom(mapItem.getZoom());
    }
  })

  /******************************************************
   * Show the street view function
   ******************************************************/
  const showStreetView = useCallback((container, latLng, callback) => {
    if (container && latLng && latLng.lat && latLng.lng) {
      let position;
      if (typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
        position = latLng;
      } else {
        position = new google.maps.LatLng(parseFloat(latLng.lat), parseFloat(latLng.lng)); // eslint-disable-line no-undef
      }

      container.innerHTML = "";

      const streetViewService = new google.maps.StreetViewService(); // eslint-disable-line no-undef
      const STREETVIEW_MAX_DISTANCE = 200;
      streetViewService.getPanorama({ location: position, radius: STREETVIEW_MAX_DISTANCE }, function (data, status) {
        if (status === google.maps.StreetViewStatus.OK) { // eslint-disable-line no-undef
          const location = data?.location?.latLng;
          new google.maps.StreetViewPanorama( // eslint-disable-line no-undef
            container,
            {
              position: location,
              pov: {
                heading: 0,
                pitch: 0,
              },
            }
          );
        } else {
          if (callback) callback(google.maps.StreetViewStatus, status); // eslint-disable-line no-undef
        }
      });
    }
  });

  /******************************************************
   * Restart elements on map
   ******************************************************/
  const restartMap = () => {
    //stop all animations
    focusStopTrailMarkersAnimations();

    //restart the follow references
    followUnitRef.current = null;
    centerUnitIdRef.current = null;

    removeAll();
    removeMapClusterListener();
    removeMapBoundsChangedListener();
    removeMapClusterUpdateEvent();
    removeMapClickRouteListener();
    // Properly destroy the overlay (removes container from pane)
    if (customOverlay) {
      customOverlay.setMap(null);
    }
    customOverlay = null;
    overlayRef.current = null;
    mapItem = null;
    kmlList = [];
    instance = null
    realStateInfoWindow = false;
    mapClickEvent = null;
    mapClusterEvent = null;
    mapClusterUpdateEvent = null;
    mapBoundsChangedEvent = null;
    onCallBackZoom = null;

    if(timerRefreshWeather) clearInterval(timerRefreshWeather);
    timerRefreshWeather = null;

    hideTraffic();
    hideWeather();

    // Clean up WebGL layers
    clearWebGLClusterLabels();
    if (webglLayer) {
      webglLayer.destroy();
      webglLayer = null;
    }
    if (webglTrailLayer) {
      webglTrailLayer.destroy();
      webglTrailLayer = null;
    }
  }

  /******************************************************
   * Start the map load into initMap
   ******************************************************/
  const initMap = () => {

    restartMap();

    const styles = {
      default: [],
      show_poi: [
        {
          featureType: "poi.business",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "transit",
          elementType: "labels.icon",
          stylers: [{ visibility: "on" }],
        },
      ],
    };

    const mapItemElement = new google.maps.Map(divMap.current, { // eslint-disable-line no-undef
      // Only attach a vector mapId when the user has hardware acceleration
      // enabled.  Without mapId Google Maps falls back to the legacy raster
      // renderer, which is required for the non-WebGL path to work correctly
      // (overlays, custom markers, etc. behave differently on vector maps).
      ...(hardwareAccelerationRef.current && endpoints.GOOGLE_MAPS_MAP_ID
        ? { mapId: endpoints.GOOGLE_MAPS_MAP_ID }
        : {}),
      center: { lat: 36.046405, lng: -77.0345380 },
      zoom: 4,
      minZoom: 3,
      mapTypeControl: false,
      streetViewControl: (!props.showEtaLink && !props.generatePrint) || false,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM // eslint-disable-line no-undef
      },
      controlSize: 40,
      zoomControl: false, //custom this control initZoomControl()
      cameraControl: false, // disable the new control on zoom on maps
      mapTypeId: google.maps.MapTypeId.ROADMAP, // eslint-disable-line no-undef
      rotateControl: false,
      scaleControl: true,
      fullscreenControl: false,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM // eslint-disable-line no-undef
      },
      panControl: false,
      //gestureHandling: 'cooperative',
      //draggableCursor: 'pointer'

    });

    //To Enabling and Disabling 45° Imagery / Globe View on google maps
    //https://developers.google.com/maps/documentation/javascript/maptypes#Enabling45DegreeImagery
    mapItemElement.setTilt(0);

    mapItemElement.setOptions({ styles: styles['show_poi'] });

    //setMapItem(mapItemElement)
    mapItem = mapItemElement;

    //Add the input search button   
    initButtonOnMap("input_SearchMap", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Style native street view (pegman) control to match custom buttons
    if (!props.showEtaLink && !props.generatePrint) {
      styleNativeControl(mapItem);
    }

    //Add custom fullscreen button (native control doesn't support CSS in vector maps)
    if (!props.showEtaLink && !props.generatePrint) {
      initButtonOnMap("fullscreen-idcontrol", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef
      initFullscreenControl(mapItem);
    }

    if (!props.generatePrint && !props.isMobile) {
      //Add zoom buttons
      initZoomControl(mapItem); //comment this button
    }

    //Add the button to center vehicles
    initButtonOnMap("center_vehicles", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add the Map Settings
    initButtonOnMap("mapSettings-idcontrol", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add layer button
    initButtonOnMap("layer-idcontrol", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add the pagination control
    initButtonOnMap("draw_LayerMap", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add button to draw polygon
    initButtonOnMap("draw-idcontrol", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add the Tag filter Applied button
    initButtonOnMap("mapTagFilterApplied-idcontrol", google.maps.ControlPosition.RIGHT_BOTTOM, mapItem); // eslint-disable-line no-undef

    //Add the legend button
    initButtonOnMap("legends-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef
    
    //Add the filter events map button
    initButtonOnMap("filter-events-map-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Add traffic button
    initButtonOnMap("traffic-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Add weather button
    initButtonOnMap("weather-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Add the speed override list
    initButtonOnMap("speed-override-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Add the speed override list
    initButtonOnMap("route-info-idcontrol", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //Add the pagination control
    initButtonOnMap("input_Pagination", google.maps.ControlPosition.BOTTOM_CENTER, mapItem); // eslint-disable-line no-undef

    //Add the pannel eta Link in top
    initButtonOnMap("div_etaLink", google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef
    //is requered in bot enable this replace for this line
    //initButtonOnMap("div_etaLink", props.isMobile ? google.maps.ControlPosition.BOTTOM_LEFT : google.maps.ControlPosition.LEFT_TOP, mapItem); // eslint-disable-line no-undef

    //initWeatherLayer(mapItem);

    //Add the clip requests drawer button
    initButtonOnMap("clip-requests-drawer-idcontrol", google.maps.ControlPosition.RIGHT_TOP, mapItem); // eslint-disable-line no-undef

    //start the event for MapBounds
    addMapBoundsEndedListener()
    console.log('google map versión', google.maps.version)

    // ── WebGL capability gate ──
    // Detect once whether the GPU/browser can initialise a WebGL context.
    // When unavailable (legacy Intel HD 2000/3000, --disable-gpu, etc.)
    // OR when the user has explicitly disabled hardware acceleration in
    // Map Settings, fall back to DOM-based google.maps.Marker +
    // google.maps.Polyline which already have full support throughout
    // the codebase.
    const userDisabledHwAccel = hardwareAccelerationRef.current === false;
    if (USE_WEBGL_MARKERS && (userDisabledHwAccel || !_isWebGLAvailable())) {
      USE_WEBGL_MARKERS = false;
      if (userDisabledHwAccel) {
        console.info('[Map] Hardware acceleration disabled by user — using legacy renderer');
      } else {
        console.warn('[Map] WebGL not available — falling back to DOM rendering');
        if (!_webglWarningShown) {
          _webglWarningShown = true;
          NotificationManager.warning(
            'Your browser does not support GPU acceleration (WebGL). The map will work with reduced performance.',
            'Limited graphics support',
            8000
          );
        }
      }
    }

    // Initialize WebGL trail layer for GPU-accelerated vehicle trail polylines
    if (webglTrailLayer) { webglTrailLayer.destroy(); webglTrailLayer = null; }
    if (USE_WEBGL_MARKERS) {
      webglTrailLayer = new WebGLTrailLayer(mapItem);
    }

    // Initialize WebGL marker layer for high-performance unit rendering
    if (USE_WEBGL_MARKERS) {
      if (webglLayer) webglLayer.destroy();
      webglLayer = new WebGLMarkerLayer(mapItem);
      // Expose on window for console debugging: window.webglLayer.debugMarker('id')
      window.webglLayer = webglLayer;
      webglLayer.setViewDetailText(messages['map_view_detail'] || 'View Detail...');
      webglLayer.setOnClick((mapObj, info) => {
        // ── Route stop click ──
        // When isEditRoutesModule is active and the clicked object is a
        // route stop, fire the dedicated onRouteMarkerClick callback,
        // magnify the clicked pin, and reset all others to default size.
        if (props?.isEditRoutesModule && mapObj.layerName === TypeLayer.FSMRoute && props?.onRouteMarkerClick) {
          const findMapObject = mapObjects.find(item => item.id === mapObj.id);
          if (findMapObject) {
            props.onRouteMarkerClick({
              mapObject: findMapObject,
              instance: instance,
            });
            // Reset all route markers to default size FIRST,
            // then magnify the clicked one so it stays enlarged.
            showNormalMarkers();
            if (USE_WEBGL_MARKERS && webglLayer) {
              webglLayer.setSelectedRouteStop(findMapObject.id);
            }
          }
          return;
        }

        // ── Unit / generic marker click ──
        if (props.modalPopupEvent) {
          const findMapObject = mapObjects.find(item => item.id === mapObj.id);
          if (findMapObject) {
            const pickPos = info?.object?.coordinates;
            if (pickPos && findMapObject.instance) {
              try {
                findMapObject.instance.setPosition({ lat: pickPos[1], lng: pickPos[0] });
              } catch (_) { /* non-fatal */ }
            }
            props.modalPopupEvent(findMapObject, null, instance);
            if (followUnitRef?.current && mapObj.id != centerUnitIdRef?.current) {
              dispatch(setFollowUnit(false));
            }
          }
        }
      });

      // ── Manual picking: click & hover ──
      // The deck.gl container has pointer-events:none (set by
      // _raiseCanvasAboveMarkers) so its built-in event manager never
      // receives DOM events.  We compensate by registering a capture-phase
      // click handler on the map div that calls pickObject() on the GPU
      // framebuffer.  This sets _unitClickedAt BEFORE any Google Maps
      // shape/marker click handler fires, making the existing setTimeout +
      // _unitClickedAt checks in landmark handlers work reliably.
      const mapDiv = mapItem.getDiv();

      // Clean up previous handlers (map re-init)
      if (webglClickHandler) {
        mapDiv.removeEventListener('click', webglClickHandler, true);
      }
      if (webglHoverHandler) {
        mapDiv.removeEventListener('mousemove', webglHoverHandler, true);
      }
      if (webglMouseDownHandler) {
        mapDiv.removeEventListener('mousedown', webglMouseDownHandler, true);
      }
      if (webglInteractionHandler) {
        mapDiv.removeEventListener('wheel', webglInteractionHandler, true);
        mapDiv.removeEventListener('mousedown', webglInteractionHandler, true);
        mapDiv.removeEventListener('touchstart', webglInteractionHandler, true);
      }

      // Track mousedown position to distinguish real clicks from drags.
      // When the user drags the map and releases over a marker, the browser
      // fires a click event — we suppress it if the mouse moved > 5 px.
      webglMouseDownHandler = (e) => { _mouseDownPos = { x: e.clientX, y: e.clientY }; };
      mapDiv.addEventListener('mousedown', webglMouseDownHandler, true);

      webglClickHandler = (e) => {
        if (!webglLayer) return;

        // ── Drag guard ──
        // If the mouse moved more than 5px between mousedown and click,
        // the user was dragging the map, not clicking a marker.
        if (_mouseDownPos) {
          const dx = e.clientX - _mouseDownPos.x;
          const dy = e.clientY - _mouseDownPos.y;
          if (dx * dx + dy * dy > 25) return; // 5px threshold squared
        }

        // Ignore clicks on UI overlays (InfoBubble, modals, buttons, tooltips)
        // that sit above the map canvas.  Only process clicks whose target is
        // the map div itself, its internal Google Maps layers, or the deck.gl
        // canvas (which has pointer-events:none but bubbles to mapDiv).
        const target = e.target;
        if (target.closest('.gm-style-iw, .info-window-container, .live-tracking-indicator, .follow-active-button, .anim-zone-indicator, .mapButton, [role="dialog"], .MuiTooltip-popper, .MuiPopover-root')) {
          return;
        }
        const rect = mapDiv.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        webglLayer.handleClick(x, y);
      };
      mapDiv.addEventListener('click', webglClickHandler, true);

      // Throttled hover: pickObject on mousemove for tooltip + cursor
      webglHoverHandler = (e) => {
        if (!webglLayer || webglHoverRafId) return;
        webglHoverRafId = requestAnimationFrame(() => {
          webglHoverRafId = null;
          if (!webglLayer) return;
          const rect = mapDiv.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          webglLayer.handleHover(x, y, e.pageX, e.pageY);
        });
      };
      mapDiv.addEventListener('mousemove', webglHoverHandler, true);

      // Detect user gestures to temporarily pause animation centering
      // so scroll/zoom/drag are not blocked by per-frame setCenter calls
      webglInteractionHandler = () => { _lastUserMapInteraction = Date.now(); };
      mapDiv.addEventListener('wheel', webglInteractionHandler, { capture: true, passive: true });
      mapDiv.addEventListener('mousedown', webglInteractionHandler, true);
      mapDiv.addEventListener('touchstart', webglInteractionHandler, { capture: true, passive: true });

      // Push any already-existing unit objects to WebGL immediately.
      // This avoids an initial state where markers/labels don't appear
      // until the first user interaction triggers the idle handler.
      try {
        updateWebGLMarkers();
      } catch (e) {
        // Non-fatal: idle handler will refresh.
      }

      // Ensure label settings are applied from the first render.
      try {
        addLabelMarkers(TypeLayer.UnitLabel, true, false);
      } catch (e) {
        // Non-fatal: labels will sync on idle.
      }
    }

    // ── Pre-create the overlay container ──
    // The Google Maps OverlayView lifecycle (setMap → onAdd → draw) is async.
    // Creating the overlay eagerly during map init means the container is
    // already attached to the float pane by the time the user clicks a marker.
    // showMarkerModal's reuse path then fires synchronously — zero delay.
    if (!customOverlay) {
      const dummyMarker = { position: { lat: 0, lng: 0 } };
      customOverlay = new Overlay(dummyMarker, 0, () => {
        // Pre-creation complete — hide container until first showMarkerModal
        if (customOverlay?.container_) {
          customOverlay.container_.style.display = 'none';
        }
      });
      overlayRef.current = customOverlay;
      customOverlay.setMap(mapItem);
    }

    return mapItem;
  }

  const onEndBoundsChangedWeather = () => {
    //end refresh map for weather
    if(timerRefreshWeather) clearInterval(timerRefreshWeather);
    timerRefreshWeather = null;

    //Add the weather layer
    if (isWeatherEnabled && weatherPermision) {
      // Set onEnableWeatherBounds = true BEFORE calling showWeather() so that
      // getTileUrl (which checks this flag) always receives true — regardless of
      // whether Google Maps calls it synchronously (e.g. during a map-type
      // transition) or asynchronously (normal pan/zoom).
      onEnableWeatherBounds = true;
      showWeather();

      //every 6 mins the weather is updated
      timerRefreshWeather = setInterval(() => { 
        showWeather() 
      }, 360000)
    }else if(isWeatherEnabled && !weatherPermision){
      hideWeather()
    }
  }


  /*********************************************************
  * Call the save preferences and restart the timer
  *********************************************************/
  const onEndBoundsChanged = () => {

    const bounds = mapItem.getBounds();

    if (bounds && bounds.getSouthWest() && bounds.getNorthEast()) {
      lat1 = bounds.getSouthWest().lat(), lat2 = bounds.getNorthEast().lat();
      lng1 = bounds.getSouthWest().lng(), lng2 = bounds.getNorthEast().lng();
    }

    LogManagerConsole("Map", "onEndBoundsChanged")
    props.onBoundsChanged && props.onBoundsChanged(props.dataUnits, bounds)


    // WebGL mode: never use DOM-based label repositioning.
    // Trigger a WebGL label refresh based on zoom + settings.
    if (USE_WEBGL_MARKERS && webglLayer) {
      // Only apply no-overlap behavior when clusters are OFF.
      if (webglLayer.isClusteringEnabled()) return;

      const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
      const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
      if (!objectNoOverlapSettings?.enable || !objectShowLabelSettings?.enable) {
        // Ensure WebGL layer leaves normal labels on.
        addLabelMarkers(TypeLayer.UnitLabel, true, false);
        return;
      }

      addLabelMarkers(TypeLayer.UnitLabel, true, false);
      return;
    }

    // Legacy (non-WebGL) path
    //validate is cluster off
    const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
    if (objectCluster && objectCluster.instance) {
      return
    }

    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (objectNoOverlapSettings && !objectNoOverlapSettings?.enable || !objectNoOverlapSettings) {
      return
    }

    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    if (objectShowLabelSettings && !objectShowLabelSettings?.enable) {
      return
    }

    if (mapItem.getZoom() > zoomLabelsPositions) {
      //check this line to create areas.
      //activeLabelsOnMap();
      createNewPositionsToLabels();
    } else {
      valueZoomNoOverlap = mapItem.getZoom(); //update value from value zoom
      restarLabelsToDefault()
    }
  }

  /*********************************************************
  * Restart the labels no overlap
  *********************************************************/
  const restarLabelsToDefault = () => {
    // Clear WebGL cluster label overlays
    clearWebGLClusterLabels();

    // WebGL mode: clear group bubbles too.
    if (USE_WEBGL_MARKERS && webglLayer) {
      clearWebGLUnitLabelGroups();
      return;
    }

    // Clear WebGL unit-label-group overlays (legacy DOM overlays)
    clearWebGLUnitLabelGroups();

    //all to normal value
    const getLabelLines = mapObjects.filter((element) => String(element?.id || "").includes('temporalLabel_poly_') == true);

    const getCircles = mapObjects.filter((element) => String(element?.id || "").includes('circleCluster_') == true);
    getCircles.forEach((item) => {
      removeMapObject(item.id, item.type);
    })

    if (getLabelLines && getLabelLines.length > 0) {
      getLabelLines.forEach((item) => {
        removeMapObject(item.id, item.type);
      })

      const getMapLabels = mapObjects.filter((element) => String(element?.id || "").includes('label_') && element.labelPosition && element.instance);
      getMapLabels.forEach((element) => {
        //update defaul position label
        element.position = element.startPosition;
        element.labelPosition = false;
        element.labelGroup = null;
        element.labelPositionSpace = null;
        element.labelGroupArea = null;
        element.labelGroupIdMain = null;

        const unit = getObject(TypeObjectMap.Marker, element.id.toString().replace('label_', ''))
        unit.labelPosition = false;
        unit.labelGroup = null;
        unit.labelPositionSpace = null;
        unit.labelGroupArea = null;
        unit.labelGroupIdMain = null;

        const googlePosition = new google.maps.LatLng(element.position.lat, element.position.lng);
        element.instance.onMove(googlePosition);
      })
      addLabelMarkers(TypeLayer.UnitLabel, true, false)
    }
  }

  /* eslint-disable no-unused-vars */
  /******************************************************
   * Load the init Zoom Buttons into the UI
   ******************************************************/
  const initZoomControl = (map) => {

    document.getElementById("zoomIn-idcontrol").onclick = function () {
      map.setZoom(map.getZoom() + 1);
    };

    document.getElementById("zoomOut-idcontrol").onclick = function () {
      map.setZoom(map.getZoom() - 1);
    };

    // Push the single group container so both buttons stay connected
    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push( // eslint-disable-line no-undef
      document.querySelector(".zoom-buttons-group")
    );
  }

  /******************************************************
   * Style native Google Maps controls and control wrappers.
   *
   * Google Maps v3.63 with vector maps (mapId) wraps each
   * pushed control in its own <div> with inline styles
   * (background, box-shadow, border-radius 2px, overflow hidden).
   * CSS alone cannot override these — we must apply inline
   * styles to the wrapper divs via JS.
   *
   * This function polls for:
   *  1. LEFT_TOP control wrappers → rounded corners + softer shadow
   *  2. RIGHT_BOTTOM container → padding-right from edge
   *  3. Native pegman (street view) → matching rounded style
   ******************************************************/
  const styleNativeControl = (map) => {
    const softShadow = '0 1px 4px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)';
    const hoverShadow = '0 2px 6px rgba(0,0,0,0.2), 0 0 0 0.5px rgba(0,0,0,0.08)';

    // Apply modern style to a Google Maps wrapper div
    const styleWrapper = (wrapper) => {
      if (!wrapper || wrapper._styledByApp) return;
      wrapper._styledByApp = true;
      wrapper.style.borderRadius = '8px';
      wrapper.style.boxShadow = softShadow;
      wrapper.style.overflow = 'hidden';
      wrapper.style.transition = 'box-shadow 0.2s ease';
      wrapper.addEventListener('mouseenter', () => { wrapper.style.boxShadow = hoverShadow; });
      wrapper.addEventListener('mouseleave', () => { wrapper.style.boxShadow = softShadow; });
    };

    const applyPegmanStyles = (el) => {
      if (el._styledByApp) return;
      el._styledByApp = true;
      el.style.borderRadius = '8px';
      el.style.boxShadow = softShadow;
      el.style.transition = 'box-shadow 0.2s ease';
      el.style.marginBottom = '6px';
      el.addEventListener('mouseenter', () => { el.style.boxShadow = hoverShadow; });
      el.addEventListener('mouseleave', () => { el.style.boxShadow = softShadow; });
    };

    let attempts = 0;
    let pegmanDone = false;
    let leftTopDone = false;
    let rightBottomDone = false;

    const poll = setInterval(() => {
      attempts++;
      const mapDiv = map.getDiv();

      // ── LEFT_TOP wrappers ──
      // Each pushed control (.controls, .controlSearch) sits inside
      // a Google Maps wrapper div. Find by class and style the parent.
      if (!leftTopDone) {
        const leftControls = mapDiv.querySelectorAll('.controls, .controlSearch');
        let styled = 0;
        leftControls.forEach((ctrl) => {
          const wrapper = ctrl.parentElement;
          if (wrapper && wrapper !== mapDiv && !wrapper._styledByApp) {
            styleWrapper(wrapper);
          }
          if (wrapper?._styledByApp) styled++;
        });
        if (styled > 0 && styled >= leftControls.length) leftTopDone = true;
      }

      // ── RIGHT_BOTTOM padding ──
      if (!rightBottomDone) {
        const anyButton = mapDiv.querySelector('.mapButton');
        if (anyButton) {
          let el = anyButton.parentElement;
          while (el && el !== mapDiv) {
            const r = el.style?.right;
            if (r === '0px' || r === '0') {
              el.style.paddingRight = '10px';
              rightBottomDone = true;
              break;
            }
            el = el.parentElement;
          }
        }
      }

      // ── Native pegman ──
      if (!pegmanDone) {
        const pegman = mapDiv.querySelector('.gm-svpc');
        if (pegman) {
          applyPegmanStyles(pegman);
          pegmanDone = true;
        }
      }

      // Stop when everything is styled or we hit the limit
      if ((pegmanDone && leftTopDone && rightBottomDone) || attempts > 20) {
        clearInterval(poll);
      }
    }, 200);
  }

  /******************************************************
   * Custom Fullscreen button (replaces native control hidden in shadow DOM)
   ******************************************************/
  const initFullscreenControl = (map) => {
    const btn = document.getElementById("fullscreen-idcontrol-btn");
    if (!btn) return;
    btn.onclick = () => {
      const mapDiv = map.getDiv();
      if (document.fullscreenElement === mapDiv) {
        document.exitFullscreen();
      } else {
        mapDiv.requestFullscreen();
      }
    };
  }

  /******************************************************
   * Show the elements to label actives on map
  ******************************************************/
  const activeLabelsOnMap = () => {
    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (objectNoOverlapSettings && objectNoOverlapSettings.enable) {

      //removeMapObjects(TypeObjectMap.Polyline);
      //temporalLabel_poly_
      const mapObjectsLines = mapObjects.filter((item) => item.type == TypeObjectMap.Polyline && item.layerName == TypeLayer.UnitLabel)
      mapObjectsLines.forEach((mapObjectToDelete) => {
        removeMapObject(mapObjectToDelete.id, TypeObjectMap.Polyline);
      })

      const objectUnitCluster = getObject(TypeObjectMap.Settings, clusterId);
      if (objectUnitCluster && objectUnitCluster.enable) {
        const mapObjectsLabels = mapObjects.filter((item) => item.type == TypeObjectMap.Label && item.layerName == TypeLayer.UnitLabel)
        mapObjectsLabels.forEach((mapObjectToDelete) => {
          removeMapObject(mapObjectToDelete.id, TypeObjectMap.Label);
        })
      }
    }
  }

  /******************************************************
   * Zoom out 
  ******************************************************/
  const setZoomOut = (numberOfPoints = 1) => {
    if (mapItem)
      mapItem.setZoom(mapItem.getZoom() - numberOfPoints);
  }

  /******************************************************
   * Zoom out 
  ******************************************************/
  const setZoom = (defaulZoom = 4) => {
      mapItem.setZoom(defaulZoom);
  }

  /******************************************************
   * Return the default value on zoom on map
  ******************************************************/
  const getZoom = () => {
    return mapItem.getZoom();
  }

  /******************************************************
   * Hide or show kml files from mouse wheel zooming 
  ******************************************************/
  const showsKmlObjectsByZoom = (zoomMap) => {
    if (kmlList && kmlList.length > 0) {
      kmlList.forEach(element => {
        const typeLayer = validateTypeLayerKml(element)
        showKmlObject(
          element.id,
          (zoomMap >= element.zoom),
          element.path,
          typeLayer
        )
      });
    }
  }

  /******************************************************
   * Load Button or options into the realtime maps
   ******************************************************/
  const initButtonOnMap = (idControl, position, map) => {
    if (map) {
      map.controls[position].push(
        document.getElementById(idControl)
      );
    }
  }

  /* eslint-enable no-unused-vars */

  /******************************************************
   * Create a new kml object into the realtime maps
   ******************************************************/
  const showKmlObject = (key, show, url, layerType) => {
    if (show) {
      const element = getObject(TypeObjectMap.KmlFile, key)

      if (!element) {
        const mapObject = {
          id: key,
          url: url,
          layerName: layerType,
          type: TypeObjectMap.KmlFile
        }
        addMapObject(mapObject)
      }
    } else {
      removeMapObject(key, TypeObjectMap.KmlFile)
    }
  }

  /******************************************************
   * Show the traffic elements into the realtime maps
   ******************************************************/
  const showTraffic = () => {
    if (traffic == null) {
      const trafficLayer = new google.maps.TrafficLayer(); // eslint-disable-line no-undef
      if (mapItem) {
        trafficLayer.setMap(mapItem);
        setTraffic(trafficLayer);
      } else {
        let trafficTimer = setTimeout(() => {
          trafficLayer.setMap(mapItem);
          setTraffic(trafficLayer);
          trafficTimer = null;
        }, 1500);
      }
    }
  }

  const hideTraffic = () => {
    traffic?.setMap(null);
    setTraffic(null);
  }

  const getRoundedDate = (minutes, d = new Date()) => {

    let ms = 1000 * 60 * minutes; // convert minutes to ms
    let roundedDate = new Date(Math.floor(d.getTime() / ms) * ms);

    return roundedDate
  }

  const showWeather = (paramLayers = null) => {
    // Guard: MVCArray.removeAt throws RangeError on empty array. After setMapTypeId,
    // Google Maps internally resets overlayMapTypes before the next idle fires.
    if (mapItem?.overlayMapTypes?.getLength() > 0) {
      mapItem.overlayMapTypes.removeAt(0);
    }
    mapItem?.overlayMapTypes.push(null);

    if(paramLayers != null && weatherLayersOnShow != paramLayers) weatherLayersOnShow = paramLayers;

    let radar = new google.maps.ImageMapType({
      getTileUrl: function (coord, zoom) {
        //let layers = 'satellite,radar';
        let layers = paramLayers != null ? paramLayers : weatherLayersOnShow;

        //let returnUri = [`${endpoints.MAP_WEATHER_URL}/maps/2.0/radar/`, zoom, '/', coord.x, '/', coord.y, `?appid=${endpoints.OPEN_WEATHER_KEY}`, `&tm=${moment(getRoundedDate(10)).add(-20, 'minutes').unix()}`].join('');
        
        if(!weatherPermision) return

        if(layers == "") return

        let returnUri = endpoints.MAP_WEATHER_URL + "/" + endpoints.MAP_WEATHER_ID + "_" + endpoints.OPEN_WEATHER_KEY + "/"+layers+"/" + zoom + "/" + coord.x + "/" + coord.y +'/current.png';

        if(!onEnableWeatherBounds) return


        /* if (zoom == 7) {
          let values = sessionStorage.getItem("weaterLayer")
          values = values ? JSON.parse(values) : []
          values.push({ key: `${coord.x}_${coord.y}`, value: returnUri })
          sessionStorage.setItem("weaterLayer", JSON.stringify(values))
        }
        else if (zoom < 7) {
          sessionStorage.setItem("weaterLayer", "[]")
        }

        if (zoom > 7) {
          let values = sessionStorage.getItem("weaterLayer")
          values = values ? JSON.parse(values) : []
          let lastZoom7 = values.find(x => x.key == `${coord.x}_${coord.y}`)
          return lastZoom7?.value
        } */

        

        if (zoom >= 4 && zoom <= 13) {
          return returnUri   
        }
        //return returnUri

      },
      tileSize: new google.maps.Size(256, 256),
      maxZoom: 14,
      minZoom: 4
    });

    if (mapItem) {
      mapItem?.setOptions({ minZoom: 0, maxZoom: 22 });
      mapItem?.overlayMapTypes.setAt(0, radar);
      isWeatherEnabled = true;
    } else {
      let weatherTimer = setTimeout(() => {
        mapItem?.setOptions({ minZoom: 0, maxZoom: 22 });
        mapItem?.overlayMapTypes.setAt(0, radar);
        isWeatherEnabled = true;
        weatherTimer = null;
      }, 1500);
    }
  }

  // const showWeather = () => {
  //   mapItem?.overlayMapTypes.removeAt(0);
  //   mapItem?.overlayMapTypes.push(null);

  //   let radar = new google.maps.ImageMapType({
  //     getTileUrl: function (coord, zoom) {
  //       //return [`${endpoints.MAP_WEATHER_URL}/maps/2.0/weather/CL/`, zoom, '/', coord.x, '/', coord.y, `?appid=${endpoints.OPEN_WEATHER_KEY}`, '&palette=0:d3d2d2;10:c8c7c7;20:bdbcbc;30:b1b0b0;40:a6a5a5;50:9b9a9a;60:908f8f;70:858383;80:7a7878;90:6f6d6d;100:636262;200:585757', '&opacity=0.4'].join('');
  //       //return [`${endpoints.MAP_WEATHER_URL}/maps/2.0/weather/PR0/`, zoom, '/', coord.x, '/', coord.y, `?appid=${endpoints.OPEN_WEATHER_KEY}`,'&opacity=0.4'].join('');
  //       //https://maps.openweathermap.org/maps/2.0/radar/{z}/{x}/{y}?appid={API key}&tm={date}

  //       return [`${endpoints.MAP_WEATHER_URL}/maps/2.0/weather/1h/PR0/`, zoom, '/', coord.x, '/', coord.y, `?appid=${endpoints.OPEN_WEATHER_KEY}`, '&opacity=0.4'].join('');
  //     },
  //     tileSize: new google.maps.Size(256, 256),
  //     maxZoom: 9,
  //     minZoom: 0
  //   });

  //   if (mapItem) {
  //     mapItem?.setOptions({ minZoom: 4, maxZoom: 13 });
  //     mapItem?.overlayMapTypes.setAt(0, radar);
  //     isWeatherEnabled = true;
  //   } else {
  //     let weatherTimer = setTimeout(() => {
  //       mapItem?.setOptions({ minZoom: 4, maxZoom: 13 });
  //       mapItem?.overlayMapTypes.setAt(0, radar);
  //       isWeatherEnabled = true;
  //       weatherTimer = null;
  //     }, 1500);
  //   }
  // }

  const hideWeather = () => {
    mapItem?.setOptions({ minZoom: 0, maxZoom: 22 });
    mapItem?.overlayMapTypes.removeAt(0);
    isWeatherEnabled = false;
  }

  /*******************************************************
   * Validate eventName to get color from the unit
   * @param {Object JSON} item 
   *******************************************************/
  const validateEventColor = (item) => {

    if(!item) return ''

    let eventName = '';
    if (item['eventName']) {
      eventName = item['eventName'].toString();
    }

    if (item['event'] && eventName == '') {
      eventName = item['event'].toString();
    }

    return getEventColor(eventName);
  }

  /******************************************************
   * Add the marker into google maps to google.maps.Marker
   * @param {JSON} mapObject 
   ******************************************************/
  const addMarker = (mapObject) => {
    if (!validateNewObject(mapObject)) {
      return
    }
    let color = ''
    if (mapObject.color === '#FFFFFF' || !mapObject.color) {
      color = validateEventColor(mapObject)
      mapObject.color = color;
    } else {
      color = mapObject.color
    }

    let iconMarker = '', iconMarkerMagnified = '', isTrailClipRequest = false;
    switch (mapObject.layerName) {
      case TypeLayer.Units:
        iconMarker = getIconAttributes(color, mapObject.heading, null, null, mapObject)
        break;
      case TypeLayer.VehicleTrailLayer:
        iconMarker = getIconAttributes(color, mapObject.heading)
        break;
      case TypeLayer.VehicleTrails:
        let url = null;
        let imageStyle = {};
        //Vidfleet camera events
        if (mapObject?.eventCode && mapObject?.eventCode?.includes("CameraEvent")) {
          let event = getVidFleetEvent(mapObject?.eventCode);
          url = event.icon;
          imageStyle = {
            anchor: new google.maps.Point(15, 10)  // eslint-disable-line no-undef
          };
        //LightMetric camera events
        } else if (mapObject?.eventCode && mapObject?.eventCode?.includes("event")) {
          let event = getLightMetricEvent(mapObject?.eventCode);
          url = event.icon;
          imageStyle = {
            anchor: new google.maps.Point(15, 10)  // eslint-disable-line no-undef
          };
          //Clip request icons on map
        } else if(mapObject?.eventCode && mapObject?.eventCode?.includes("clipRequestIcons")) {
          iconMarker = mapObject?.icon;
          isTrailClipRequest = true;
        //Switness camera events
        } else if(mapObject?.eventCode && getSwitnessEvent(mapObject?.eventCode)) {
          let event = getSwitnessEvent(mapObject?.eventCode);
          if(event?.icon){
            url = event.icon;
            imageStyle = {
              anchor: new google.maps.Point(15, 10)  // eslint-disable-line no-undef
            };
          }
        }
        //if not camera icons, find the default markers for marker trails
        if(!isTrailClipRequest){
          iconMarker = getIconAttributes(color, mapObject.heading, url, imageStyle, mapObject)
        }
        break;

      case TypeLayer.PolygonCreate:
        iconMarker = getIconForPolygonCreate(color);
        break;

      case TypeLayer.SquareCreate:
      case TypeLayer.CircleCreate:
        if (mapObject?.url)
          iconMarker = getIconAttributes(
            null,
            90,
            null,
            { //styleImage
              scale: 1,
              size: new google.maps.Size(10, 10),
              anchor: new google.maps.Point(10, 10),
              fillColor: '#000000',
              fillOpacity: 1,
              strokeColor: '#000000', //'#6f6f6f',
              sstrokeWeight: 0,
              rotation: 45,
            },
            null,
            'M21 11L21 3L13 3L16.29 6.29L6.29 16.29L3 13L3 21L11 21L7.71 17.71L17.71 7.71z'
          )
        break;

      case TypeLayer.FSMRoute:
            
            if(!mapObject?.isRouteTracker){
              iconMarker = createIconMarkerForRoute(mapObject)
            }else{
              //show icons for routeTracker
              iconMarker = createIconMarkerForRouteTracker(mapObject)
            }


            iconMarkerMagnified = {
              ...iconMarker,
              scaledSize: new google.maps.Size(48, 48) // Tamaño del icono aumentado
            };

            mapObject.defaultIconIncrease = iconMarkerMagnified;

        break;


    }

    //console.log('iconMarker for marker', iconMarker);

    let marker = null;

    if(!iconMarker && mapObject?.icon){
      iconMarker = mapObject?.icon
    }

    mapObject.defaultIcon = iconMarker;

    // WebGL mode renders unit markers AND route stops via deck.gl.
    // Keep the MarkerCompat instance for internal listeners (follow-unit,
    // position_changed, etc.) but never render it on the map to avoid
    // any visual flashes or "fly" from (0,0).
    // ETA link pages have no WebGL layer — always render DOM markers there.
    const renderOnMap = props.isEtaLink || !(USE_WEBGL_MARKERS && (
      mapObject.layerName === TypeLayer.Units || mapObject.layerName === TypeLayer.FSMRoute
    ));
    
    marker = new MarkerCompat({
      id: mapObject.id,
      position: mapObject.position,
      map: renderOnMap ? mapItem : null,
      draggable: mapObject.draggable,
      icon: iconMarker,
      title: mapObject?.label ? mapObject?.label.toString() : '',
      zIndex: mapObject.zIndex || 10,
      visible: renderOnMap ? !mapObject.hideMarker : false,
      crossOnDrag: typeof mapObject?.crossOnDrag == "boolean" ? mapObject?.crossOnDrag : true
    });

    //Drag and drop event
    if (mapObject.draggable) {
      const dragEvent = marker.addListener('drag', function (event) {
        if (event?.paramMarker?.instance) {
          event.paramMarker.instance.setPosition(event.newPosition)
        } else {
          if (props.onDragMarkerEvent) {
            const findMapObject = mapObjects.find(item => item.id == mapObject.id)
            return props.onDragMarkerEvent(event, findMapObject)
          }
        }
      });

      //add dragsend events to marker for polygon
      if (mapObject?.saveDragEvent) {


        const dragStartEvent = marker.addListener('dragstart', onDragStartSquareEvent);
        const dragEndEvent = marker.addListener('dragend', onDragEndSquareEvent);

        mapObject.dragEndEvent = dragEndEvent
        mapObject.dragEvent = dragEvent
        mapObject.dragStartEvent = dragStartEvent
      }



      marker.addListener('rightclick', function () {
        if (props.onMarkerRightClick) {
          const findMapObject = mapObjects.find(item => item.id == mapObject.id)
          props.onMarkerRightClick(findMapObject)
        }
      });
      //Add the click events for markers
    } else if (mapObject.layerName != TypeLayer.VehicleTrailLayer && mapObject.layerName != TypeLayer.VehicleTrailsTrailHistory && mapObject.layerName != TypeLayer.Pointers && !mapObject?.noClickEvent) {
      
      marker.addListener('click', () => {
        if (mapObject?.onClick) {
          mapObject?.onClick(mapObject);
        } else if(props?.isEditRoutesModule && mapObject.layerName == TypeLayer.FSMRoute && props?.onRouteMarkerClick){
          const findMapObject = mapObjects.find(item => item.id == mapObject.id)
          props.onRouteMarkerClick({
            mapObject: findMapObject, 
            instance: instance
          })
          //first show the default icon for all markers
          showNormalMarkers()
          //magnified the icon after the selection
          marker.setIcon(iconMarkerMagnified);
          
        } else if ( props.modalPopupEvent) {
          const findMapObject = mapObjects.find(item => item.id == mapObject.id)
          props.modalPopupEvent(findMapObject, null, instance)

          //if vehicle trails markers show the element on table vehicle trail (focus)
          if(mapObject.layerName == TypeLayer.VehicleTrails){
            props.onClickMarkerEvent(mapObject.id)
          }

          //remove the followUnit is the user click on the map and select other unit
          if (followUnitRef?.current && mapObject?.id != centerUnitIdRef?.current) {
            dispatch(setFollowUnit(false));
          }
        }
      })
    }

    //is vehicle marker
    if(TypeLayer.Units == mapObject.layerName && mapObject?.type == TypeObjectMap.Marker){
        let lastUpdate = 0;
        marker.addListener('position_changed', () => {
          const now = Date.now();
          
          // Limita a 10 actualizaciones por segundo
          if (centerUnitIdRef?.current == mapObject?.id && !mapObject?.hideMarker && followUnitRef?.current) {
            if (now - lastUpdate > CENTER_UPDATE_INTERVAL && (Date.now() - _lastUserMapInteraction > USER_INTERACTION_PAUSE_MS)) {
              centerMapAnimated(marker?.getPosition()?.lat(), marker?.getPosition()?.lng());
              lastUpdate = now;
            }
          }

      });
    }

    //show label position
    if ((mapObject.label && mapObject.showLabel) && mapObject.layerName == TypeLayer.Pointers) {
      const infowindow = new google.maps.InfoWindow({
        content: DOMPurify.sanitize(`<div id="content">${mapObject.label ? '<p>' + mapObject.label + '</p>' : ''}
                            <p>${mapObject.position.lat}, ${mapObject.position.lng}</p></div>`)
      });

      infowindow.open({
        anchor: marker,
        mapItem,
        shouldFocus: false,
      });

      marker.addListener('click', () => {
        infowindow.open({
          anchor: marker,
          mapItem,
          shouldFocus: false,
        });
      })
    }

    // In WebGL mode, hide the individual marker for unit layers — WebGL handles rendering.
    // Skip for ETA link — no WebGL layer is initialized on that page.
    if (USE_WEBGL_MARKERS && !props.isEtaLink && mapObject.layerName === TypeLayer.Units) {
      const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
      if (objectCluster?.instance && !objectCluster.instance._webglCluster) {
        // Real legacy MarkerClusterer — add marker to it
        objectCluster.instance.addMarker(marker);
      } else {
        // WebGL mode (no cluster, or WebGL-native cluster) — hide DOM marker
        marker.setMap(null);
      }
    }

    return marker
  }

  /**********************************************************************
   * Show style to normal style markers in map
   * @param {Object} event 
   **********************************************************************/
  const showNormalMarkers = (layer = TypeLayer.FSMRoute) => {
    const findMarkersToUpdateStyle = mapObjects.filter(item => item.type == TypeObjectMap.Marker && item.layerName == layer);
    if(findMarkersToUpdateStyle){
      findMarkersToUpdateStyle.forEach((item) => {
        item.instance.setIcon(item.defaultIcon);
      })
    }
    // Also reset WebGL route stop magnification
    if (USE_WEBGL_MARKERS && webglLayer && layer === TypeLayer.FSMRoute) {
      webglLayer.setSelectedRouteStop(null);
    }
  }

  /**********************************************************************
   * Show style to normal style markers in map
   * @param {Object} event 
   **********************************************************************/
  const setSizeMarkerIcon = (id = '', layer = TypeLayer.FSMRoute, iconSize = EnumIconSize.Default) => {
    if(layer == TypeLayer.FSMRoute){

      const findMarkersToUpdateStyle = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == layer && item.id == id);
      if(findMarkersToUpdateStyle){
        switch(iconSize){
            case EnumIconSize.Default:
              findMarkersToUpdateStyle.instance.setIcon(findMarkersToUpdateStyle.defaultIcon);
              break;

            case EnumIconSize.Magnified:
              findMarkersToUpdateStyle.instance.setIcon(findMarkersToUpdateStyle.defaultIconIncrease);
              break;
        }
      }

      // Also update WebGL route stop selection
      if (USE_WEBGL_MARKERS && webglLayer) {
        webglLayer.setSelectedRouteStop(
          iconSize === EnumIconSize.Magnified ? id : null
        );
      }

    }else if(layer == TypeLayer.Units){
      //to change the icon when the user selected a unit on map
      const findMarkersToUpdateStyle = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == layer && item.id == id);
      
      const color = validateEventColor(findMarkersToUpdateStyle)
        if (!findMarkersToUpdateStyle.icon) {
          findMarkersToUpdateStyle.instance.setIcon(getIconAttributes('#' + color, findMarkersToUpdateStyle.heading, null, null, findMarkersToUpdateStyle));
        }else{      
          const iconMarker = getIconAttributes(findMarkersToUpdateStyle.color, 0, findMarkersToUpdateStyle.icon, false)
          findMarkersToUpdateStyle.instance.setIcon(iconMarker)
        }
    }
    
  }


  /**********************************************************************
   * Event asociated to square object, perform the calc to minimun value to square.
   * @param {Object} event 
   **********************************************************************/
  function onDragStartSquareEvent(event) {
    const findSquareObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Square)
    if (findSquareObjectToUpdate) {
      const { north, south, east, west } = findSquareObjectToUpdate.instance.getBounds().toJSON()
      if (north >= 0) {
        squareLatMin = north - minimunSizeSquare
        //squareLatMinSing = '>'
      } else if (north < 0) {
        squareLatMin = north - minimunSizeSquare
        //squareLatMinSing = '>'
      }

      if (west <= 0) {
        squareLngMin = west + minimunSizeSquare
        //squareLngMinSing = '<'
      } else if (west > 0) {
        squareLngMin = west + minimunSizeSquare
        //squareLngMinSing = '<'
      }
    }
  }

  /**********************************************************************
   * Event asociated to square object, update the parameters to draw square.
   * @param {Object} event 
   **********************************************************************/
  function onDragEndSquareEvent(event) {
    const findSquareObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Square)
    if (findSquareObjectToUpdate) {
      findSquareObjectToUpdate.minBoundsRectangle = findSquareObjectToUpdate.instance.getBounds().toJSON()

      if (!findSquareObjectToUpdate.freeRectangle) {
        updateSquareSizeToDragEvents(findSquareObjectToUpdate, event, true)
      }
    }
  }

  const updateSquareSizeToDragEvents = (findSquareObjectToUpdate, event, isDragEnd = false) => {
    //drag and drop the marker              
    const { lat, lng } = event.latLng.toJSON();

    //create square for last position / return distance in kilometers
    let radioSquare = calcDistanceToPoints(findSquareObjectToUpdate.position.lat, findSquareObjectToUpdate.position.lng,
      lat, lng);

    if (findSquareObjectToUpdate.maxSize > 0 && radioSquare > findSquareObjectToUpdate.maxSize) {

      const coordinatesFromCenter = createSquareBoundingboxByMilesDistance(findSquareObjectToUpdate.position.lat,
        findSquareObjectToUpdate.position.lng,
        findSquareObjectToUpdate.maxSize);

      const boundsToSquare = {
        north: coordinatesFromCenter[0].latitude,
        south: coordinatesFromCenter[1].latitude,
        east: coordinatesFromCenter[0].longitude,
        west: coordinatesFromCenter[2].longitude,
      };
      findSquareObjectToUpdate.boundsRectangle = boundsToSquare;

      //update the square size
      findSquareObjectToUpdate.instance.setBounds(boundsToSquare)

      //calc the point to marker
      const newLngOnMarkerDrag = getLngByDistance(findSquareObjectToUpdate.position.lat,
        findSquareObjectToUpdate.position.lng,
        findSquareObjectToUpdate.maxSize)
      const position = { lat: findSquareObjectToUpdate.position.lat, lng: newLngOnMarkerDrag }
      const findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker &&
        item.layerName == TypeLayer.SquareCreate)
      if (findMarkerSquareObject) {
        findMarkerSquareObject.instance.setPosition(position)
        findMarkerSquareObject.position = findMarkerSquareObject
      }

      radioSquare = findSquareObjectToUpdate.maxSize //convert to metters
      findSquareObjectToUpdate && findSquareObjectToUpdate?.onUpdateList && findSquareObjectToUpdate?.onUpdateList(findSquareObjectToUpdate.maxSize)
      return

    }
    //radioSquare value is in metters
    const coordinatesFromCenter = createSquareBoundingboxByMilesDistance(findSquareObjectToUpdate.position.lat,
      findSquareObjectToUpdate.position.lng,
      radioSquare);

    const boundsToSquare = {
      north: coordinatesFromCenter[0].latitude,
      south: coordinatesFromCenter[1].latitude,
      east: coordinatesFromCenter[0].longitude,
      west: coordinatesFromCenter[2].longitude,
    };
    findSquareObjectToUpdate.boundsRectangle = boundsToSquare;

    //update the square size
    findSquareObjectToUpdate.instance.setBounds(boundsToSquare)

    const coordinates = convertBoundsToCoordinates(boundsToSquare)
    findSquareObjectToUpdate.coordinates = coordinates;
    findSquareObjectToUpdate.position = { lat: findSquareObjectToUpdate.position.lat, lng: findSquareObjectToUpdate.position.lng }

    //const position = { lat: boundsToSquare.south, lng: boundsToSquare.east }
    if (isDragEnd) {
      const findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker &&
        item.layerName == TypeLayer.SquareCreate)
      //update marker position into objects
      if (findMarkerSquareObject) {
        //calc the point to marker
        const newLngOnMarkerDrag = getLngByDistance(findSquareObjectToUpdate.position.lat,
          findSquareObjectToUpdate.position.lng,
          radioSquare) //in metters
        const position = { lat: findSquareObjectToUpdate.position.lat, lng: newLngOnMarkerDrag }
        findMarkerSquareObject.instance.setPosition(position)
        findMarkerSquareObject.position = findMarkerSquareObject
      }

      //center the element on map
      centerByFitBounds(coordinates);
    }

    //if have event to update list
    findSquareObjectToUpdate && findSquareObjectToUpdate?.onUpdateList && findSquareObjectToUpdate?.onUpdateList(radioSquare)

  }

  /******************************************************
   * Update the size for the square and update the marker position
   * @param {double} diagonally on the square on metters
   ******************************************************/
  const setUpdateSquareDiagonally = (diagonallyOnMetters) => {

    //find the square object in map
    const findSquareObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Square)
    if (!findSquareObjectToUpdate) return;
    const squareCoordinates = createSquareBoundingboxByMilesDistance(findSquareObjectToUpdate.position.lat, findSquareObjectToUpdate.position.lng, diagonallyOnMetters); //convert metters to feet
    const boundsToSquare = {
      north: squareCoordinates[0].latitude,
      south: squareCoordinates[1].latitude,
      east: squareCoordinates[0].longitude,
      west: squareCoordinates[2].longitude
    };

    findSquareObjectToUpdate.boundsRectangle = boundsToSquare;

    //update the square size
    findSquareObjectToUpdate.instance.setBounds(boundsToSquare);
    const coordinates = convertBoundsToCoordinates(boundsToSquare);
    findSquareObjectToUpdate.coordinates = coordinates;

    //const position = { lat: boundsToSquare.south, lng: boundsToSquare.east };
    const findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.SquareCreate);
    //update marker position into objects
    if (findMarkerSquareObject) {
      const centerSquare = getCenterFromBounds(boundsToSquare)
      const position = { lat: centerSquare.lat, lng: boundsToSquare.east }
      findMarkerSquareObject.instance.setPosition(position)
      findMarkerSquareObject.position = position
    }

    centerByFitBounds(coordinates);

  }

  /******************************************************
   * Add the marker into google maps to google.maps.Marker
   * @param {JSON} mapObject 
   ******************************************************/
  const addKml = (mapObject) => {

    const kmlLayer = new google.maps.KmlLayer(mapObject.url, { // eslint-disable-line no-undef
      suppressInfoWindows: true,
      preserveViewport: true,
      map: mapItem,
      pane: "markerLayer",
      zIndex: mapObject.layerName == TypeLayer.KmlMarker ? 1 : 0,
      //clickable: false
    });

    //Add the event click on the kmls objects.
    kmlLayer.addListener('click', function (event) {
      // Defer to let deck.gl's onClick fire first and set _unitClickedAt.
      setTimeout(() => {
        if (webglLayer && (Date.now() - (webglLayer._unitClickedAt || 0)) < 300) return;
        const { featureData } = event
        if (featureData && featureData.name && props.modalPopupEvent && !realStateInfoWindow) {
        const obj = {
          id: featureData.name,
          position: event.latLng,
          icon: {
            rotation: 0
          },
          layerName: mapObject.layerName == TypeLayer.KmlGeofence ? TypeLayer.Geofence : TypeLayer.Landmark
        }
        props.modalPopupEvent(obj, obj);
      }
      }, 50);
    });

    return kmlLayer;
  }

  /******************************************************
   * Create the new object route into the realtime map
  ******************************************************/
  const addRoute = () => {
    try {
      const panelContainer = document.getElementById('directionsPanel');
      const directionsRenderer = new google.maps.DirectionsRenderer(); // eslint-disable-line no-undef
      directionsRenderer.setMap(mapItem);
      if (panelContainer) {
        panelContainer.innerHTML = "";
        directionsRenderer.setPanel(panelContainer);
      }

      return directionsRenderer;
    } catch (ex) {
      console.log('load the routes', ex);
    }
    return null
  }

  /******************************************************
   * Show the modal popup at the marker into the realtime maps
   * @param {mapObject} marker object
   * @param {string} content react component
   ******************************************************/
  const showMarkerModal = (marker, mapObject, content, position, currentZoom) => {
    const newMapObject = { id: mapObject?.id?.toString(), layerName: mapObject?.layerName };
    const newCurrentMarkerModal = { id: currentMarkerModalRef.current?.id?.toString(), layerName: currentMarkerModalRef.current?.layerName };

    if (!_.isEqual(newMapObject, newCurrentMarkerModal) || !openInfoWindowRef.current) {
      currentMarkerModalRef.current = mapObject;

      // Cancel any pending hideMarkerModal timeout that would reset
      // realStateInfoWindow to false after 500ms.  Without this,
      // a quick hide → show sequence leaves the timeout active and
      // it fires AFTER the new modal is open, breaking
      // getOpenInfoWindow() and modal position tracking.
      if (window.openInfoBubble) {
        clearTimeout(window.openInfoBubble);
        window.openInfoBubble = null;
      }

      // ── Reuse existing overlay when switching markers ──
      // Destroying the old overlay and creating a new one forces the full
      // Google Maps OverlayView lifecycle (onAdd → pane attach → draw),
      // which is asynchronous and introduces a visible delay before the
      // InfoBubble appears at the new marker.  Instead, if an overlay
      // already exists and is attached to the map, reuse it: update its
      // marker reference, id, and move it to the new position.  This keeps
      // the container, pane attachment, and projection intact — draw()
      // fires synchronously via onMove(), so the portal renders
      // immediately without waiting for Google Maps' async pipeline.
      if (customOverlay && customOverlay.container_) {
        // Update the overlay's marker and id to the new target
        customOverlay.marker_ = marker;
        customOverlay.id = mapObject?.id || 0;
        // Clear the cached onMove position so draw() reads from the new marker
        if (customOverlay.overlay_) {
          customOverlay.overlay_.position = null;
        }
        // Move overlay to the new marker's position BEFORE making the
        // container visible.  hideMarkerModal hides it via display:none,
        // so it's still hidden here.  Repositioning first prevents the
        // container from flashing at the OLD marker's pixel coordinates
        // for one frame before draw() recalculates the new position.
        if (position) {
          const movePos = new google.maps.LatLng(position.lat, position.lng); // eslint-disable-line no-undef
          customOverlay.onMove(movePos);
        } else {
          customOverlay.draw();
        }
        // Now that the container sits at the correct position, make it visible
        customOverlay.container_.style.display = '';
        // Mark as open
        openInfoWindowRef.current = true;
        realStateInfoWindow = true;

        // ── Immediate content update ──
        // Update the content ref and trigger a synchronous render so the
        // portal reflects the new marker data in the same visual frame.
        // Use queueMicrotask instead of flushSync to avoid the React
        // warning "flushSync was called from inside a lifecycle method"
        // when this path is reached from a useEffect (e.g. the
        // centerUnitId effect → showInfoWindow → centerObjectOnMap →
        // modalPopupEvent → showMarkerModal).  Microtasks execute before
        // the browser paints, so there is no visible flash.
        infoBubbleContentRef.current = content;
        queueMicrotask(() => { infoBubbleUpdateRef.current?.(); });

        if (position) centerPointOnMap(position?.lat, position?.lng, currentZoom, -190);
        return;
      } else {
        // No existing overlay — create from scratch (first open only)
        if (customOverlay) {
          customOverlay.setMap(null);
          customOverlay = null;
          overlayRef.current = null;
          openInfoWindowRef.current = false;
          realStateInfoWindow = false;
          mapItem && mapItem.setOptions({ draggable: true });
        }

        customOverlay = new Overlay(marker, mapObject?.id || 0, () => {
          openInfoWindowRef.current = true;
          realStateInfoWindow = true;
          // Trigger InfoWindow now that the container exists.
          // flushSync bypasses React 18 auto-batching so the portal
          // renders in this same synchronous callback.
          flushSync(() => { infoBubbleUpdateRef.current?.(); });
        });
        overlayRef.current = customOverlay;
        customOverlay.setMap(mapItem);
      }

      // Store content in ref so the Overlay's onAdd callback can render it.
      // For first-open, openInfoWindowRef is still false here (the Google
      // Maps async pipeline hasn't called onAdd yet), so the actual FlushSync
      // render happens inside the Overlay constructor callback.
      infoBubbleContentRef.current = content;

      if (position) centerPointOnMap(position?.lat, position?.lng, currentZoom, -190);
    }
  }

  /******************************************************
   * Update the units Modal into the view
   ******************************************************/
  const updateModalPosition = (element) => {
    if (customOverlay && mapItem) {
      const googlePosition = new google.maps.LatLng(element.position.lat, element.position.lng)
      customOverlay.onMove(googlePosition);
    }
  }

  const updateModalPositionTrails = (element) => {
    if (customOverlay && mapItem && element?.id == customOverlay?.id) {
      const googlePosition = new google.maps.LatLng(element.position.lat, element.position.lng)
      customOverlay.onMove(googlePosition);
    }
  }

  /******************************************************
   * Add the label overflow objects
   ******************************************************/
  const addLabel = useCallback((mapObject) => {
    if (!mapItem)
      return
    if (!validateNewObject(mapObject)) {
      return
    }

    let googlePosition = null;

    googlePosition = new google.maps.LatLng(mapObject.position.lat, mapObject.position.lng) // eslint-disable-line no-undef

    const bubbleAnchor = document.createElement("div");
    bubbleAnchor.innerHTML = DOMPurify.sanitize(mapObject?.label || '');
    bubbleAnchor.setAttribute("id", `label_${mapObject.id}`);
    // Apply light theme to DOM label if darkLabels is OFF (default)
    if (USE_WEBGL_MARKERS && webglLayer && !webglLayer._labelSettings.darkLabels) {
      bubbleAnchor.classList.add('popup-bubble--light');
    }


    const customOverlayLabels = new MarkerOverlay(googlePosition, bubbleAnchor);

    customOverlayLabels.setMap(mapItem);
    return customOverlayLabels;
  }, [mapItem]);

  /******************************************************
   * Get the new position from the labels for Clusters
   ******************************************************/
  const createNewPositionsToLabelsCluster = (listUnitsInCluster = [], centerClusterPoss = null) => {

    if (!listUnitsInCluster || listUnitsInCluster && listUnitsInCluster.length <= 1 || !centerClusterPoss) return

    let startZoneLat = 0, startZoneLng = 0;
    let zoneLat = 0.000009, zoneLng = 0.00007, counterPos = 0;
    const zoomValue = mapItem.getZoom();

    let arrayLabelsLatLng = mapObjects.filter((item) =>
      item.type == TypeObjectMap.Label
      && item.layerName == TypeLayer.UnitLabel)
      .map((labelElement) => ({
        label: labelElement.label,
        labelGroup: labelElement.labelGroup,
        lat: labelElement.position.lat,
        lng: labelElement.position.lng,
        labelPositionSpace: labelElement.labelPositionSpace
      }));

    let percentZoomLat = 0, percentZoomLng = 0;
    if (zoomValue >= 3 && zoomValue < 22) {
      const { latZoom, lngZoom } = valuesToZoom(zoomValue)
      percentZoomLat = latZoom
      percentZoomLng = lngZoom

      zoneLat = zoneLat + percentZoomLat
      zoneLng = zoneLng + percentZoomLng
    }

    //get the first element at the list
    const mapObject = listUnitsInCluster[0];
    if (!mapObject) return

    let { newPositionLabel, labelPositionSpace } = getNewPositionLabel(arrayLabelsLatLng, centerClusterPoss, { position: centerClusterPoss }, counterPos, zoneLat, zoneLng, startZoneLat, startZoneLng, true);

    let label = '';

    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    //is goup by driver or unit
    const labelType = objectShowLabelSettings?.labelType;

    // Filter units based on labelType object { label?: boolean, driver?: boolean }
    const showUnit = Boolean(labelType?.label);
    const showDriver = Boolean(labelType?.driver);

    const getListOfLabels = listUnitsInCluster?.filter((x) => {
      const hasLabel = String(x?.label ?? '').trim().length > 0;
      const hasDriver = String(x?.labelDriver ?? '').trim().length > 0;

      if (showUnit && showDriver) return hasLabel || hasDriver;
      if (showUnit) return hasLabel;
      if (showDriver) return hasDriver;
      return false;
    }) || [];

    //get the ids into area
    const valueLabelGroup = Array.isArray(getListOfLabels) ? getListOfLabels.map((unit) => unit.id) : [];
    if(valueLabelGroup?.length > 0){
      mapObject.labelGroup = valueLabelGroup;
    }

    let newLabel = '';
    getListOfLabels.forEach((unit, index) => {      
      unit.labelPositionSpace = labelPositionSpace;

      if (index <= 4) {
        newLabel = getGroupLineLabel(labelType, unit);
        label = `${label}${newLabel} </br>`;
      }

      const showLabel = unit.id == mapObject.id;

      if (!showLabel) {
        removeMapObject(`temporalLabel_poly_${unit.id}`, TypeObjectMap.Polyline);
      }

      //show all elements            
      const objectOverlay = getObject(TypeObjectMap.Label, `label_${unit.id}`);
      if (objectOverlay) {
        objectOverlay.labelGroup = valueLabelGroup;
        objectOverlay.labelPosition = true;
        objectOverlay.labelPositionSpace = labelPositionSpace;
        objectOverlay.instance.onDisplayObject(showLabel, mapItem);
      }
      unit.labelPosition = true;
    });

    //if label contains data to group y label cluster
    if(label != ''){ 
      //create the cluster label
      const objectLabelCluster = new MapObject().buildLabel({
        id: `label_${mapObject.id}`,
        label: label,
        position: centerClusterPoss,
        layerName: TypeLayer.UnitLabel
      })
      //add the object into the map
      addMapObject(objectLabelCluster);

      //dray the polilyne to redirect a label
      addMapObject(new MapObject().buildPolyLine({
        id: `temporalLabel_poly_${mapObject.id}`,
        coordinates: [centerClusterPoss, newPositionLabel],
        layerName: TypeLayer.UnitLabel,
        heading: 0,
        color: '#B0B0B0'
      }), true, true);
    }

    //Add the message View Detail... into the label
    if (getListOfLabels?.length > 5) {
      updateMarkerLabel(mapObject, '', newPositionLabel, true, label);
    }

  }

  /******************************************************
   * Get the new position from the labels
   ******************************************************/
  const createNewPositionsToLabels = (restart = false) => {

    if (!mapObjects || (mapObjects && mapObjects.length <= 0)) return;

    if (mapItem.getZoom() > zoomLabelsPositions) {

      //Restart elements out the range
      let getLabelElementsToRemove = mapObjects.filter((item) => String(item?.id || "").includes('label_')
        && item.instance
        && item.labelPosition
        && item.position
        && !(item.position.lat >= lat1 && item.position.lat <= lat2
          && item.position.lng >= lng1 && item.position.lng <= lng2));

      //remove this label objects into the map
      getLabelElementsToRemove.forEach((element) => {
        //update defaul position label
        element.position = element.startPosition;
        element.labelPosition = false;
        element.labelGroup = null;
        element.labelPositionSpace = null;

        //Remove circle and lines to modals
        const idObjectMap = element.id.replace('label_', '')
        removeMapObject(`temporalLabel_poly_${idObjectMap}`, TypeObjectMap.Polyline);
        removeMapObject(`circleCluster_${idObjectMap}`, TypeObjectMap.Circle);

        //update default unit
        const objectmarker = getObject(TypeObjectMap.Marker, idObjectMap);
        objectmarker.labelPosition = false;
        objectmarker.labelGroup = null;
        objectmarker.labelPositionSpace = null;

        const googlePosition = new google.maps.LatLng(element.position.lat, element.position.lng)
        element.instance.onMove(googlePosition);
      })

      //vars for show UnitLabel or DriverLabel
      const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
      const labelType = objectShowLabelSettings?.labelType; 

      //get all vehicles into the range
      let getMapMarkers = mapObjects.filter((item) => item.type == TypeObjectMap.Marker
        && (item.instance && item.instance.getMap())
        && item.layerName == TypeLayer.Units
        && item.position
        && item.position.lat >= lat1 && item.position.lat <= lat2
        && item.position.lng >= lng1 && item.position.lng <= lng2
        //filter is the object have a label to show
        && (getGroupLineLabel(labelType, item)?.length > 0)
      );

      //update elements to no labels
      if (valueZoomNoOverlap != mapItem.getZoom() || restart) {
        //restart elements to reorganize by zoom
        getMapMarkers = getMapMarkers.map((mapObject) => {
          mapObject.labelPosition = false;
          mapObject.labelGroup = null;
          mapObject.labelPositionSpace = null;
          mapObject.labelGroupArea = null;
          mapObject.labelGroupIdMain = null;
          const objectOverlayObject = getObject(TypeObjectMap.Label, `label_${mapObject.id}`);
          if (objectOverlayObject) {
            objectOverlayObject.labelPosition = false;
            objectOverlayObject.labelGroup = null;
            objectOverlayObject.labelPositionSpace = null;
            objectOverlayObject.labelGroupArea = null;
            objectOverlayObject.labelGroupIdMain = null;
          }

          removeMapObject(`temporalLabel_poly_${mapObject.id}`, TypeObjectMap.Polyline);
          removeMapObject(`circleCluster_${mapObject.id}`, TypeObjectMap.Circle);

          return mapObject;
        })
      }
      valueZoomNoOverlap = mapItem.getZoom();

      getMapMarkers.forEach((mapObject) => {
        let label = ''
        const startPosition = { lat: mapObject.position.lat, lng: mapObject.position.lng };
        const objectOverlayObject = getObject(TypeObjectMap.Label, `label_${mapObject.id}`);
        //eval no update to labels
        if (!mapObject.labelGroup && (!objectOverlayObject || (objectOverlayObject && !objectOverlayObject.labelPosition))) {
          const {
            newPositionLabel,
            labelPositionSpace,
            groupElements,
            labelGroupArea,
            labelGroupIdMain
          } = evaluateLabelsPosition(mapObject, mapItem.getZoom());
          mapObject.labelPositionSpace = labelPositionSpace;
          mapObject.labelGroupArea = labelGroupArea;
          mapObject.labelGroupIdMain = labelGroupIdMain;
          if (objectOverlayObject) objectOverlayObject.labelPositionSpace = labelPositionSpace;

          let groupsArray = []
          if (groupElements.arrayElements) {
            groupsArray = groupElements.elements
            const valueLabelGroup = groupsArray.map((group) => group.id)
            mapObject.labelGroup = valueLabelGroup;

            groupsArray.forEach((unit, index) => {
              unit.labelGroup = valueLabelGroup;
              unit.labelPositionSpace = labelPositionSpace;
              unit.labelGroupArea = labelGroupArea;
              unit.labelGroupIdMain = labelGroupIdMain;

              const valueForLabel = getGroupLineLabel(labelType, unit)

              if (index <= 4 && valueForLabel?.length > 0) {
                label = `${label}${valueForLabel} </br>`;
              }

              //show all elements
              const showLabel = unit.id == mapObject.id
              const objectOverlay = getObject(TypeObjectMap.Label, `label_${unit.id}`);
              if (objectOverlay) {
                objectOverlay.labelGroup = valueLabelGroup;
                objectOverlay.labelPosition = true;
                objectOverlay.labelPositionSpace = labelPositionSpace;
                objectOverlay.labelGroupArea = labelGroupArea;
                objectOverlay.labelGroupIdMain = labelGroupIdMain;
                objectOverlay.instance.onDisplayObject(showLabel, mapItem);
              }

              if (!showLabel) {
                removeMapObject(`temporalLabel_poly_${unit.id}`, TypeObjectMap.Polyline);
              }
            });

            //update elements
            updateMarkerLabel(mapObject, '', newPositionLabel, true, label);
            
            //if object contains names draw the polyline
            addMapObject(new MapObject().buildPolyLine({
              id: `temporalLabel_poly_${mapObject.id}`,
              coordinates: [startPosition, newPositionLabel],
              layerName: TypeLayer.UnitLabel,
              heading: 0,
              color: '#B0B0B0'
            }), true, true);
          } else {
            //is only one item for label
            let getLabelMarker = getGroupLineLabel(labelType, mapObject);

            //is marker label have data to show on view
            if(getLabelMarker?.length > 0){
              const objectOverlay = getObject(TypeObjectMap.Label, `label_${mapObject.id}`);
              if (objectOverlay) {
                objectOverlay.instance.onDisplayObject(true, mapItem);
                objectOverlay.labelPosition = true;
                objectOverlay.labelPositionSpace = labelPositionSpace;
                objectOverlay.labelGroupArea = labelGroupArea;
                objectOverlay.labelGroupIdMain = labelGroupIdMain;
              }

              //update elements
              updateMarkerLabel(mapObject, '', newPositionLabel, true, getLabelMarker);
              //draw the polyline to the label
              addMapObject(new MapObject().buildPolyLine({
                id: `temporalLabel_poly_${mapObject.id}`,
                coordinates: [startPosition, newPositionLabel],
                layerName: TypeLayer.UnitLabel,
                heading: 0,
                color: '#B0B0B0'
              }), true, true);

            }

          }

          
        }

        if (objectOverlayObject) objectOverlayObject.labelPosition = true;
      });

    } else {
      //return all to normal position
      const getMapLabels = mapObjects.filter((element) => String(element?.id || "").includes('label_') && element.labelPosition && element.instance);
      getMapLabels.forEach((element) => {
        //update defaul position label
        element.position = element.startPosition
        element.labelPosition = false
        element.labelGroup = null
        element.labelGroupArea = null
        element.labelGroupIdMain = null
        element.labelPositionSpace = null;

        const googlePosition = new google.maps.LatLng(element.position.lat, element.position.lng)
        element.instance.onMove(googlePosition);
      })
    }

  }

  /******************************************************
   * Values to zoom to reorder the labels into view
   ******************************************************/
  const valuesToZoom = (zoomValue) => {
    let multiplier = 1;
    switch (zoomValue) {
      case 21: multiplier = 1; break;
      case 20: multiplier = 3; break;
      case 19: multiplier = 7; break;
      case 18: multiplier = 14; break;
      case 17: multiplier = 30; break;
      case 16: multiplier = 62; break;
      case 15: multiplier = 124; break;
      case 14: multiplier = 248; break;
      case 13: multiplier = 496; break;
      case 12: multiplier = 992; break;
      case 11: multiplier = 1984; break;
      case 10: multiplier = 3968; break;
      case 9: multiplier = 7936; break;
      case 8: multiplier = 15872; break;
      case 7: multiplier = 31744; break;
      case 6: multiplier = 63488; break;
      case 5: multiplier = 126976; break;
      case 4: multiplier = 253952; break;
      case 3: multiplier = 507904; break;
    }
    return { latZoom: (0.000009 * multiplier), lngZoom: (0.00007 * multiplier) }
  }

  /******************************************************
  * Logic to get new position from labels
  ******************************************************/
  const evaluateLabelsPosition = (mapObject, zoomValue, isRealtime = false) => {

    const radiusGroup = 4;

    let startZoneLat = 0, startZoneLng = 0;
    let zoneLat = 0.000009, zoneLng = 0.00007, counterPos = 0;

    let arrayLabelsLatLng = mapObjects.filter((item) =>
      item.type == TypeObjectMap.Label
      && item.layerName == TypeLayer.UnitLabel
      /* && !item.labelGroup */
      && (item.position
        && item.position.lat >= lat1 && item.position.lat <= lat2
        && item.position.lng >= lng1 && item.position.lng <= lng2))
      .map((labelElement) => ({
        id: labelElement.id,
        label: labelElement.label,
        labelGroup: labelElement.labelGroup,
        lat: labelElement.position.lat,
        lng: labelElement.position.lng,
        labelPositionSpace: labelElement.labelPositionSpace
      }));

    if (arrayLabelsLatLng && arrayLabelsLatLng.length <= 1) {
      const newPositionLabel = getPositionObject(mapObject.position, 2, zoneLat, zoneLng, startZoneLat, startZoneLng);

      let zoneLatFactorLong = zoneLat;

      let pointLatFirst = newPositionLabel.lat - (zoneLatFactorLong / 2);
      let pointLatSecond = newPositionLabel.lat + (zoneLat / 2);
      let pointLngFirst = newPositionLabel.lng;
      let pointLngSecond = newPositionLabel.lng + zoneLng;

      let labelPositionSpace = { pointLatFirst, pointLatSecond, pointLngFirst, pointLngSecond };

      return {
        newPositionLabel,
        labelPositionSpace,
        groupElements: { arrayElements: false, elements: [mapObject] }
      };
    }

    //zoom 22 - 3
    let percentZoomLat = 0, percentZoomLng = 0;
    if (zoomValue >= 3 && zoomValue < 22) {
      const { latZoom, lngZoom } = valuesToZoom(zoomValue)
      percentZoomLat = latZoom
      percentZoomLng = lngZoom

      zoneLat = zoneLat + percentZoomLat
      zoneLng = zoneLng + percentZoomLng
    }
    //create landmark group
    let labelGroupArea = {
      pointLatFirst: mapObject.position.lat - (zoneLat * radiusGroup),
      pointLatSecond: mapObject.position.lat + (zoneLat * radiusGroup),
      pointLngFirst: mapObject.position.lng - (zoneLat * radiusGroup),
      pointLngSecond: mapObject.position.lng + (zoneLat * radiusGroup)
    }
    if (isRealtime && mapObject.labelGroupArea) {
      labelGroupArea = mapObject.labelGroupArea
    }

    //vars for show UnitLabel or DriverLabel
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    const labelType = objectShowLabelSettings?.labelType;

    let arrayLabelsStack = mapObjects.filter((item) =>
      item.type == TypeObjectMap.Marker
      && (item.instance && item.instance.getMap())
      && item.layerName == TypeLayer.Units
      && (item.position
        && item.position.lat >= lat1 && item.position.lat <= lat2
        && item.position.lng >= lng1 && item.position.lng <= lng2));

    //get the units into the cuadrant
    arrayLabelsStack = arrayLabelsStack.filter((item) =>
      item.position
      && !item.labelGroup
      && ((item.position.lat >= labelGroupArea.pointLatFirst)
        && (item.position.lat <= labelGroupArea.pointLatSecond)
        && (item.position.lng >= labelGroupArea.pointLngFirst)
        && (item.position.lng <= labelGroupArea.pointLngSecond))
        && (getGroupLineLabel(labelType, item)?.length > 0)
      )

    const isGroup = arrayLabelsStack && arrayLabelsStack.length > 5;

    // ************************************************************************
    //add units to arrayObjects.
    arrayLabelsLatLng = [...arrayLabelsLatLng, ...mapObjects.filter((item) =>
      item.type == TypeObjectMap.Marker
      && item.layerName == TypeLayer.Units
      /* && !item.labelGroup */
      && (item.position
        && item.position.lat >= lat1 && item.position.lat <= lat2
        && item.position.lng >= lng1 && item.position.lng <= lng2))
      .map((labelElement) => ({
        id: labelElement.id,
        label: labelElement.label,
        labelGroup: null,
        lat: labelElement.position.lat,
        lng: labelElement.position.lng,
        labelPositionSpace: {
          pointLatFirst: labelElement.position.lat - (zoneLat / 2),
          pointLatSecond: labelElement.position.lat + (zoneLat / 2),
          pointLngFirst: labelElement.position.lng - (zoneLat / 2),
          pointLngSecond: labelElement.position.lng + (zoneLat / 2)
        }
      }
      ))];

    let { newPositionLabel, labelPositionSpace } = getNewPositionLabel(arrayLabelsLatLng, mapObject.position, mapObject, counterPos, zoneLat, zoneLng, startZoneLat, startZoneLng, isGroup)

    if (isGroup && !isRealtime) {
      //create the circle area for labels clusters
      const { arrayUnits } = labelDrawGroupAreaOnMap(mapObject, arrayLabelsStack, zoneLat)

      //return arrayElements
      return {
        newPositionLabel,
        labelPositionSpace: labelPositionSpace,
        groupElements: { arrayElements: true, elements: arrayUnits },
        clusterPosition: { lat: mapObject.position.lat - (zoneLat * radiusGroup), lng: mapObject.position.lng + (zoneLat * radiusGroup) },
        labelGroupArea: labelGroupArea,
        labelGroupIdMain: mapObject.labelGroupIdMain || mapObject.id
      };
    }

    return {
      newPositionLabel,
      labelPositionSpace: labelPositionSpace,
      groupElements: { arrayElements: false, elements: [mapObject] },
      labelGroupArea: null,
      labelGroupIdMain: null
    };
  }

  /******************************************************
   * Draw the circles into the map, validate the area and draw the elements.
   * @param {*} mapObject 
   * @param {*} arrayLabelsStack 
   * @param {*} zoneLat 
   * @returns 
   ******************************************************/
  const labelDrawGroupAreaOnMap = (mapObjectMain, arrayLabelsStack, zoneLat = null) => {

    if (zoneLat == null) {
      const zoomValue = mapItem.getZoom();
      zoneLat = 0.000009;
      if (zoomValue >= 3 && zoomValue < 22) {
        const { latZoom } = valuesToZoom(zoomValue)
        zoneLat = latZoom
      }
    }

    const arrayUnits = []
    //get units elements    
    let distanceOnMetters = calcDistanceToPoints(mapObjectMain.position.lat, mapObjectMain.position.lng, mapObjectMain.position.lat + zoneLat, mapObjectMain.position.lng + zoneLat);
    //convert this part in metters
    let convertToKilometers = distanceOnMetters * 1000;
    let maxValue = {
      position: { lat: mapObjectMain.position.lat + zoneLat, lng: mapObjectMain.position.lng + zoneLat },
      radius: convertToKilometers
    }

    arrayLabelsStack.forEach((item) => {
      distanceOnMetters = calcDistanceToPoints(mapObjectMain.position.lat, mapObjectMain.position.lng, item.position.lat, item.position.lng);
      convertToKilometers = distanceOnMetters * 1000;
      if (maxValue.radius < convertToKilometers) {
        maxValue = {
          position: { lat: item.position.lat, lng: item.position.lng },
          radius: convertToKilometers
        }
      }
      arrayUnits.push(getObject(TypeObjectMap.Marker, item.id.toString().replace('label_', '')));
    })

    //Create a temporal circle with to show group
    const tLat1 = mapObjectMain.position.lat;
    const tLng1 = mapObjectMain.position.lng;
    const tLat2 = maxValue.position.lat;
    const tLng2 = maxValue.position.lng;

    const degress = Math.acos(Math.sin(tLat1 * Math.PI / 180) * Math.sin(tLat2 * Math.PI / 180) + Math.cos(tLat1 * Math.PI / 180) * Math.cos(tLat2 * Math.PI / 180) * Math.cos(tLng2 * Math.PI / 180 - tLng1 * Math.PI / 180)) * 6371000;

    //remove old object
    removeMapObject(`circleCluster_${mapObjectMain.labelGroupIdMain || ''}`, TypeObjectMap.Circle);
    removeMapObject(`circleCluster_${mapObjectMain.id}`, TypeObjectMap.Circle);

    addMapObject(new MapObject().buildCircle({
      id: `circleCluster_${mapObjectMain.labelGroupIdMain || mapObjectMain.id}`, //Ref to move elements in marker event
      position: mapObjectMain.position,
      layerName: TypeLayer.MarkerCluster,
      label: '',
      heading: 0,
      color: mapObjectMain.color || '#0098dc',
      eventName: 'no event',
      radius: /* maxValue.radius, */ degress,
      draggable: false,
      fillOpacity: 0.01
    }));

    return { arrayUnits: arrayUnits }
  }

  /******************************************************
  * Recursive function to get new label name in view
  ******************************************************/
  const getNewPositionLabel = (arrayLabelsLatLng, newPosition, mapObject, counterPos = 0, zoneLat = 0.000009, zoneLng = 0.00007, startZoneLat = 0, startZoneLng = 0, isGroup = false) => {

    let responsePosition = newPosition
    if (counterPos == 0 && startZoneLat == 0 && startZoneLng == 0) {
      responsePosition = getPositionObject(mapObject.position, counterPos, zoneLat, zoneLng, startZoneLat, startZoneLng)
      counterPos = counterPos + 1;
    }

    let zoneLatFactorLong = isGroup ? (zoneLat * 5) : zoneLat;
    let zoneLngFactorLong = isGroup ? (zoneLng / 2) : 0;

    let pointLatFirst = responsePosition.lat - (zoneLatFactorLong / 2);
    let pointLatSecond = responsePosition.lat + (zoneLat / 2);
    let pointLngFirst = responsePosition.lng - zoneLngFactorLong;
    let pointLngSecond = responsePosition.lng + zoneLng;

    let labelPositionSpace = {
      pointLatFirst: pointLatFirst,
      pointLatSecond: pointLatSecond,
      pointLngFirst: pointLngFirst,
      pointLngSecond: pointLngSecond
    };

    if (arrayLabelsLatLng && arrayLabelsLatLng.length > 0) {
      //realizar los calculos sobre el boundary
      const itemFind = arrayLabelsLatLng.find((item) =>
        item.labelPositionSpace
        &&
        ((item.labelPositionSpace.pointLatFirst <= responsePosition.lat &&
          item.labelPositionSpace.pointLatSecond > responsePosition.lat &&
          item.labelPositionSpace.pointLngFirst <= responsePosition.lng &&
          item.labelPositionSpace.pointLngSecond > responsePosition.lng)
          ||
          (item.labelPositionSpace.pointLatFirst <= pointLatFirst &&
            item.labelPositionSpace.pointLatSecond > pointLatFirst &&
            item.labelPositionSpace.pointLngFirst <= pointLngFirst &&
            item.labelPositionSpace.pointLngSecond > pointLngFirst)
          ||
          (item.labelPositionSpace.pointLatFirst <= pointLatFirst &&
            item.labelPositionSpace.pointLatSecond > pointLatFirst &&
            item.labelPositionSpace.pointLngFirst <= pointLngSecond &&
            item.labelPositionSpace.pointLngSecond > pointLngSecond)
          ||
          (item.labelPositionSpace.pointLatFirst <= pointLatSecond &&
            item.labelPositionSpace.pointLatSecond > pointLatSecond &&
            item.labelPositionSpace.pointLngFirst <= pointLngFirst &&
            item.labelPositionSpace.pointLngSecond > pointLngFirst)
          ||
          (item.labelPositionSpace.pointLatFirst <= pointLatSecond &&
            item.labelPositionSpace.pointLatSecond > pointLatSecond &&
            item.labelPositionSpace.pointLngFirst <= pointLngSecond &&
            item.labelPositionSpace.pointLngSecond > pointLngSecond))
      );

      if (itemFind) {


        const newPositionTemp = getPositionObject(mapObject.position, counterPos, zoneLat, zoneLng, startZoneLat, startZoneLng)
        counterPos = counterPos + 1;

        if (counterPos == 14) {
          counterPos = 0;
          startZoneLat = startZoneLat + zoneLat;
          startZoneLng = startZoneLng + zoneLng;
        }

        const { newPositionLabel: paramResponsePosition, labelPositionSpace: paramLabelPositionSpace } = getNewPositionLabel(arrayLabelsLatLng, newPositionTemp, mapObject, counterPos, zoneLat, zoneLng, startZoneLat, startZoneLng, isGroup);

        responsePosition = paramResponsePosition
        labelPositionSpace = paramLabelPositionSpace
        return { newPositionLabel: responsePosition, labelPositionSpace };
      }
    }
    return { newPositionLabel: responsePosition, labelPositionSpace };
  }


  /******************************************************
   * Statics positions in map
   ******************************************************/
  const getPositionObject = (position = { lat: 0, lng: 0 }, numberPosition = 0, zoneLat, zoneLng, startZoneLat, startZoneLng) => {
    let newPosition = null
    switch (numberPosition) {
      case 0:
        newPosition = { lat: startZoneLat + position.lat, lng: startZoneLng + position.lng + (zoneLng / 6) };
        break;

      case 1:
        newPosition = { lat: startZoneLat + position.lat + zoneLat, lng: startZoneLng + position.lng + (zoneLng / 6) };
        break;

      case 2:
        newPosition = { lat: startZoneLat + position.lat + (zoneLat * 2), lng: startZoneLng + position.lng + (zoneLng / 7) };
        break;

      case 3:
        newPosition = { lat: startZoneLat + position.lat - zoneLat, lng: startZoneLng + position.lng + (zoneLng / 6) };
        break;

      case 4:
        newPosition = { lat: startZoneLat + position.lat - (zoneLat * 2), lng: startZoneLng + position.lng + (zoneLng / 7) };
        break;

      case 5:
        newPosition = { lat: startZoneLat + position.lat - (zoneLat * 3), lng: startZoneLng + position.lng };
        break;

      case 6:
        newPosition = { lat: startZoneLat + position.lat - (zoneLat * 4), lng: startZoneLng + position.lng - (zoneLng / 8) };
        break;

      case 7:
        newPosition = { lat: startZoneLat + position.lat - (zoneLat * 5), lng: startZoneLng + position.lng - (zoneLng / 4) };
        break;

      case 8:
        newPosition = { lat: - startZoneLat + position.lat - (zoneLat * 2), lng: - startZoneLng + position.lng - zoneLng };
        break;

      case 9:
        newPosition = { lat: - startZoneLat + position.lat - zoneLat, lng: - startZoneLng + position.lng - zoneLng };
        break;

      case 10:
        newPosition = { lat: - startZoneLat + position.lat, lng: - startZoneLng + position.lng - zoneLng };
        break;

      case 11:
        newPosition = { lat: - startZoneLat + position.lat + zoneLat, lng: - startZoneLng + position.lng - zoneLng };
        break;

      case 12:
        newPosition = { lat: - startZoneLat + position.lat + (zoneLat * 2), lng: - startZoneLng + position.lng - zoneLng };
        break;

      case 13:
        newPosition = { lat: - startZoneLat + position.lat + (zoneLat * 3), lng: - startZoneLng + position.lng - zoneLng + (zoneLng / 8) };
        break;

    }
    return newPosition;
  }

  /******************************************************
  * Add marker from landmarks on map
  ******************************************************/
  const addImageMapType = (mapObject, validateDobleObject = true) => {

    if (!mapItem)
      return
    if (validateDobleObject && !validateNewObject(mapObject)) {
      return
    }

    let iconUrl = ''

    if (mapObject.url) {
      iconUrl = `${pathFile}/${mapObject.url}`
      //is flag icon, center the flag in point on map
      if(mapObject?.url?.includes('/flagicon/')){
        iconUrl = {
          url: iconUrl,
          //x-y
          origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
          anchor: new google.maps.Point(6, 24)
        }
      }else{
        //is normal icon, point to center on map
        iconUrl = {
          url: iconUrl,
          //x-y
          origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
          anchor: new google.maps.Point(12, 20)
        }
      }

    } else {
    //define the icon to default flag icon
      const colorFlag = findColor(mapObject.color.replace("#", ""));
      iconUrl = `${pathFile}/kmls/flagicon/${colorFlag}.png`
      iconUrl = {
        url: iconUrl,
        //x-y
        origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
        anchor: new google.maps.Point(6, 24)
      }
    }

    const position = new google.maps.LatLng(mapObject.position.lat, mapObject.position.lng);
    const marker = new MarkerCompat({
      id: mapObject.id,
      position: position,
      map: mapItem,
      icon: iconUrl,
    });

    marker.addListener('click', () => {
      // Defer to let deck.gl's onClick fire first and set _unitClickedAt.
      // If a unit marker was clicked within the last 300ms, skip the landmark popup.
      setTimeout(() => {
        if (webglLayer && (Date.now() - (webglLayer._unitClickedAt || 0)) < 300) return;
        // Skip if a modal is already open — prevents click-through from the overlay
        if (realStateInfoWindow) return;
        const obj = {
          id: mapObject.id.toString().replace("landmark_", ""),
          position: position,
          icon: {
            rotation: 0
          },
          layerName: TypeLayer.Landmark
        }
        props.modalPopupEvent(obj, obj);
      }, 50);
    })
    return marker;
  }

  /******************************************************
  * Add Circle on landmarks marker
  ******************************************************/
  const addChildLandmarkCircle = (mapObject, validateDobleObject = true) => {
    if (!mapItem)
      return
    if (validateDobleObject && !validateNewObject(mapObject)) {
      return
    }
    const mapObjectChild = mapObject
    const position = new google.maps.LatLng(mapObject.position.lat, mapObject.position.lng);
    mapObjectChild.position = position

    const circle = addCircle(mapObjectChild, true)

    //create envelope for circle
    const { envelop, center, radius} = createCircleEnvelop(circle)
    mapObjectChild.envelop = envelop;
    mapObjectChild.objectCenter = center;
    mapObjectChild.radiusByObject = radius;

    circle.addListener('click', () => {
      // Defer to let deck.gl's onClick fire first and set _unitClickedAt.
      setTimeout(() => {
        if (webglLayer && (Date.now() - (webglLayer._unitClickedAt || 0)) < 300) return;
        // Suppress click-through re-open when the modal was just closed
        if ((Date.now() - _lastModalCloseAt) < 300) return;
        // Skip if a modal is already open — prevents click-through from the overlay
        if (realStateInfoWindow) return;
        const obj = {
          id: mapObject.id.toString().replace("landmark_", "").replace("geofence_", ""),
          position: position,
          icon: {
            rotation: 0
          },
          layerName: mapObject.type == TypeObjectMap.Landmark ? TypeLayer.Landmark : TypeLayer.Geofence
        }
        props.modalPopupEvent(obj, obj);
      }, 50);
    })

    return circle
  }

  /******************************************************
  * Add Polygon on landmarks marker
  ******************************************************/
  const addChildLandmarkPolygon = (mapObject, validateDobleObject = true) => {
    if (!mapItem)
      return
    if (validateDobleObject && !validateNewObject(mapObject)) {
      return
    }
    const mapObjectChild = mapObject
    const position = new google.maps.LatLng(mapObject.position.lat, mapObject.position.lng);
    mapObjectChild.position = position

    const polygon = addPolygon(mapObjectChild)

    //create envelope for polygon
    const { envelop } = createPolygonEnvelop(polygon)
    mapObjectChild.envelop = envelop;

    polygon.addListener('click', () => {
      // Defer to let deck.gl's onClick fire first and set _unitClickedAt.
      setTimeout(() => {
        if (webglLayer && (Date.now() - (webglLayer._unitClickedAt || 0)) < 300) return;
        // Suppress click-through re-open when the modal was just closed
        if ((Date.now() - _lastModalCloseAt) < 300) return;
        // Skip if a modal is already open — prevents click-through from the overlay
        if (realStateInfoWindow) return;
        const obj = {
          id: mapObject.id.toString().replace("landmark_", "").replace("geofence_", ""),
          position: position,
          icon: {
            rotation: 0
          },
          layerName: mapObject.type == TypeObjectMap.Landmark ? TypeLayer.Landmark : TypeLayer.Geofence
        }
        props.modalPopupEvent(obj, obj);
      }, 50);
    })

    return polygon
  }

  /******************************************************
  * Add the settings values into mapObjects
  * Used by:
  * 1. Polygon on landmarks marker
  * 2. Prevent Overlap Labels
  ******************************************************/
  const addMapObjectSettings = (active, markerZoom = 0, radiusZoom = 0, id = 0, event = null, otherSettings = null, unitLabelSelect = {}) => {

    let objectSettings = mapObjects.find((item) => item.id == id);
    let changeState = true
    if (objectSettings) {
      changeState = objectSettings.enable != active
      objectSettings.enable = active;
      objectSettings.value = markerZoom;
      objectSettings.valueChild = radiusZoom;
      objectSettings.labelType = unitLabelSelect && Object.keys(unitLabelSelect).length > 0 ? unitLabelSelect : { label: false, driver: false };
      if (otherSettings) {
        for (let item in otherSettings) {
          objectSettings[item] = otherSettings[item]
        }
      }
      if (markerZoom > 0)
        showLandmarksGeofenceByZoom(id, mapItem.getZoom());
    } else {
      objectSettings = new MapObject().buildSettings({
        id: id,
        enable: active,
        value: markerZoom,
        valueChild: radiusZoom,
        showLabel: false,
        onCallbackEvent: event,
        labelType: unitLabelSelect && Object.keys(unitLabelSelect).length > 0 ? unitLabelSelect : { label: false, driver: false }
      });

      if (otherSettings) {
        for (let item in otherSettings) {
          objectSettings[item] = otherSettings[item]
        }
      }

      mapObjects.push(objectSettings);
      _indexAdd(objectSettings);
    }

    //create instance for boundsChangedEvents
    if (id == 'noOverlapSettings' && changeState) {
      if (active) {
        startingOverlapSettings()
      }
      else {
        // WebGL: ensure the pure-WebGL no-overlap mode is disabled immediately
        if (USE_WEBGL_MARKERS && webglLayer) {
          webglLayer.setNoOverlapLabels({ enabled: false, minZoom: zoomLabelsPositions });
        }
        restarLabelsToDefault()
      }
    }
  }

  /******************************************************
  * Vars to starting overlap in maps
  ******************************************************/
  const startingOverlapSettings = () => {
    addLabelMarkers(TypeLayer.UnitLabel, true, false)
    restarLabelsToDefault()
    // WebGL mode: no DOM label repositioning; WebGLMarkerLayer handles layout.
    if (!USE_WEBGL_MARKERS || !webglLayer) {
      createNewPositionsToLabels(true)
    }
  }

  /******************************************************
  * Show or hide landmarks by zoom objects
  ******************************************************/
  const showLandmarksGeofenceByZoom = (id, zoomMap) => {
    const settingsObject = mapObjects.find((item) => item.id == id);

    if (!settingsObject) return

    let layer = TypeLayer.LandmarkMarker;
    if (settingsObject.id == "geofenceSettings") {
      layer = TypeLayer.Geofence
    }

    if (!settingsObject?.enable) {
      showLayer(layer, false);
      return
    }

    //show layer information
    let showMain = (zoomMap >= settingsObject.value)
    let showChild = (zoomMap >= settingsObject.valueChild)

    if ((showMain || showChild) && !settingsObject.showLabel && settingsObject.onCallbackEvent) {
      settingsObject.showLabel = true
      //show first label landmark //dispatch(loadObjectsLandmarksOnMap(true)) || dispatch(loadObjectsGeofenceOnMap(true))
      dispatch(settingsObject.onCallbackEvent(true))
    }

    showLayerMainOrChild(layer, showMain, true)
    showLayerMainOrChild(layer, showChild, false)
  }

  /******************************************************
   * Add the label name from markers
   ******************************************************/
  const addLabelMarkers = useCallback((layer = TypeLayer.UnitLabel, show = true, evalOverlap = true) => {

    // ── WebGL mode: delegate labels to deck.gl TextLayer ──
    if (USE_WEBGL_MARKERS && webglLayer && layer === TypeLayer.UnitLabel) {
      const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
      const labelType = objectShowLabelSettings?.labelType;

      // Labels should be available immediately on initial load.
      // In practice, `unitLabelSettings` and/or `labelType` may not be
      // populated yet, which previously resulted in `enabled=false` until
      // the user interacted with the map.
      //
      // Rule:
      // - Only disable labels if unitLabelSettings.enable === false
      // - Otherwise, default to showing the unit label.
      const labelsAllowed = show && objectShowLabelSettings?.enable !== false;
      let showUnit = labelType?.label;
      let showDriver = labelType?.driver;
      const darkLabels = Boolean(labelType?.darkLabels);
      if (showUnit == null) showUnit = true;
      if (showDriver == null) showDriver = false;
      if (!showUnit && !showDriver) showUnit = true;

      const enabled = Boolean(labelsAllowed);
      const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
      const noOverlapActive = objectNoOverlapSettings?.enable ?? false;
      const zoomOk = (mapItem?.getZoom?.() ?? 0) > zoomLabelsPositions;

      // Batch both state changes so only ONE _renderLayers fires.
      // Previously setLabelSettings + setNoOverlapLabels each triggered
      // a full render with collision detection — doubling CPU cost.
      webglLayer.beginUpdate();

      webglLayer.setViewDetailText(messages['map_view_detail'] || 'View Detail...');
      webglLayer.setLabelSettings({
        enabled,
        showUnit: Boolean(showUnit),
        showDriver: Boolean(showDriver),
        darkLabels,
      });

      // Pure WebGL no-overlap labels only when: setting ON + clusters OFF + zoom > threshold
      webglLayer.setNoOverlapLabels({
        enabled: enabled && noOverlapActive && zoomOk && !webglLayer.isClusteringEnabled(),
        minZoom: zoomLabelsPositions,
      });

      webglLayer.endUpdate();

      // Group-label click → open right panel list
      webglLayer.setOnLabelGroupClick(onCallbackClickLabel);
      return;
    }

    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (evalOverlap && layer == TypeLayer.UnitLabel && mapItem.getZoom() > zoomLabelsPositions && objectNoOverlapSettings && objectNoOverlapSettings?.enable && getCurrentZoom() < maxClusterZoom) {
      return
    }

    // Validate overlap settings and zoom level
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    if (mapItem.getZoom() > zoomLabelsPositions && objectShowLabelSettings && objectShowLabelSettings?.enable) {
      show = true
    }

    // Hide the layer if `show` is false
    if (!show) {
      showLayer(layer, show)
      return
    }

    // Remove existing labels for the layer
    if (layer == TypeLayer.UnitLabel) {
      removeLayer(layer)
    }

    let mapMarkers = null;

    //add the validation (layer == TypeLayer.VehicleTrails && show) for show labels in vehicle trails
    const labelType = objectShowLabelSettings?.labelType;
    const hasAnyLabelActive = Boolean(labelType?.label || labelType?.driver);
    
    if (mapObjects?.length > 0 && (hasAnyLabelActive || (layer == TypeLayer.VehicleTrails && show))) {
      
      // Filter visible markers based on the layer type
      mapMarkers = mapObjects.filter((item) => item.type == TypeObjectMap.Marker && (item.instance && item.instance.getMap()))
      if (layer == TypeLayer.UnitLabel) {
        mapMarkers = mapMarkers?.filter((item) => item.layerName == TypeLayer.Units);
        addMapClusterListener() //add listener to cluster object
      } else {
        mapMarkers = mapMarkers?.filter((item) => item.layerName == layer);
      }
      
      // Create labels for visible markers
      if (mapMarkers?.length > 0) {
        createLabelForUnits(mapMarkers, layer, labelType);
      }

      // Filter hidden markers based on the layer type
      mapMarkers = mapObjects.filter((item) => item.type == TypeObjectMap.Marker && (item?.instance && !item?.instance?.getMap()))
      if (layer == TypeLayer.UnitLabel) {
        mapMarkers = mapMarkers.filter((item) => item.layerName == TypeLayer.Units);
      } else {
        mapMarkers = mapMarkers.filter((item) => item.layerName == layer);
      }

      // Create labels for hidden markers
      if (mapMarkers?.length > 0) {
        createLabelForUnits(mapMarkers, layer, labelType);

        //remove the labels when the unit is hidden on map
        mapItem && hiddenMarkersOnMap(mapItem?.getBounds());
      }

    }
  },[mapItem])

  const createLabelForUnits = useCallback((mapMarkers, layer, labelType) => {
    mapMarkers?.map((item) => {
      const stringLabelOnUnit = getGroupLineLabel(labelType, item);
        if (item.instance != null && !item?.hideMarker && stringLabelOnUnit.length > 0) {
          const objectLabel = new MapObject().buildLabel({
            id: `label_${item.id}`,
            label: stringLabelOnUnit ,
            position: item.position,
            startPosition: item.position,
            layerName: layer
          })
  
          //add the object into the map
          addMapObject(objectLabel);
        }else if(layer == TypeLayer.VehicleTrails && item?.hideMarker){
          removeMapObject(`label_${item.id}`, TypeObjectMap.Label)
        }
    });
  
  },[mapItem]);


  /******************************************************
   * Show Labels when cluster is active on map
   ******************************************************/
  const showOrHideLabelsOnCluster = useCallback((cluster = null) => {
    // In WebGL mode, cluster labels are handled via MarkerOverlay overlays
    if (USE_WEBGL_MARKERS && webglLayer) {
      updateWebGLClusterLabels();
      return;
    }

    let objectCluster = cluster
    if (!cluster) {
      objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
      if (objectCluster) {
        objectCluster = objectCluster.instance
      }
    }

    if (!objectCluster) return

    const clusters = objectCluster?.clusters || [];

    const getLabelLines = mapObjects.filter((element) => String(element?.id || "").includes('temporalLabel_poly_') == true);
    getLabelLines.forEach((item) => {
      removeMapObject(item.id, item.type);
    })

    //Get the activate label clusters
    const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
    const labelType = objectShowLabelSettings?.labelType;

    for (let i = 0; i < clusters.length; i++) {
      //let markersOnCluster = objectCluster.getMarkers(); 
      const markersOnCluster = clusters[i]?.markers || [];

      for (var j = 0; j < markersOnCluster.length; j++) {
        const item = markersOnCluster[j];
        removeMapObject(`label_${item.id}`, TypeObjectMap.Label);
        if (markersOnCluster.length <= 1) {
          const mapElement = getObject(TypeObjectMap.Marker, item.id)
          // { label: false, driver: false }
          const labelFromUnit = mapElement ? getGroupLineLabel(labelType, mapElement) : '';

          
          if(labelFromUnit?.length == 0) continue;
          
          const objectLabel = new MapObject().buildLabel({
            id: `label_${item.id}`,
            label: labelFromUnit,
            position: {
              lat: typeof item.position.lat === 'function' ? item.position.lat() : item.position.lat,
              lng: typeof item.position.lng === 'function' ? item.position.lng() : item.position.lng,
            },
            layerName: TypeLayer.UnitLabel
          })
          //add the object into the map
          addMapObject(objectLabel);
        }
      }

    }

    const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
    if (!objectNoOverlapSettings || (objectNoOverlapSettings && !objectNoOverlapSettings?.enable)) return

    const labelMarkerCluster = clusters.filter((clusterElement) => clusterElement?.markers?.length > 0)

    const zoomValue = getCurrentZoom();

    labelMarkerCluster.forEach((elementCluster) => {
      const unitsOnCluster = [];
      elementCluster?.markers?.forEach((clusterMarker) => {
        unitsOnCluster.push(getObject(TypeObjectMap.Marker, clusterMarker.id));
      });
      if (unitsOnCluster.length > 1) {
        let zoneLat = 0;
        if (zoomValue >= 3 && zoomValue < 22) {
          const { latZoom } = valuesToZoom(zoomValue)
          const clusterLat = typeof elementCluster.position.lat === 'function' ? elementCluster.position.lat() : elementCluster.position.lat;
          zoneLat = clusterLat + latZoom
        }
        const clusterLng = typeof elementCluster.position.lng === 'function' ? elementCluster.position.lng() : elementCluster.position.lng;
        const centerClusterPoss = { lat: zoneLat, lng: clusterLng };
        createNewPositionsToLabelsCluster(unitsOnCluster, centerClusterPoss)
      }
    })

  })

  /******************************************************
   * Add the label overflow objects
   ******************************************************/
  const removeLabelOverlay = useCallback((layerName) => {

    for (let i = (mapObjects.length - 1); i >= 0; i--) {
      let mapObject = mapObjects[i];
      if (mapObject.layerName == layerName) {
        try {
          mapObject.instance.setMap(null);
        }
        catch (err) {
          console.log('error ***', err.toString())
        }

      }
    }
  })

  /******************************************************
   * Hide the modal popup at the marker into the realtime maps
   ******************************************************/
  const hideMarkerModal = useCallback(() => {
    openInfoWindowRef.current = false;
    currentMarkerModalRef.current = null;
    infoBubbleContentRef.current = null;
    _lastModalCloseAt = Date.now();
    // Hide the overlay container instead of destroying it (setMap(null)).
    // Keeps the Google Maps OverlayView pane attachment intact so the next
    // showMarkerModal can reuse it synchronously — no async onAdd/draw cycle.
    if (customOverlay?.container_) {
      customOverlay.container_.style.display = 'none';
    }
    // Trigger InfoWindow re-render — reads openRef=false → renders empty fragment.
    // flushSync ensures the portal unmounts synchronously so the container
    // is empty before display:none hides it.
    flushSync(() => { infoBubbleUpdateRef.current?.(); });
    mapItem && mapItem.setOptions({ draggable: true });

    if (window.openInfoBubble) {
      clearTimeout(window.openInfoBubble)
    }

    window.openInfoBubble = setTimeout(() => {
      realStateInfoWindow = false;
      window.openInfoBubble = null;
    }, 500);
  })

  /******************************************************
   * Get the info modal show or hide on map
   ******************************************************/
  const getOpenInfoWindow = useCallback(() => {
    return realStateInfoWindow
  })

  /******************************************************
   * Validate if the marker exist into the array elements.
   * @param {JSON} mapObject 
   ******************************************************/
  const existsObject = (mapObject) => {
    return mapObjects.find((item) => item.type == mapObject.type && item.id == mapObject.id && !mapObject.instance);
  }

  /******************************************************
   * Add the Polyline objecto into google maps to google.maps.Polyline
   * @param {JSON} mapObject 
   ******************************************************/
  const addPolyline = (mapObject, evalInstance = false) => {
    if (evalInstance && existsObject(mapObject)) {
      return
    }

    if(!mapObject) return
    
    let dashedLine = null;

    if(!mapObject?.hideDrawDirection){
      //to create line into route
      dashedLine = {
        //path: 'M 0,-1 0,1',
        path: 'M -2,0 2,0 0,-4 z',
        strokeOpacity: 0.8,
        scale: 3,
        //fillColor: mapObject.color,
        fillOpacity: 0.8,
      };
    }
    
    const polyline = new google.maps.Polyline({ // eslint-disable-line no-undef
      path: mapObject.coordinates,
      geodesic: true,
      strokeColor: mapObject.color,
      strokeOpacity: mapObject?.fillOpacity || 1.0,
      strokeWeight: mapObject?.strokeWeight || 2,
      map: mapItem,
      layerName: mapObject.layerName || TypeLayer.Default,
      // Only set `clickable` when explicitly provided; otherwise let Google Maps default apply (true)
      ...(typeof mapObject?.clickable === 'boolean' ? { clickable: mapObject.clickable } : {}),
      icons: dashedLine ? [{
        icon: dashedLine,
        offset: '0',
        repeat: '200px' // Define la distancia entre cada segmento punteado
      }] : null,
    })
    
    if(mapObject?.onClickEvent){
      const onPolylineClick = (event) => {
        const isSameSelection = selectedPolylineIdRef.current == mapObject.id;

        // If user clicks the same polyline again, deselect it and restore default style
        if (isSameSelection) {
          polyline.setOptions({
            strokeColor: mapObject?.color,
            scale: 3,
            fillOpacity: mapObject?.fillOpacity || 1.0,
            strokeWeight: mapObject?.strokeWeight || 2,
          });

          removeMapObject(`temporalTrailPolyLine_${mapObject.id}`, TypeObjectMap.Polyline);
          selectedPolylineIdRef.current = null;

          mapObject?.onClickEvent && mapObject?.onClickEvent({ ...mapObject.trailLineProps });
          return;
        }

        const oldMapObject = mapObjects?.find(
          (item) => item.id == selectedPolylineIdRef.current && item.type == TypeObjectMap.Polyline
        );

        oldMapObject?.instance && oldMapObject.id != mapObject.id && oldMapObject?.instance?.setOptions({
          strokeColor: oldMapObject?.color,
          scale: 3,
          fillOpacity: oldMapObject?.fillOpacity || 1.0,
          strokeWeight: oldMapObject?.strokeWeight || 2,
        });

        oldMapObject?.instance && oldMapObject.id != mapObject.id && removeMapObject(`temporalTrailPolyLine_${oldMapObject.id}`, TypeObjectMap.Polyline);

        //change color to the current object
        polyline.setOptions({
          strokeColor: '#005BF8',
          scale: 4,
          fillOpacity: 1,
          strokeWeight: (mapObject?.strokeWeight || 2) + 10,
        });
        
        //create polyline temporal to border the line
        const tempPolyLine = new google.maps.Polyline({ // eslint-disable-line no-undef
          path: mapObject.coordinates,
          geodesic: true,
          strokeColor: '#FFFFFF',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map: mapItem,
          layerName: mapObject.layerName || TypeLayer.Default,
          zIndex: 10,
          // Only set `clickable` when explicitly provided; otherwise let Google Maps default apply (true)
          ...(typeof mapObject?.clickable === 'boolean' ? { clickable: mapObject.clickable } : {}),
          icons: dashedLine ? [{
            icon: dashedLine,
            offset: '0',
            repeat: '200px'
          }] : null,
        })

        // The overlay polyline is drawn above the base polyline and captures clicks,
        // so it must use the same handler.
        tempPolyLine.addListener('click', onPolylineClick);

        //add to the mapObjects array
        const tempObj = { ...mapObject, instance: tempPolyLine, id: `temporalTrailPolyLine_${mapObject.id}` };
        mapObjects.push(tempObj);
        _indexAdd(tempObj);

        selectedPolylineIdRef.current = mapObject.id;
        mapObject?.onClickEvent && mapObject?.onClickEvent({ ...mapObject.trailLineProps });
      }

      polyline.addListener('click', onPolylineClick);
    }

    return polyline
  }

  /******************************************************
   * Add the point to polyline object
   ******************************************************/
  const addPolylinePoint = useCallback((id, latLng, isArray = false) => {
    // Route to WebGL trail layer when it manages this polyline
    if (webglTrailLayer && webglTrailLayer.hasSegment(id)) {
      if (!isArray && latLng && latLng?.lat && latLng?.lng) {
        webglTrailLayer.addPoint(id, latLng);
      } else if (latLng && latLng.length > 0 && isArray) {
        webglTrailLayer.addPoints(id, latLng);
      }
      return;
    }

    const polyline = mapObjectsIndex.get(_indexKey(TypeObjectMap.Polyline, id)) || null;

    if (polyline) {
      const path = polyline.instance.getPath();
      let objLatLng = null
      if(!isArray && latLng && latLng?.lat && latLng?.lng){
        objLatLng = new google.maps.LatLng(latLng.lat, latLng.lng) // eslint-disable-line no-undef  
        path.push(objLatLng);
      }else if(latLng && latLng.length > 0 && isArray){
        latLng.forEach((element)=> {
          if(element.lat && element.lng){
            path.push(new google.maps.LatLng(parseFloat(element.lat), parseFloat(element.lng)))
          }
          
        });
      }
      
      
    }
  })

  /******************************************************
   * Add the Polygon objecto into google maps to google.maps.Polygon
   * @param {JSON} mapObject 
   ******************************************************/
  const addPolygon = (mapObject) => {
    let polygon = null
    const safeColor = mapObject.color || '#8677D9';
    if (mapObject.coordinates && mapObject.coordinates.length > 0) {
      let fillOpacity = mapObject?.fillOpacity || 0.35
      let strokeWeight = mapObject?.strokeWeight || 0.5

      polygon = new google.maps.Polygon({ // eslint-disable-line no-undef          
        geodesic: false,
        strokeColor: mapObject.strokeColor || safeColor,
        strokeOpacity: 1.0,
        strokeWeight: strokeWeight,
        fillColor: safeColor,
        fillOpacity: fillOpacity,
        paths: mapObject.coordinates,
        map: mapItem,
        zIndex: 6,
        optimized: true
      })
    } else {
      polygon = new google.maps.Polygon({ // eslint-disable-line no-undef          
        geodesic: false,
        strokeColor: mapObject.strokeColor || safeColor,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: safeColor,
        fillOpacity: 0.35,
        map: mapItem,
        zIndex: 6,
        optimized: true
      })
    }

    return polygon
  }

  /******************************************************
   * Add the Circle object into google maps to google.maps.Circle
   * @param {JSON} mapObject 
   ******************************************************/
  const addCircle = (mapObject, drawColor = false, opacity) => {
    const circle = new google.maps.Circle({ // eslint-disable-line no-undef          
      geodesic: false,
      strokeColor: mapObject.color,
      strokeOpacity: 0.6,
      strokeWeight: 0.5,
      fillColor: drawColor ? mapObject?.color : '#000',
      fillOpacity: mapObject?.fillOpacity > 0 ? mapObject?.fillOpacity : (drawColor ? 0.3 : 0.15),
      center: mapObject.position,
      radius: mapObject.radius,
      map: mapItem,
      zIndex: 6,
      optimized: true
    })

    return circle
  }

  /******************************************************
   * Add the Rectangle object into google maps to google.maps.Rectangle
   * @param {JSON} mapObject 
   ******************************************************/
  const addRectangle = (mapObject, drawColor = false, opacity) => {
    let rectangle = new google.maps.Rectangle();
    rectangle.setOptions({ // eslint-disable-line no-undef          
      //geodesic: false,
      strokeColor: mapObject.color,
      strokeOpacity: 0.6,
      strokeWeight: 0.5,
      fillColor: drawColor ? mapObject.color : '#000',
      fillOpacity: mapObject?.fillOpacity > 0 ? mapObject?.fillOpacity : (drawColor ? 0.3 : 0.15),
      bounds: mapObject.boundsRectangle,
      map: mapItem,
      zIndex: 6,
      //optimized: true,
      draggable: mapObject.draggable || false
    })

    return rectangle
  }

  /******************************************************
   * Add the Square object into google maps to google.maps.Rectangle
   * @param {JSON} mapObject 
   ******************************************************/
  const addSquare = (mapObject, drawColor = false, opacity) => {

    const square = new google.maps.Rectangle({ // eslint-disable-line no-undef          
      geodesic: false,
      strokeColor: mapObject.color,
      strokeOpacity: 0.6,
      strokeWeight: 0.5,
      fillColor: drawColor ? mapObject.color : '#000',
      fillOpacity: mapObject?.fillOpacity > 0 ? mapObject?.fillOpacity : (drawColor ? 0.3 : 0.15),
      bounds: mapObject.boundsRectangle,
      map: mapItem,
      zIndex: 6,
      optimized: true,
      draggable: mapObject.draggable || false
    })

    square.addListener('drag', function (event) {
      const findSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Square)
      const findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.SquareCreate)
      const { north, south, east, west } = findSquareObject.instance.getBounds().toJSON();
      findSquareObject.boundsRectangle = findSquareObject.instance.getBounds().toJSON();


      if (findMarkerSquareObject) {
        const centerSquare = getCenterFromBounds(findSquareObject.instance.getBounds().toJSON())

        const position = { lat: centerSquare.lat, lng: east }
        findMarkerSquareObject.instance.setPosition(position)
        findMarkerSquareObject.position = position
      }
    });

    square.addListener('dragend', function (event) {
      const findSquareObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Square)
      findSquareObjectToUpdate.minBoundsRectangle = findSquareObjectToUpdate.instance.getBounds().toJSON()

      //update the center because the square is moved
      const centerSquare = getCenterFromBounds(findSquareObjectToUpdate.instance.getBounds().toJSON())
      findSquareObjectToUpdate.position = centerSquare;

      //update coordinates for square object
      const coordinates = convertBoundsToCoordinates(findSquareObjectToUpdate.instance.getBounds().toJSON())
      findSquareObjectToUpdate.coordinates = coordinates;
    });

    return square
  }

  const updateTypeSquareToFreeRectangle = useCallback((valueFreeRectangle = false) => {
    const findSquareObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Square)
    if (findSquareObjectToUpdate) {
      findSquareObjectToUpdate.freeRectangle = valueFreeRectangle
    }
  })

  const convertBoundsToCoordinates = (objectBounds) => {
    const coordinates = [];
    const { north, south, east, west } = objectBounds;

    coordinates.push({ latitude: north, longitude: west });
    coordinates.push({ latitude: north, longitude: east });
    coordinates.push({ latitude: south, longitude: east });
    coordinates.push({ latitude: south, longitude: west });
    coordinates.push(coordinates[0])

    return coordinates;
  }

  const getCenterFromBounds = (objectBounds) => {

    let coordinates = convertBoundsToCoordinates(objectBounds)
    const LatLngList = [];
    coordinates.forEach((element) => {
      LatLngList.push(new google.maps.LatLng(element.latitude, // eslint-disable-line no-undef
        element.longitude));
    })
    //Close the polygon object
    LatLngList.push(LatLngList[0])
    let latlngbounds = new google.maps.LatLngBounds(); // eslint-disable-line no-undef
    LatLngList.forEach((latLng) => {
      latlngbounds.extend(latLng);
    });

    let center = latlngbounds.getCenter();
    return { lat: center.lat(), lng: center.lng() };

  }

  /******************************************************
   * Add the Polygon objecto into google maps to google.maps.Polygon
   * @param {string} id of mapObject 
   ******************************************************/
  const AddPolygonPoint = useCallback((mouseEvent, id, markerObject) => {

    const mapObjectPolygon = mapObjects.find((item) => item.id == id);
    if (mapObjectPolygon) {

      const path = mapObjectPolygon.instance.getPath();

      //Make the validation of polygon -- Remove this two lines
      path.push(mouseEvent.latLng);
      //Create marker icon
      addMapObject(markerObject);

      /* const validationResult = validatePolygon(tempPath.Kb)
      if(validationResult.status){
        //Update new position polygon
        path.push(mouseEvent.latLng);
        //Create marker icon
        addMapObject(markerObject);
      } */
    }
  })

  /******************************************************
   * Create a new landmark on realtime maps
   * @param {*} position lat / lng position to the circle
   * @param {*} idFromTo name at the object
   * @param {*} onUpdateList events to return data on the UI
   * @param {*} objectOnMap 
   ******************************************************/
  const addCircleOnMapToCreate = (position, onUpdateList, idMarker, idCircle, radius, maxSize = 0, newMarkerStyle = null, zIndex = 10, noCenter = false, callbackDragEnd = null, typePoint = '') => {
    // Disable landmark/geofence area clicks while circle is being placed/resized.
    setLandmarkGeofenceAreasClickable(false);

    //if the value of the radius is 0, no new area is created for the point
    let radiusToMetters = radius;
    //create a main marker into the UI
    const mapObject = new MapObject().buildMarker({
      id: idMarker,
      position,
      layerName: TypeLayer.CircleCreate,
      label: '',
      heading: 0,
      color: '000',
      eventName: 'no event',
      draggable: true,
      maxSize: maxSize || 0,
      zIndex: zIndex
    });
    const markerElement = addMapObject(mapObject);
    if(callbackDragEnd){
      markerElement?.instance?.addListener('dragend', function (event) {
        const { lat, lng } = event.latLng.toJSON();
        typePoint ? callbackDragEnd({ lat, lng }, typePoint) : callbackDragEnd({ lat, lng })
      })
    }

    if(newMarkerStyle && mapObject.instance)
      mapObject.instance.setIcon(newMarkerStyle)

    //if the value of the radius is 0, no new area is created for the point
    if(radius == 0){

      if(!noCenter){
        //create a marker to expand or colapse circle object
        centerObjectOnMap(TypeObjectMap.Marker, idMarker);
      }
  
      //Update the color at the objects depends the settings selecion
      updateStyleToCreateObject();

      return 
    }

    //Create the Circle Object related to the marker
    const circle = new MapObject().buildCircle({
      id: idCircle, //Ref to move elements in marker event
      position,
      layerName: TypeLayer.CircleCreate,
      label: '',
      heading: 0,
      color: '#0098dc',
      eventName: 'no event',
      draggable: true
    })
    addMapObject(circle)

    const newLngOnMarkerDrag = getLngByDistance(position.lat, position.lng, radius)

    //create a main marker into the UI
    let markerObjectToDragCircle = new MapObject().buildMarker({
      id: `${idMarker}_drag`,
      position: { lat: position.lat, lng: newLngOnMarkerDrag },
      layerName: TypeLayer.CircleCreate,
      label: '',
      heading: 0,
      color: '000',
      eventName: 'no event',
      draggable: false,
      onUpdateList: onUpdateList,
      url: '/assets/icons/markersize.png',
      crossOnDrag: false,
      maxSize: maxSize || 0
    });

    markerObjectToDragCircle = addMapObject(markerObjectToDragCircle);

    if(markerObjectToDragCircle?.instance){
      markerObjectToDragCircle.instance.setDraggable(true);
    }else{
      //no instance return value
      return
    }
      

    //create event to drag
    markerObjectToDragCircle?.instance.addListener('drag', function (event) {
      const findCircleMarker = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.CircleCreate && !item.onUpdateList)
      const findCircleObjectToUpdate = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.CircleCreate && item.onUpdateList)
      //get distance from center point and new marker
      const { lat, lng } = event.latLng.toJSON();

      radiusToMetters = calcDistanceToPoints(findCircleMarker.instance.getPosition().lat(),
        findCircleMarker.instance.getPosition().lng(),
        lat,
        lng);

      updateCirclePoint(idCircle, null, radiusToMetters);
      //if have event to update list
      findCircleObjectToUpdate && findCircleObjectToUpdate?.onUpdateList && findCircleObjectToUpdate?.onUpdateList(radiusToMetters)
    });

    markerObjectToDragCircle?.instance?.addListener('dragend', function (event) {
      const findCircleMarker = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.CircleCreate && !item.onUpdateList)
      const findCircleMarkerMove = mapObjects.find(item => item.type == TypeObjectMap.Marker
        && item.layerName == TypeLayer.CircleCreate && item.onUpdateList)
      const { lat, lng } = event.latLng.toJSON();

      radiusToMetters = calcDistanceToPoints(findCircleMarker.instance.getPosition().lat(),
        findCircleMarker.instance.getPosition().lng(),
        lat,
        lng);
      //eval distance
      if (findCircleMarker.maxSize > 0 && radiusToMetters > findCircleMarker.maxSize) {
        //calc the point to marker
        const newLngOnMarkerDrag = getLngByDistance(findCircleMarker.instance.getPosition().lat(),
          findCircleMarker.instance.getPosition().lng(),
          findCircleMarker.maxSize)
        const position = { lat: findCircleMarker.instance.getPosition().lat(), lng: newLngOnMarkerDrag }
        findCircleMarkerMove.position = position
        findCircleMarkerMove.instance.setPosition(position)

        updateCirclePoint(idCircle, null, findCircleMarker.maxSize);

        findCircleMarkerMove && findCircleMarkerMove?.onUpdateList && findCircleMarkerMove?.onUpdateList(findCircleMarker.maxSize)

        radiusToMetters = findCircleMarker.maxSize;

      } else {
        updateCirclePoint(idCircle, null, radiusToMetters);
      }

      const coordinates = createSquareBoundingboxByMilesDistance(
        findCircleMarker.instance.getPosition().lat(),
        findCircleMarker.instance.getPosition().lng(),
        radiusToMetters);

      //center the element on map
      if(!noCenter){
        centerByFitBounds(coordinates);
      }
    })

    if(!noCenter){
      //create a marker to expand or colapse circle object
      centerObjectOnMap(TypeObjectMap.Marker, idMarker);
    }
    //dispatch(pointersCreated(true));
    updateCirclePoint(idCircle, null, radiusToMetters);

    //Update the color at the objects depends the settings selecion
    updateStyleToCreateObject();
  }

  /******************************************************
   * Perform the create new square object into the realtime maps
   * @param {Object} latLng 
   * @param {String} idFromTo 
   * @param {String} radiusObject 
   * @param {Float} radius 
   * @param {Float} defaulRadius 
   * @param {Boolean} freeRectangle 
   * @param {event} onUpdateList update the object radius in UI
   * @param {Int} maxSize
   ******************************************************/
  const addSquareOnMapToCreate = (latLng, idFromTo, radiusObject, radius, freeRectangle, onUpdateList, maxSize = 0) => {
    // Disable landmark/geofence area clicks while square is being placed/resized.
    setLandmarkGeofenceAreasClickable(false);
    const toRadiusInMetter = radius
    const squareCoordinates = createSquareBoundingboxByMilesDistance(latLng.lat, latLng.lng, toRadiusInMetter);

    const bounds = {
      north: squareCoordinates[0].latitude,
      south: squareCoordinates[1].latitude,
      east: squareCoordinates[0].longitude,
      west: squareCoordinates[2].longitude,
    };

    //Create the Square Object
    const square = new MapObject().buildSquare({
      id: radiusObject, //Ref to move elements in marker event,
      position: { lat: latLng.lat, lng: latLng.lng },
      boundsRectangle: bounds,
      layerName: TypeLayer.SquareCreate,
      label: '',
      heading: 0,
      color: '#0098dc',
      eventName: 'no event',
      draggable: true,
      minBoundsRectangle: bounds,
      freeRectangle: freeRectangle,
      coordinates: squareCoordinates,
      onUpdateList: onUpdateList,
      maxSize: maxSize
    })
    addMapObject(square)
    centerPointOnMap(latLng.lat, latLng.lng, 18);

    const position = { lat: latLng.lat, lng: bounds.east }

    const mapObject = new MapObject().buildMarker({
      id: idFromTo,
      position,
      layerName: TypeLayer.SquareCreate,
      label: '',
      heading: 0,
      color: '000',
      eventName: 'no event',
      draggable: true,
      saveDragEvent: true,
      //url: '/assets/icons/markersize.png'
      url: `${pathFile}/icons/maps/zoom_out_map/253858.png`,
      maxSize: maxSize
    });
    addMapObject(mapObject);

    //center the element on map
    centerByFitBounds(squareCoordinates);

    //Update the color at the objects depends the settings selecion
    updateStyleToCreateObject();

  }

  /******************************************************
   * Add the Circle object into google maps to google.maps.Circle
   * @param {string} id of mapObject 
   ******************************************************/
  const updateCirclePoint = useCallback((id, mouseEvent = null, radius = 0, centerOnMap = false) => {
    const mapObjectCircle = mapObjects.find((item) => item.id == id && item.type == TypeObjectMap.Circle);
    if (mapObjectCircle) {
      //update Position
      if (mouseEvent) {
        mapObjectCircle.instance.setCenter(mouseEvent.latLng.toJSON());
      }
      //update radius
      if (radius > 0) {
        mapObjectCircle.instance.setRadius(parseInt(radius))
      }
    }
    //update the marker positon
    const mapMarkerCircle = mapObjects.find((item) =>
      item.type == TypeObjectMap.Marker &&
      item.layerName == TypeLayer.CircleCreate &&
      item.onUpdateList);

    if (mapMarkerCircle) {

      const mapMarkerCircleCenter = mapObjects.find((item) =>
        item.type == TypeObjectMap.Marker &&
        item.layerName == TypeLayer.CircleCreate &&
        !item.onUpdateList)

      if(!mapMarkerCircleCenter?.instance) return
      //calc the point to marker
      const newLngOnMarkerDrag = getLngByDistance(mapMarkerCircleCenter.instance.getPosition().lat(),
        mapMarkerCircleCenter.instance.getPosition().lng(),
        mapObjectCircle.instance.getRadius())
      const position = { lat: mapMarkerCircleCenter.instance.getPosition().lat(), lng: newLngOnMarkerDrag }
      mapMarkerCircle.instance.setPosition(position)

      if (centerOnMap) {
        const coordinates = createSquareBoundingboxByMilesDistance(
          mapMarkerCircleCenter.instance.getPosition().lat(),
          mapMarkerCircleCenter.instance.getPosition().lng(),
          mapObjectCircle.instance.getRadius());

        //center the element on map
        centerByFitBounds(coordinates);
      }
    }
  })

  /******************************************************
   * Add the Circle object into google maps to google.maps.Circle
   * @param {string} id of mapObject 
   ******************************************************/
  const updateSquarePoint = useCallback((id, mouseEvent = null) => {
    let mapObjectSquare = mapObjects.find((item) => item.id == id && item.type == TypeObjectMap.Square);
    let findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.SquareCreate);
    if (mapObjectSquare) {
      //update Position
      if (mouseEvent) {
        if (!mouseEvent?.latLng?.toJSON()) return

        const { lat, lng } = mouseEvent.latLng.toJSON();

        if (validateValidSquare(mouseEvent)) {

          //create square at the minimun value (50 feet)
          const nPositionMarker = updateSquareByDistanceMin(mapObjectSquare, 15.24)

          //send the drag event to square
          google.maps.event.trigger(findMarkerSquareObject.instance, 'drag', { newPosition: nPositionMarker, paramMarker: findMarkerSquareObject });
          findMarkerSquareObject.instance.setDraggable(false);
          google.maps.event.removeListener(findMarkerSquareObject.dragEvent);
          google.maps.event.removeListener(findMarkerSquareObject.dragEndEvent);
          findMarkerSquareObject.instance.setMap(null);

          removeMapObject(findMarkerSquareObject.id, findMarkerSquareObject.type)
          findMarkerSquareObject = null

          setTimeout((nPosition, mapObjectSquareParam) => {
            const newNameMarker = `MarkerSquare${new Date()}`
            const mapObjectMarkerSquare = new MapObject().buildMarker({
              id: newNameMarker,
              position: nPosition,
              layerName: TypeLayer.SquareCreate,
              label: '',
              heading: 0,
              color: '000',
              eventName: 'no event',
              draggable: false,
              saveDragEvent: false,
              url: `${pathFile}/icons/maps/zoom_out_map/253858.png`
            });
            const MarkerObjectSquare = addMapObject(mapObjectMarkerSquare);

            MarkerObjectSquare.instance.setDraggable(true);
            const dragEvent = MarkerObjectSquare.instance.addListener('drag', function (event) {
              if (event?.paramMarker?.instance) {
                event.paramMarker.instance.setPosition(event.newPosition)
              } else {
                updateSquarePoint('mapObject_01', event)
              }
            });

            const dragStartEvent = MarkerObjectSquare.instance.addListener('dragstart', onDragStartSquareEvent);
            const dragEndEvent = MarkerObjectSquare.instance.addListener('dragend', onDragEndSquareEvent);

            MarkerObjectSquare.dragEvent = dragEvent
            MarkerObjectSquare.dragEndEvent = dragEndEvent
            MarkerObjectSquare.dragStartEvent = dragStartEvent

            google.maps.event.trigger(mapObjectSquareParam.instance, 'drag', null);
          }, 500, nPositionMarker, mapObjectSquare);

        } else {

          if (mapObjectSquare.freeRectangle) {
            //define the new bounds to square object
            const newBounds = {
              ...mapObjectSquare?.boundsRectangle,
              south: lat,
              east: lng,
            };
            mapObjectSquare.boundsRectangle = newBounds;

            //update the square size
            mapObjectSquare.instance.setBounds(newBounds)
            //update marker position into objects
            findMarkerSquareObject.position = { lat, lng };
          } else {
            updateSquareSizeToDragEvents(mapObjectSquare, mouseEvent)
            findMarkerSquareObject.position = { lat, lng };
          }
        }

      }
    }
  })

  /******************************************************
   * Create the min square object on map
   ******************************************************/
  const updateSquareByDistanceMin = (findSquareObjectToUpdate, radiusInMetter) => {
    //calc the point to marker
    const newLngOnMarkerDrag = getLngByDistance(findSquareObjectToUpdate.position.lat,
      findSquareObjectToUpdate.position.lng,
      radiusInMetter)
    const position = { lat: findSquareObjectToUpdate.position.lat, lng: newLngOnMarkerDrag }

    const findMarkerSquareObject = mapObjects.find(item => item.type == TypeObjectMap.Marker &&
      item.layerName == TypeLayer.SquareCreate)
    findMarkerSquareObject.position = position
    findMarkerSquareObject.instance.setPosition(position)

    const radioSquareInMeters = calcDistanceToPoints(findSquareObjectToUpdate.position.lat, findSquareObjectToUpdate.position.lng,
      position.lat, position.lng);


    const coordinatesFromCenter = createSquareBoundingboxByMilesDistance(findSquareObjectToUpdate.position.lat,
      findSquareObjectToUpdate.position.lng,
      radioSquareInMeters);

    const boundsToSquare = {
      north: coordinatesFromCenter[0].latitude,
      south: coordinatesFromCenter[1].latitude,
      east: coordinatesFromCenter[0].longitude,
      west: coordinatesFromCenter[2].longitude,
    };
    findSquareObjectToUpdate.boundsRectangle = boundsToSquare;

    //update the square size
    findSquareObjectToUpdate.instance.setBounds(boundsToSquare)
    //center the element on map
    centerByFitBounds(boundsToSquare);

    const coordinates = convertBoundsToCoordinates(boundsToSquare)
    findSquareObjectToUpdate.coordinates = coordinates;

    const distanceInMetters = calcDistanceToPoints(findSquareObjectToUpdate.position.lat,
      findSquareObjectToUpdate.position.lng,
      position.lat,
      position.lng);

    findSquareObjectToUpdate && findSquareObjectToUpdate?.onUpdateList && findSquareObjectToUpdate?.onUpdateList(distanceInMetters)

    return position
  }

  /************************************************************************
   * Update the styles at the polygon create object 
   ************************************************************************/
  const updateStyleToCreateObject = () => {
    //get the polygon object
    const polygonCreateSettings = mapObjects.find((item) => item.type == TypeObjectMap.Settings && item.id == 'mapCreateObjectSettings')
    const polygonObject = mapObjects.find((item) => item.type == TypeObjectMap.Polygon && item.layerName == TypeLayer.PolygonCreate);
    if (polygonCreateSettings && polygonObject) {
      const { color, strokeColor } = polygonCreateSettings
      //find the polygon and change the styles
      polygonObject.instance.setOptions({ strokeColor: strokeColor, fillColor: color });

      //find the markers and update the icons
      const markersOnPolygon = mapObjects.filter((item) => item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.PolygonCreate)
      if (markersOnPolygon) {
        const newMarkerStyle = getIconForPolygonCreate(color); //get new style for icons
        markersOnPolygon.forEach((marker) => {
          marker.instance.setIcon(newMarkerStyle)
        })
      }
    }

    //if is circle object
    const circleObject = mapObjects.find((item) => item.type == TypeObjectMap.Circle && item.layerName == TypeLayer.CircleCreate);
    if (polygonCreateSettings && circleObject) {
      const { color, strokeColor } = polygonCreateSettings
      //find the polygon and change the styles
      circleObject.instance.setOptions({ strokeColor: strokeColor, fillColor: color });
    }

    //if is square object
    const squareObject = mapObjects.find((item) => item.type == TypeObjectMap.Square && item.layerName == TypeLayer.SquareCreate);
    if (polygonCreateSettings && squareObject) {
      const { color, strokeColor } = polygonCreateSettings
      //find the polygon and change the styles
      squareObject.instance.setOptions({ strokeColor: strokeColor, fillColor: color });
    }
  }

  /************************************************************************
   * Validate the parameters to square object
   * @param {object} mouseEvent 
   * @returns 
   ************************************************************************/
  const validateValidSquare = (mouseEvent) => {
    const { lat, lng } = mouseEvent.latLng.toJSON();
    if (lat > squareLatMin)
      return true //is not valid

    if (lng < squareLngMin)
      return true //is not valid

    return false; //not found errors
  }

  /************************************************************************
   * Return the value to square lat / lng, preveng the overlap lines to square control
   * @param {float} position 
   * @param {float} newPosition 
   * @param {float} valueDecrease 
   * @returns value to drecrease
   ************************************************************************/
  const decreaseLatitudeToDrawSquare = (position, newPosition) => {
    let absoluteValuePos = Math.abs(position), absoluteValueNewPos = Math.abs(newPosition);
    let valueToDecrease = absoluteValuePos > absoluteValueNewPos
      ? absoluteValuePos - absoluteValueNewPos
      : absoluteValueNewPos - absoluteValuePos;
    valueToDecrease = valueToDecrease * 3;
    if (position >= 0 && newPosition > 0) {
      return valueToDecrease * -1
    } else if (position >= 0 && newPosition < 0) {
      return valueToDecrease * -1
    } else if (position <= 0 && newPosition < 0) {
      return valueToDecrease * -1
    }
    return valueToDecrease;
  }

  const decreaseLongitudeToDrawSquare = (position, newPosition) => {
    let absoluteValuePos = Math.abs(position), absoluteValueNewPos = Math.abs(newPosition);
    let valueToDecrease = absoluteValuePos > absoluteValueNewPos
      ? absoluteValuePos - absoluteValueNewPos
      : absoluteValueNewPos - absoluteValuePos;
    valueToDecrease = valueToDecrease * 3;
    if (position <= 0 && newPosition < 0) {
      return valueToDecrease
    } else if (position >= 0 && newPosition > 0) {
      return valueToDecrease
    } else if (position <= 0 && newPosition > 0) {
      return valueToDecrease
    }
    return valueToDecrease;
  }

  /******************************************************
   * Add the Polygon objecto into google maps to google.maps.Polygon
   * @param {string} id of mapObject 
   ******************************************************/
  const movePolygonPoint = useCallback((mouseEvent, id, markerObject) => {
    //poly.getPath().removeAt(polyIndex);

    const mapObjectPolygon = mapObjects.find((item) => item.id == id);
    if (mapObjectPolygon) {
      const path = mapObjectPolygon.instance.getPath();

      if (path.getArray().length > 0) {
        path.getArray()[markerObject.index - 1] = mouseEvent.latLng.toJSON();
      }

      //TODO: make the validation polygon
      mapObjectPolygon.instance.setPath(path.getArray());

      /* const validationResult = validatePolygon(tempPath.Kb)
      if(validationResult.status && markerObject.index > 1){
        //Update new position polygon
        mapObjectPolygon.instance.setPath(tempPath.Kb);
      }else{
        markerObject.instance.setPosition(oldValue)
      } */
    }
  })

  /******************************************************
   * Remove the point at the polygon
   * @param {string} id of mapObject
   * @param {Object} markerObject of mapObject 
   ******************************************************/
  const removePolygonPoint = useCallback((id, markerObject) => {
    const mapObjectPolygon = mapObjects.find((item) => item.id == id);
    if (mapObjectPolygon) {
      const index = markerObject.index;
      const path = mapObjectPolygon.instance.getPath();

      if (path.getArray().length > 0) {
        path.getArray().splice(index - 1, 1);
      }

      mapObjectPolygon.instance.setPath(path.getArray());

      removeItemsByIdAndType([markerObject.id])
      let markerList = mapObjects.filter((item) => item.layerName == markerObject.layerName &&
        item.type == TypeObjectMap.Marker);
      // sort by value
      markerList.sort(function (a, b) {
        return a.index - b.index;
      });

      let countIndex = 1
      markerList.forEach(item => {
        item.index = countIndex
        countIndex += 1
      });

      //Rename elements
      markerList = markerList.map(item => {
        item.id = `markerLandmark_${item.index}`
        return item
      })

      mapClickItems -= 1;
    }
  })

  /******************************************************
   * Get the coordinates from polygon
   ******************************************************/
  const getPolygonCoordinates = useCallback(() => {
    const mapObjectPolygonMarkers = mapObjects.filter((item) => item.type == TypeObjectMap.Marker &&
      item.layerName == TypeLayer.PolygonCreate);
    if (mapObjectPolygonMarkers && mapObjectPolygonMarkers.length > 0) {

      const LatLngList = [], coordinates = []
      mapObjectPolygonMarkers.forEach((element) => {
        LatLngList.push(new google.maps.LatLng(element.instance.getPosition().lat(), // eslint-disable-line no-undef
          element.instance.getPosition().lng()));
        coordinates.push({
          latitude: element.instance.getPosition().lat(),
          longitude: element.instance.getPosition().lng()
        });
      })
      //Close the polygon object
      LatLngList.push(LatLngList[0])
      coordinates.push(coordinates[0])

      let latlngbounds = new google.maps.LatLngBounds(); // eslint-disable-line no-undef

      LatLngList.forEach((latLng) => {
        latlngbounds.extend(latLng);
      });

      let center = latlngbounds.getCenter();
      return { coordinates, center: { latitude: center.lat(), longitude: center.lng() } };
    }
    return { coordinates: [], center: { latitude: 0, longitude: 0 } };
  })

  /******************************************************
   * Get the circle object to create 
   ******************************************************/
  const getCircleCoordinates = useCallback(() => {
    const mapObjectCircleMarker = mapObjects.find((item) => item.type == TypeObjectMap.Marker &&
      item.layerName == TypeLayer.CircleCreate);
    if (mapObjectCircleMarker) {
      const coordinates = [];
      coordinates.push({
        latitude: mapObjectCircleMarker.instance.getPosition().lat(),
        longitude: mapObjectCircleMarker.instance.getPosition().lng()
      });
      return coordinates;
    }
    return null;
  })

  /******************************************************
   * Reduce the click items into the view
  ******************************************************/
  const reduceMapClickItems = useCallback(() => mapClickItems -= 1)

  /*******************************************************
   * Create the icon into the realtime map
   * @param {string} iconColor | color to icon
   * @param {int} rotation | grades to rotation
   ******************************************************/
  const getIconAttributes = (paramIconColor, rotation, url = null, styleImage = {}, mapObject = null, path = null) => {
    //BACKWARD_CLOSED_ARROW, BACKWARD_OPEN_ARROW, CIRCLE, FORWARD_CLOSED_ARROW, FORWARD_OPEN_ARROW
    let iconColor = '#d7d7d6'; //set the default color to markers

    const labelPosition = new google.maps.Point(20, 110); // eslint-disable-line no-undef

    if (paramIconColor) {
      iconColor = paramIconColor.toString().replace('##', '#');
    }

    if (url) {
      let imageIcon = {
        labelOrigin: labelPosition,
        url: url,
        scale: 1.8, //Yessica
        fillColor: iconColor,
        fillOpacity: 1,
        strokeColor: 'white', //'#6f6f6f',
        sstrokeWeight: 2,
        rotation: parseInt(rotation),
        anchor: new google.maps.Point(9, 9)  // eslint-disable-line no-undef
      };
      if (styleImage) {
        delete imageIcon['scale']
        delete imageIcon['rotation']
        imageIcon = {
          ...imageIcon,
          labelOrigin: labelPosition,
          ...styleImage
        }
      }
      return imageIcon;
    }

    if (mapObject) {
      const pathImage = changePathMarker(mapObject.eventName)
      const routeMarkers = mapObject.hasVideo ? 'markervideo' : 'marker'
      //get direction Image
      const imageRotation = changeMarker(rotation, `/assets/icons/${routeMarkers}/${pathImage}/`);

      return {
        labelOrigin: labelPosition, // eslint-disable-line no-undef
        url: imageRotation,
        origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
        //x-y  
        anchor: new google.maps.Point(20, 20) // eslint-disable-line no-undef
      };
    } else {
      return {
        labelOrigin: labelPosition, // eslint-disable-line no-undef
        //path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, // eslint-disable-line no-undef
        path: path ? path : `
          M 12.11,2.48
          C 12.11,2.48 4.61,20.77 4.61,20.77
            4.61,20.77 5.32,21.48 5.32,21.48
            5.32,21.48 12.11,18.48 12.11,18.48
            12.11,18.48 18.90,21.48 18.90,21.48
            18.90,21.48 19.61,20.77 19.61,20.77
            19.61,20.77 12.11,2.48 12.11,2.48 Z`,
        //size: new google.maps.Size(30, 30),
        scale: 1.8,
        fillColor: iconColor,
        fillOpacity: 1,
        strokeColor: 'white', //'#6f6f6f',
        sstrokeWeight: 2,
        rotation: parseInt(rotation),
        //x-y  
        anchor: new google.maps.Point(12, 16), // eslint-disable-line no-undef
        ...styleImage
      };
    }
  };

  /*******************************************************
   * Get the icon style for polygon create on map
   * @param {string} color the string must be contain the # char
   *******************************************************/
  const getIconForPolygonCreate = (color) => {
    const markerPolygon = window.btoa(polygonMarkerPointSvg(color));
    const iconMarker = {
      url: `data:image/svg+xml;base64,${markerPolygon}`,
      scaledSize: new google.maps.Size(18, 18),
      anchor: new google.maps.Point(9, 9)  // eslint-disable-line no-undef
    };
    return iconMarker;
  }

  /*******************************************************
   * Create the cluster element to Cluster Icons Markers
   * @param {array} markers 
  *******************************************************/
  let _showMarkerClusterInProgress = false;
  const showMarkerCluster = useCallback((show = false, realtimeMap = false, showLabels = false) => {
    // Re-entry guard: showUnitsByPreferences → evalLabelAndClusterPreferences
    // may call showMarkerCluster(true) with stale Redux props before the store
    // has updated after the user toggled clusters OFF.  Prevent that loop.
    if (_showMarkerClusterInProgress) return;

    // When WebGL is active and cluster is being enabled:
    // Disable the WebGL layer and restore legacy markers so MarkerClusterer can manage them.
    // When cluster is being disabled: hide legacy markers and re-enable WebGL layer.
    // ── WebGL mode: delegate clustering entirely to supercluster inside WebGLMarkerLayer ──
    if (USE_WEBGL_MARKERS && webglLayer) {
      _showMarkerClusterInProgress = true;
      const id = clusterId;
      let objectCluster = getObject(TypeObjectMap.MarkerCluster, id);
      if (show) {
        // Ensure the WebGL overlay is attached to the map (may have been
        // detached by setVisible(false) during vehicle trails or similar).
        // ONLY call setVisible when truly hidden — calling it during
        // _hideUnitsOnly mode (route viewing) would reset that flag and
        // break route-stop-only rendering.
        if (webglLayer._hidden) {
          webglLayer.setVisible(true);
        }
        // Enable WebGL-native clustering (skip if already enabled)
        if (!webglLayer.isClusteringEnabled()) {
          webglLayer.enableClustering({ maxZoom: maxClusterZoom, radius: 200 });
        }
        // Store a sentinel so the rest of the codebase can detect cluster-active state
        if (!objectCluster) {
          objectCluster = new MapObject().buildMarkerCluster({ id });
          mapObjects.push(objectCluster);
          _indexAdd(objectCluster);
        }
        objectCluster.instance = { _webglCluster: true };
        restarLabelsToDefault();
      } else {
        clearWebGLClusterLabels();
        webglLayer.disableClustering();
        // Force a full data rebuild so all markers render with current state
        // (color, eventName, enable, hideMarker). Without this, _data inside
        // webglMarkerLayer may be stale — markers can be missing or show
        // incorrect event colors (e.g., "Drive" units rendered as inactive).
        updateWebGLMarkers();
        restarLabelsToDefault();
        removeAllbyItemObject(TypeObjectMap.MarkerCluster);
        // Keep the guard ACTIVE during showUnitsByPreferences so that
        // any downstream call to showMarkerCluster(true) triggered by
        // stale props in evalLabelAndClusterPreferences is blocked.
        // React hasn't re-rendered yet, so userMapPreferences.unitCluster
        // is still true — without the guard, clusters get re-enabled.
        props?.showUnitsByPreferences && props.showUnitsByPreferences();
        _showMarkerClusterInProgress = false;
        return;
      }
      _showMarkerClusterInProgress = false;
      return;
    }

    // Legacy cluster path (also used in WebGL mode when cluster is toggled)
    const id = clusterId
    let objectCluster = getObject(TypeObjectMap.MarkerCluster, id)
    //if (show && !objectCluster || (objectCluster && !objectCluster.instance) || (realtimeMap && objectCluster && objectCluster.instance)) {
    if (show && !objectCluster || show && objectCluster && !objectCluster.instance) {
      if (!objectCluster) {
        objectCluster = new MapObject().buildMarkerCluster({
          id: id
        })
      }

      const markers = []
      mapObjects.forEach((item) => {
        if (item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.Units && item?.enable && !item?.hideMarker && item.instance) {
          markers.push(item.instance)
        }
      })

      if (markers.length <= 0) {
        mapObjects.push(objectCluster)
        _indexAdd(objectCluster);
        return
      }

      const svgBlue = window.btoa(clusterSvgString('#1aa0da'));
      const svgYellow = window.btoa(clusterSvgString('#e8a317'));
      const svgRed = window.btoa(clusterSvgString('#ff0000'));

      const clusterMarkers = new MarkerClusterer({
        markers,
        map: mapItem,
        algorithm: new SuperClusterAlgorithm({ maxZoom: maxClusterZoom, radius: 200/* , minPoints: 5, radius: 60 */ }),
        renderer: {
          render: ({ markers, _position: position }) => {
            let varSvg = svgBlue
            if (markers.length > 20 && markers.length < 100) {
              varSvg = svgYellow
            } else if (markers.length >= 100) {
              varSvg = svgRed
            }
            return new MarkerCompat({
              position,
              icon: {
                url: `data:image/svg+xml;base64,${varSvg}`,
                scaledSize: new google.maps.Size(50, 50),
              },
              label: { text: String(markers.length), color: "white", fontSize: "15px" },
            });
          },
        },
      });

      //remove old marker Cluster
      restarLabelsToDefault()
      removeAllbyItemObject(TypeObjectMap.MarkerCluster)
      objectCluster.instance = clusterMarkers
      mapObjects.push(objectCluster)
      _indexAdd(objectCluster);

      if (showLabels) {
        addMapClusterListener()
      }

    } else if (!show && objectCluster && objectCluster?.instance) {

      if (mapClusterUpdateEvent) {
        clearInterval(mapClusterUpdateEvent);
        mapClusterUpdateEvent = null;
      }

      //remove cluster units
      objectCluster.instance.clearMarkers();

      //show the units in map again
      props?.showUnitsByPreferences && props.showUnitsByPreferences({ centerOnPreferences: false })
      //showLayer(TypeLayer.Units, true);

      restarLabelsToDefault();
      removeMapClusterListener();
      removeAllbyItemObject(TypeObjectMap.MarkerCluster);

      // In WebGL mode, re-enable the WebGL layer after cluster teardown
      if (USE_WEBGL_MARKERS && !props.isEtaLink && webglLayer) {
        // Hide legacy markers and let WebGL render them
        mapObjects.forEach((item) => {
          if (item.type === TypeObjectMap.Marker && item.layerName === TypeLayer.Units && item.instance) {
            item.instance.setMap(null);
          }
        });
        webglLayer.setVisible(true);
        updateWebGLMarkers();
      }

      const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');
      if (objectNoOverlapSettings && objectNoOverlapSettings?.enable) {
        startingOverlapSettings()
      }
    }
  })


  /*******************************************************
   * Create the cluster icon elements to landmarks
   * @param {array} landmarks 
   *******************************************************/
  /* eslint-disable no-unused-vars */
  const clusterIconsLandmark = (landmarks) => {
    // Add a marker clusterer to manage the markers.
    const clusterLandmark = new MarkerClusterer(mapItem, landmarks, { // eslint-disable-line no-undef
      imagePath: "/src/components/Map/svg/map_cluster",
      maxZoom: 20,
      imageExtension: "svg",
      Size: 60
    });

    clusterLandmark.setImageSizes(60);
    clusterLandmark.setClusterClass('circleMarkerClass');
    return clusterLandmark;
  }
  /* eslint-enable no-unused-vars */

  /*******************************************************
   * Draw the landmark temp into the realtime map
   *******************************************************/
  const drawLandmark = useCallback((item, radius, coordinates, clickEvent = false) => {
    const mapObject = new MapObject().buildMarker({
      id: clickEvent ? item.id : 'tempObject01', //load the id from element
      position: { lat: item.latitude, lng: item.longitude },
      layerName: TypeLayer.TemporalObjects,
      label: '',
      heading: 0,
      color: '000',
      icon: item?.url ? `${pathFile}/${item?.url}` : item?.color ? `${pathFile}/kmls/flagicon/${item?.color.replace("#", '')}.png` : null,
      eventName: 'no event',
      draggable: false,
      hideMarker: item?.hideMarker
    });

    const marker = addMapObject(mapObject)

    if (clickEvent && marker && marker.instance) {
      marker.instance.addListener('click', () => {
        //Return marker click option
        if (props.modalPopupEvent) {
          // Skip if a modal is already open — prevents click-through from the overlay
          if (realStateInfoWindow) return;
          const findMapObject = mapObjects.find(item => item.id == marker.id)
          const obj = {
            id: findMapObject.id,
            position: findMapObject.instance.getPosition(),
            icon: {
              rotation: 0
            },
            layerName: TypeLayer.Landmark
          }
          props.modalPopupEvent(obj, obj);
        }
      })
    }

    if (radius > 0) { //is circle
      //Create the Circle Object
      const circle = new MapObject().buildCircle({
        id: 'tempObject02', //Ref to move elements in marker event
        position: coordinates[0],
        layerName: TypeLayer.TemporalObjects,
        label: '',
        radius: radius,
        heading: 0,
        color: item?.color || '#87aefc',
        eventName: 'no event',
        draggable: true
      })
      addMapObject(circle)
      return true
    } else if (coordinates && coordinates.length > 0) { //polygon
      const tempCoordinates = coordinates.map(element => {
        return { lat: element.latitude, lng: element.longitude }
      })
      addMapObject(new MapObject().buildPolygon({
        id: 'tempObject03',
        coordinates: tempCoordinates,
        layerName: TypeLayer.TemporalObjects,
        label: 'Create Label...', //Add label name of landmark
        heading: 0,
        color: item?.color || '#87aefc'
      }))
      return true
    }
    return false
  })

  const drawShapeObjectTempOnMap = (item, tag, isLandmark = true) => {
    let objectColor = '8677D9', landmarkIcon = null;

    objectColor = item.color ? item.color : tag?.color  //add the default color to pallete
    landmarkIcon = tag && tag.iconName ? tag.iconName : '' //get icon from landmark tag
    landmarkIcon = item.iconName ? item.iconName : getIconGroupWithAnotherColor(landmarkIcon, item.color)  // get icon from landmark

    let position = validateLatLng(item);
    let childType = null;
    if (item.shape) {
      childType = item.shape == "circle" ? TypeObjectMap.Circle : TypeObjectMap.Polygon
    } else {
      childType = item.radius > 0 ? TypeObjectMap.Circle : TypeObjectMap.Polygon
    }

    let coordinates = []
    if (childType == TypeObjectMap.Polygon && item.coordinates) {
      coordinates = item.coordinates.map(element => {
        return { lat: parseFloat(element.latitude), lng: parseFloat(element.longitude) }
      })
      if ((!position.lat || !position.lng) && coordinates && coordinates.length > 0) {
        position.lat = coordinates[0].lat.toString();
        position.lng = coordinates[0].lng.toString();
      }
    }

    if ((!position.lat || !position.lng) && item.radius > 0 && item.coordinates && item.coordinates.length > 0) {
      position = { lat: item.coordinates[0].latitude, lng: item.coordinates[0].longitude }
    }

    let mapObject = null;
    if (isLandmark) {
      mapObject = new MapObject().buildLandmark({
        id: `tempObject01_${item.id}`,
        index: item.id,
        position: { lat: item.latitude, lng: item.longitude },
        groupId: item && item.tagsId ? item.tagsId.split(',') : [],
        url: landmarkIcon,
        radius: radiusFeetToMeters(item.radius),
        layerName: TypeLayer.TemporalObjects,
        label: cutNotes(item.name, 40),
        showLabel: false,
        hasChild: true,
        childType: childType,
        coordinates: item.radius == 0 ? coordinates : [],
        color: `#${objectColor}`
      })
    } else {
      mapObject = new MapObject().buildGeofence({
        id: `tempObject01_${item.id}`,
        index: item.id,
        position,
        groupId: item && item.tagsId ? item.tagsId.split(',') : [],
        radius: item.radius,
        layerName: TypeLayer.TemporalObjects,
        label: cutNotes(item.name, 40),
        showLabel: false,
        hasChild: false,
        childType: childType,
        coordinates: coordinates,
        color: `#${objectColor}`
      })
    }

    addMapObject(mapObject)
  }

  /*******************************************************
   * Suscription to props.units
   *******************************************************/
  const validateNewObject = (mapObject) => {
    let validate = true
    if (!mapObject.position || mapObject.position && (!mapObject.position.lat || !mapObject.position.lng)) {
      validate = false
    } else if (existsObject(mapObject)) {
      validate = false
    }
    return validate
  }

  /*******************************************************
   * Center the object into the realtime maps
  *******************************************************/
  const centerObjectOnMap = useCallback((type, id, zoom = 16, panBy = 0, getInstance = false, currentZoom = false, openModal = false) => {
    if (!mapObjects) {
      return
    }

    if(currentZoom && mapItem){
      zoom =  mapItem?.getZoom();
    }

    const mapObject = mapObjects.find((element) => element.type == type && element.id == id);
    if (mapObject) {
      // Resolve the best available position for this marker.
      // Priority: endPosition → position → instance.getPosition()
      // The first two are plain {lat,lng} objects updated by updateObject().
      // The instance fallback covers the edge case where the data properties
      // are stale/empty but the Google Maps marker IS on the map (e.g. the
      // unit was created with valid coords but never received an updateObject
      // update yet, or endPosition/position were overwritten with bad data).
      let lat, lng;
      const latestPos = mapObject.endPosition || mapObject.position;
      lat = parseFloat(latestPos?.lat);
      lng = parseFloat(latestPos?.lng);

      // Fallback: read from the marker instance if the data properties fail
      if ((isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) && mapObject.instance?.getPosition) {
        const instancePos = mapObject.instance.getPosition();
        if (instancePos) {
          const iLat = typeof instancePos.lat === 'function' ? instancePos.lat() : parseFloat(instancePos.lat);
          const iLng = typeof instancePos.lng === 'function' ? instancePos.lng() : parseFloat(instancePos.lng);
          if (!isNaN(iLat) && !isNaN(iLng) && !(iLat === 0 && iLng === 0)) {
            lat = iLat;
            lng = iLng;
          }
        }
      }

      // Final guard: if no source provided valid coordinates, bail out.
      if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
        return null;
      }

      // Cancel any running rAF animation for this marker.
      // Without this, the animation loop overwrites the position we are
      // about to set on the very next frame, causing the OverlayView
      // (InfoBubble) to snap to the intermediate animated position instead
      // of the resolved target.  The queue is also drained because its
      // items are now stale — the next updateObject will start fresh.
      const anim = mapObject._webglAnim;
      if (anim?.stopAnim) {
        anim.stopAnim();
        anim.stopAnim = null;
        anim.queue.length = 0;
        anim.lastAnimTime = 0;
        if (anim.isTrailSequence) {
          if (USE_WEBGL_MARKERS && webglLayer) webglLayer.stopAnimation(mapObject.id);
          _hideLiveIndicator();
          anim.isTrailSequence = false;
        }
      }

      if (mapObject.instance) {
        mapObject.instance.setPosition({ lat, lng });
      }

      // Sync the data position so the WebGL layer renders at the correct
      // spot on the next refresh (animation was cancelled, so the per-frame
      // position sync in animateMarker no longer runs).
      mapObject.position = { lat, lng };
      mapObject.endPosition = { lat, lng };

      if (USE_WEBGL_MARKERS && webglLayer) {
        webglLayer.updateMarkerPosition(mapObject.id, lng, lat, mapObject.heading || 0);
        scheduleWebGLRefresh(updateWebGLMarkers);
      }

      // openModal: when true, open the InfoBubble modal directly from here.
      // This consolidates position resolution, WebGL sync, and modal opening
      // into a single atomic operation.  The modal popup handler
      // (modalPopupEvent) calls showMarkerModal which positions the overlay
      // at the new marker FIRST and then centers the map — this prevents the
      // overlay from flashing at the old marker's position during the pan.
      if (openModal && props.modalPopupEvent) {
        // Update centerUnitIdRef immediately so that the old marker's
        // animation frames stop centering the map on its position.
        // The animation itself keeps running normally — only the map
        // centering and modal position guards (which compare against
        // centerUnitIdRef and customOverlay.id) change behavior.
        centerUnitIdRef.current = id;

        const instanceObject = mapObject?.instance ? Object.create(mapObject.instance) : null;
        props.modalPopupEvent(mapObject, instanceObject, null, zoom);
      } else {
        centerPointOnMap(lat, lng, zoom, panBy);
      }
      return mapObject
    } else {
      //Only set zoom elements
      if (mapItem) {
        mapItem.setZoom(zoom);
      }
    }
    return null
  })

  /*******************************************************
   * Center the object into the realtime maps
   * @param {TypeObjectMap} is TypeObjectMap object
   * @param {string} id Element for mapObject
  *******************************************************/
  const getObject = useCallback((type, id = '') => {
    if (id) {
      return mapObjectsIndex.get(_indexKey(type, id)) || null;
    }
    return null
  })

  /*******************************************************
   * Center the object into the realtime maps
  *******************************************************/
  const getObjectsByType = useCallback((type) => {
    const mapObject = mapObjects.find((element) => element.type == type);
    if (mapObject) {
      return mapObject
    }
    return null
  })

  /*******************************************************
   * Center the object into the realtime maps
  *******************************************************/
  const getObjectsByTypeByLayer = useCallback((type, layer = TypeLayer.Default) => {
    let mapObject = mapObjects.filter((element) => element.type == type && element.layerName == layer);

    if (mapObject) {
      return mapObject
    }
    return null
  })

  /*******************************************************
   * Set enable true or false by type and optionally by groupId.
   * When layerName is provided, only objects on that layer are affected;
   * this prevents route-stop (FSMRoute) markers from being accidentally
   * disabled when unit-visibility preferences refresh.
   *******************************************************/
  const setEnableObjectsByType = useCallback((type, enable = true, groupId = null, layerName = null) => {
    mapObjects?.forEach((element, index) => {
      if (
        element.type === type &&
        (!groupId || element.groupId?.includes(groupId)) &&
        (!layerName || element.layerName === layerName)
      ) {
        // Verify if the object is immutable
        if (Object.isFrozen(element)) {
          // Replace immutable object with a mutable copy
          _indexRemove(element);
          mapObjects[index] = { ...element, enable };
          _indexAdd(mapObjects[index]);
        } else {
          // Modify directly if mutable
          element.enable = enable;
        }
      }
    });
  }, [mapItem]);

  /*******************************************************
   * Set enable true or false by idGroup
  *******************************************************/
  const setEnableObjectsByIds = useCallback((type, ids, enable = true) => {
    const mapElements = mapObjects.filter((element) => element.type == type && ids?.map(String).includes(String(element.id)));

    if (mapElements && mapElements.length > 0) {
      mapElements.forEach((element) => {
        element.enable = enable
      })
    }
  })

  /*******************************************************
   * Update the mapType view in realtime map
   *******************************************************/
  const updateMapType = useCallback((type) => {
    if (mapItem) {
      // Pure satellite mode uses only raster imagery tiles with no label overlay,
      // which hides all POIs (cafes, hotels, etc.).  Hybrid mode provides the
      // same satellite imagery AND the vector label/POI overlay, so the user
      // retains the aerial view while keeping points of interest visible.
      const resolvedType = type === 'satellite' ? 'hybrid' : type;
      mapItem.setMapTypeId(resolvedType);
      // Satellite/hybrid mode may re-enable 45° tilt, which applies a CSS
      // perspective transform to the map container and breaks the deck.gl
      // overlay z-index.  Calling setTilt(0) keeps the map flat.
      mapItem.setTilt(0);
    }
  })

  /*******************************************************
   * Show the vehicle trails components into the realtime maps
   *******************************************************/
  const showVehicleTrails = useCallback((showVehicleTrails, pinnedUnitId = null) => {
    if (showVehicleTrails) {
      //remove old vehicletrails layers, able to pagination
      removeLayer(TypeLayer.VehicleTrails);
      //hide marker cluster
      showMarkerCluster(false);
      //hide Markers
      showLayer(TypeLayer.Units, false);
      showLayer(TypeLayer.UnitLabel, false);
      // Hide unit markers in WebGL but keep route-stop pins visible.
      // setUnitsVisible(false) suppresses fleet arrows without hiding the
      // route-stop pin layer that shares the same overlay.
      // When a pinnedUnitId is provided (assigned route with driver),
      // that unit remains visible with live event updates.
      if (USE_WEBGL_MARKERS && webglLayer) {
        if (pinnedUnitId) {
          // Re-enable the pinned unit's mapObject so updateWebGLMarkers()
          // includes it in _data.  showLayer(Units, false) above set
          // hideMarker=true on ALL units — undo it for this one.
          const pinnedObj = mapObjects.find(
            item => item.id == pinnedUnitId && item.layerName === TypeLayer.Units
          );
          if (pinnedObj) {
            pinnedObj.hideMarker = false;
          }
          webglLayer.setPinnedUnitId(pinnedUnitId);
          // Force immediate data rebuild so the pinned unit is in _data
          // before setUnitsVisible renders the filtered view.
          updateWebGLMarkers();
        }
        webglLayer.setUnitsVisible(false);
      }
    } else {
      //hide trails
      removeLayer(TypeLayer.VehicleTrails);

      // WebGL mode: explicitly undo what the entry path did
      if (USE_WEBGL_MARKERS && webglLayer) {
        // Clear any pinned unit from route mode
        webglLayer.setPinnedUnitId(null);
        // Reset hideMarker flag (was set to true by showLayer(Units, false) on entry)
        showLayer(TypeLayer.Units, true);
        // Re-enable all markers in the WebGL overlay
        webglLayer.setUnitsVisible(true);
        // Push unit data to the WebGL layer immediately
        updateWebGLMarkers();
      }

      //show Markers
      props?.showUnitsByPreferences && props.showUnitsByPreferences()

      const objectUnitCluster = getObject(TypeObjectMap.Settings, 'unitClusterSettings');
      const objectShowLabelSettings = getObject(TypeObjectMap.Settings, 'unitLabelSettings');
      //const objectNoOverlapSettings = getObject(TypeObjectMap.Settings, 'noOverlapSettings');

      const showLabel = (objectShowLabelSettings && objectShowLabelSettings.enable) || false;
      const showCluster = (objectUnitCluster && objectUnitCluster?.enable) || false

      if (showLabel) {
        if (!showCluster) {
          addLabelMarkers(TypeLayer.UnitLabel, true, false)
        } else {
          showOrHideLabelsOnCluster()
        }
      } else {
        removeLayer(TypeLayer.UnitLabel)
        if (showCluster && !showLabel) {
          removeMapClusterListener();
        }
      }

      if (showCluster) {
        if (!showLabel) {
          removeMapClusterListener()
        }
        showMarkerCluster(true, false, showLabel)
      }
    }
  })

  /*******************************************************
   * Center the map with the coordinates to params, panBy can be used for to move the center area vertically
   * @param {float} lat 
   * @param {float} lng 
   * @param {int} zoom 
   * @param {int} panBy  
   *******************************************************/
  const centerPointOnMap = useCallback((lat, lng, zoom = 0, panBy, milliseconsToPanBy = 0) => {
    if (!mapItem || !lat || !lng) {
      return;
    }

    if (zoom) {
      mapItem.setZoom(zoom);
      if(!isNaN(lat) && !isNaN(lng)){
        mapItem.panTo({ lat: lat, lng: lng });
        if (panBy) {
          if(milliseconsToPanBy > 0){
            mapItem.panBy(0, panBy, milliseconsToPanBy);
          }else{
            mapItem.panBy(0, panBy);
          }
        }
      }
    } else {
      centerByFitBounds([{ lat: lat, lng: lng }]);
    }
  })

  /*******************************************************
   * Toggle `clickable` on all landmark/geofence area shapes.
   * When active=false, clicks pass through to the map so that
   * polygon points can be placed and the info modal is not opened
   * while a new landmark/geofence is being drawn.
  *******************************************************/
  const setLandmarkGeofenceAreasClickable = (active) => {
    mapObjects.forEach((item) => {
      // Landmark: area shape (circle/polygon) is stored in childInstance
      if (item.type === TypeObjectMap.Landmark && item.childInstance?.setOptions) {
        try { item.childInstance.setOptions({ clickable: active }); } catch (e) {}
      }
      // Geofence: area shape is the main instance (no separate icon marker)
      if (item.type === TypeObjectMap.Geofence && item.instance?.setOptions) {
        try { item.instance.setOptions({ clickable: active }); } catch (e) {}
      }
    });
  };

  /*******************************************************
   * Add the event to add map click
  *******************************************************/
  const initMapClick = useCallback((eventClick = null) => {
    // Disable landmark/geofence area clicks so they don't intercept
    // the map click events used to place polygon points.
    setLandmarkGeofenceAreasClickable(false);
    if (mapItem) {
      mapClickEvent = mapItem.addListener("click", (mapsMouseEvent) => { // eslint-disable-line no-undef
        if (mapsMouseEvent && eventClick) {
          mapClickItems = mapClickItems + 1
          eventClick(mapsMouseEvent.latLng.toJSON(), mapsMouseEvent, mapClickItems)
        }
      })
    }
  })

  /*******************************************************
   * Add the event to remove map click
  *******************************************************/
  const removeMapClick = useCallback(() => {
    // Restore landmark/geofence area clickability when creation mode ends.
    setLandmarkGeofenceAreasClickable(true);
    if (mapClickEvent) {
      google.maps.event.removeListener(mapClickEvent); // eslint-disable-line no-undef
      mapClickItems = 0
    }
  })


  /*******************************************************
   * Add the event to cluster 
  *******************************************************/
  const addMapClusterListener = useCallback(() => {
    // In WebGL mode, cluster labels are handled by the TextLayer — no event listener needed
    if (USE_WEBGL_MARKERS && webglLayer) return;

    if (!mapClusterEvent) {
      let clusterMarkers = getObject(TypeObjectMap.MarkerCluster, clusterId)
      if (clusterMarkers && clusterMarkers.instance) {
        mapClusterEvent = google.maps.event.addListener(clusterMarkers.instance, 'clusteringend', showOrHideLabelsOnCluster); // eslint-disable-line no-undef
      }
    }
  }, [mapClusterEvent, mapItem])

  /*******************************************************
   * Remove the cluster event 
  *******************************************************/
  const removeMapClusterListener = useCallback(() => {
    if (mapClusterEvent) {
      google.maps.event.removeListener(mapClusterEvent); // eslint-disable-line no-undef
      mapClusterEvent = null;
    }
  }, [mapClusterEvent, mapItem])  

  /*******************************************************
   * Add the event to cluster 
  *******************************************************/
  const addMapClickRouteListener = () => {
    if(!mapClickRouteEvent && mapItem){
      mapClickRouteEvent = mapItem.addListener("click", (mapsMouseEvent) => { // eslint-disable-line no-undef
        if(props?.onRouteMapClick){
          props.onRouteMapClick()
        }
      })
    } 

  }

  /*******************************************************
   * Remove the cluster event 
  *******************************************************/
  const removeMapClickRouteListener = useCallback(() => {
    if (mapClickRouteEvent && mapItem) {
      google.maps.event.removeListener(mapClickRouteEvent); // eslint-disable-line no-undef
      mapClickRouteEvent = null;
    }
  })

  /*******************************************************
   * Remove Route events
  *******************************************************/
  const removeRouteListener = useCallback(() => {
    if (eventMouseOverFirstPoint) {
      google.maps.event.removeListener(eventMouseOverFirstPoint)
      eventMouseOverFirstPoint = null;
    }

    if (eventMouseOutFirstPoint) {
      google.maps.event.removeListener(eventMouseOutFirstPoint)
      eventMouseOutFirstPoint = null;
    }

    if (eventMouseOverFirstMarker) {
      google.maps.event.removeListener(eventMouseOverFirstMarker)
      eventMouseOverFirstMarker = null;
    }

    if (eventMouseOutFirstMarker) {
      google.maps.event.removeListener(eventMouseOutFirstMarker)
      eventMouseOutFirstMarker = null;
    }

    if (eventInfoWindow) {
      google.maps.event.removeListener(eventInfoWindow)
      eventInfoWindow = null;
    }

    if (startPointMarkerDragend) {
      google.maps.event.removeListener(startPointMarkerDragend)
      startPointMarkerDragend = null;
    }

    if (endPointMarkerDragend) {
      google.maps.event.removeListener(endPointMarkerDragend)
      endPointMarkerDragend = null;
    }

    if (infoWindowMarker && infoWindowMarker.getMap()) {
      infoWindowMarker.setMap(null)
      infoWindowMarker = null;
    }
    durationRoute = 0;

  })

  /*******************************************************
   * Function: showLandmarksGeofenceByZoomInBounds
   * Description: Determines the visibility of landmarks or geofences 
   *              based on the current zoom level of the map.
   * Parameters:
   *  - id: The ID of the settings object to evaluate (e.g., "landmarkSettings" or "geofenceSettings").
   *  - zoomMap: The current zoom level of the map.
   * Returns:
   *  - An object containing two boolean values:
   *      - showMain: Indicates whether the main layer should be visible.
   *      - showChild: Indicates whether the child layer should be visible.
   *******************************************************/
  const showLandmarksGeofenceByZoomInBounds = (id, zoomMap) => {
    // Find the settings object by its ID
    const settingsObject = mapObjects.find((item) => item.id == id);

    // If the settings object is not found, return early
    if (!settingsObject) return;

    // Determine the layer type (Landmark or Geofence)
    let layer = TypeLayer.LandmarkMarker;
    if (settingsObject.id == "geofenceSettings") {
      layer = TypeLayer.Geofence;
    }

    // If the layer is disabled, return visibility as false for both main and child layers
    if (!settingsObject?.enable) {
      return { showMain: false, showChild: false };
    }

    // Evaluate visibility based on the zoom level
    const showMain = zoomMap >= settingsObject.value; // Main layer visibility
    const showChild = zoomMap >= settingsObject.valueChild; // Child layer visibility

    // Return the visibility status
    return { showMain, showChild };
  };

  /*******************************************************
   * Function: updateVisibilityForLayer
   * Description: Updates the visibility of map objects (polygons or circles) 
   *              based on their layer, type, and bounds.
   * Parameters:
   *  - layerName: The name of the layer to filter objects.
   *  - type: The type of the map object (e.g., Landmark, Geofence).
   *  - childType: The child type of the object (e.g., Polygon, Circle).
   *  - currentBounds: The current visible bounds of the map.
   *  - isInBoundsFn: A function to check if the object is within bounds.
   *  - zoomValues: An object containing zoom values for visibility.
   *******************************************************/
  const updateVisibilityForLayer = (layerName, type, childType, boundsObject, isInBoundsFn, zoomValues, currentBounds) => {
    // Efficiently iterate through all map objects
    for (let i = 0, len = mapObjects.length; i < len; i++) {
      const item = mapObjects[i];
      
      // Check if the current item matches the target layer, type and child type
      if (!(item.layerName === layerName && item.type === type && item.childType === childType && item?.enable)) {
        continue;
      }
      
      // Get the appropriate instance to work with
      const instance = item?.childInstance || item?.instance;
      if (!instance) {
        continue;
      }
      
      // Check if the instance is within the current map bounds
      //const isInBounds = isInBoundsFn(instance, currentBounds);
      const isInBounds = isInBoundsFn(item, boundsObject, currentBounds);
      
      // Handle visibility for items with both parent and child instances
      if (item?.childInstance && item?.instance) {
        // Set map visibility for the main instance based on bounds and zoom settings
        item.instance.setMap(isInBounds && zoomValues?.showMain ? mapItem : null);
        // Set map visibility for the child instance based on bounds and zoom settings
        item.childInstance.setMap(isInBounds && zoomValues?.showChild ? mapItem : null);
      } else {
        // Set map visibility for single-instance items based on bounds and zoom settings
        instance.setMap(isInBounds && zoomValues?.showMain ? mapItem : null);
      }
    }
  };

  const updateUnitsAndClusterInIdle = useCallback(() => {
    const currentBounds = mapItem?.getBounds();
    
    hiddenMarkersOnMap(currentBounds);

    // Update markers — WebGL layer or legacy cluster
    if (USE_WEBGL_MARKERS) {
      // Batch all WebGL state mutations so only ONE _renderLayers() fires.
      // setZoom, updateMarkers, and label settings each call _scheduleRender
      // internally — without the batch that would be 3+ redundant renders.
      if (webglLayer) {
        webglLayer.beginUpdate();
      }
      // Feed viewport bbox + zoom so supercluster recomputes accurately on pan/zoom
      if (webglLayer && webglLayer.isClusteringEnabled() && currentBounds) {
        webglLayer.setZoom(mapItem.getZoom());
      }
      // Always refresh WebGL markers (clustering is handled internally)
      updateWebGLMarkers();
      if (webglLayer) {
        webglLayer.endUpdate();
      }
      // Update cluster label overlays when "Label Clusters" is active
      updateWebGLClusterLabels();
      // Update group label bubbles (clusters OFF + noOverlap + zoom threshold)
      updateWebGLUnitLabelGroups();
    } else {
      updateClusterMarkers(clusterId);
    }

    // wheather and cluster objects
    onEndBoundsChanged(); 
  },[mapItem])

  /*******************************************************
   * Add the event to cluster 
   *******************************************************/
  const addMapBoundsEndedListener = () => {
    if (!mapBoundsChangedEvent) {
      // save the current zoom level
      let previousZoomLevel = mapItem.getZoom();

      // Re-apply weather overlay whenever the base map type changes.
      // setMapTypeId() resets Google Maps' tile pipeline and may clear
      // overlayMapTypes before the next idle fires. This mirrors the
      // maptypeid_changed pattern used by webglMarkerLayer.js.
      mapItem.addListener('maptypeid_changed', () => { // eslint-disable-line no-undef
        if (isWeatherEnabled) {
          google.maps.event.addListenerOnce(mapItem, 'idle', () => { // eslint-disable-line no-undef
            if (isWeatherEnabled) {
              onEnableWeatherBounds = true;
              showWeather();
            }
          });
        }
      });

      /*******************************************************
       * zoom_changed — fires continuously during scroll-zoom.
       * Debounce so cluster recomputation + label collision
       * updates happen every ~300 ms instead of waiting for
       * `idle` (which can be seconds after the user stops).
       * DOM overlays (cluster labels, unit-group bubbles) are
       * cleared immediately to avoid stale overlay artifacts.
       *******************************************************/
      if (!mapZoomChangedEvent) {
        mapZoomChangedEvent = mapItem.addListener('zoom_changed', () => {
          if (!USE_WEBGL_MARKERS || !webglLayer) return;

          // Immediately clear DOM-based label overlays so they
          // don't sit at stale positions during the zoom animation.
          clearWebGLClusterLabels();
          clearWebGLUnitLabelGroups();

          // Debounce the heavier WebGL cluster + label recomputation
          if (zoomChangedDebounceTimer) clearTimeout(zoomChangedDebounceTimer);
          zoomChangedDebounceTimer = setTimeout(() => {
            zoomChangedDebounceTimer = null;
            if (!mapItem || !webglLayer) return;

            const currentZoom = mapItem.getZoom();

            // Refresh animation-zone indicator on zoom change
            _updateAnimZoneIndicator(currentZoom);

            // Hide LIVE indicator when zoom drops below the animation threshold.
            // Animations snap at low zoom so the badge would be misleading.
            if (currentZoom < zoomToEnableTrails) {
              _hideLiveIndicator();
            }

            // All state mutations below call _scheduleRender inside the
            // WebGL layer — only one render fires per batch.

            // Let the WebGL layer recompute clusters at the new zoom.
            webglLayer.setZoom(currentZoom, true);

            // Clear any stale animation IDs that may keep labels transparent.
            // Animations are unreliable during rapid zoom — some cancel without
            // calling stopAnimation, leaving IDs stuck in _animatingIds.
            webglLayer.clearStaleAnimations();

            // Invalidate collision cache so label layout recalculates with
            // the current viewport bounds/projection.
            webglLayer.refresh();

            // Refresh label collision / no-overlap state
            addLabelMarkers(TypeLayer.UnitLabel, true, false);

            // Recreate DOM cluster / group label overlays at the new zoom
            updateWebGLClusterLabels();
            updateWebGLUnitLabelGroups();
          }, 300);
        });
      }

      mapBoundsChangedEvent = mapItem.addListener('idle', () => {
        const currentBounds = mapItem.getBounds();
        const currentZoomLevel = mapItem.getZoom(); // get the current zoom level

        // Refresh animation-zone indicator on idle (covers pan, programmatic zoom, etc.)
        _updateAnimZoneIndicator(currentZoomLevel);

        // Cancel any pending zoom_changed debounce — idle does a full refresh.
        if (zoomChangedDebounceTimer) {
          clearTimeout(zoomChangedDebounceTimer);
          zoomChangedDebounceTimer = null;
        }

        if (currentBounds && currentBounds.getSouthWest() && currentBounds.getNorthEast()) {
          lat1 = currentBounds.getSouthWest().lat(), lat2 = currentBounds.getNorthEast().lat();
          lng1 = currentBounds.getSouthWest().lng(), lng2 = currentBounds.getNorthEast().lng();
        }
        
        if (isWeatherEnabled) {
          onEnableWeatherBounds = false;
          mapItem?.setOptions({ minZoom: 0, maxZoom: 22 });
          mapItem?.overlayMapTypes.removeAt(0);
        }

        if(currentZoomLevel !== previousZoomLevel){
          if (onCallBackZoom)
            onCallBackZoom(currentZoomLevel)
          //showsKmlObjectsByZoom(zoomMap);

          //show landmarks icons
          showLandmarksGeofenceByZoom('landmarkSettings', currentZoomLevel);
          showLandmarksGeofenceByZoom('geofenceSettings', currentZoomLevel);
        }
        previousZoomLevel = currentZoomLevel; //update the current zoom level
  
        
        // Update visibility for units, cluster
        updateUnitsAndClusterInIdle()

        // Safety: after every idle event, clear stale animation state that
        // could keep labels transparent, and ensure cluster layers are coherent.
        if (USE_WEBGL_MARKERS && webglLayer) {
          webglLayer.clearStaleAnimations();

          // Resolve any pending cluster transition (click-to-zoom).
          // The map camera has settled — getBounds() now returns the correct
          // viewport, so cluster recomputation will use accurate bounds.
          webglLayer.resolveClusterTransition();
        }

        // wheather
        onEndBoundsChangedWeather();
        
        // Ensure only one interval is active
        if (mapClusterUpdateEvent) {
          clearInterval(mapClusterUpdateEvent);
          mapClusterUpdateEvent = null;
        }

        const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
        if (objectCluster && objectCluster?.instance) {
          
          // Start a new interval to update cluster markers every 2 minute
          mapClusterUpdateEvent = setInterval(() => { 

            if(!divMap?.current || !mapItem){
              clearInterval(mapClusterUpdateEvent);
              mapClusterUpdateEvent = null;
              return;
            }

            // Update visibility for units, cluster
            updateUnitsAndClusterInIdle();
          }, 120000);
        }

        // Get the map's current bounds
        const boundsNE = currentBounds.getNorthEast();
        const boundsSW = currentBounds.getSouthWest();

        const mapBoundsToValidate = {
          north: boundsNE.lat(),
          south: boundsSW.lat(),
          east: boundsNE.lng(),
          west: boundsSW.lng(),
        };

        // Update visibility for landmarks (polygons and circles)
        let zoomValues = showLandmarksGeofenceByZoomInBounds('landmarkSettings', mapItem.getZoom()); //eval preferences for zoom to show landmark
        if(zoomValues?.showMain || zoomValues?.showChild){
          updateVisibilityForLayer(TypeLayer.LandmarkMarker, TypeObjectMap.Landmark, TypeObjectMap.Polygon, mapBoundsToValidate, isPolygonInBounds, zoomValues, currentBounds);
          updateVisibilityForLayer(TypeLayer.LandmarkMarker, TypeObjectMap.Landmark, TypeObjectMap.Circle, mapBoundsToValidate, isCircleInBounds, zoomValues, currentBounds);
        }

        // Update visibility for geofences (polygons and circles)
        zoomValues = showLandmarksGeofenceByZoomInBounds('geofenceSettings', mapItem.getZoom()); //eval preferences for zoom to show geofence
        if(zoomValues?.showMain || zoomValues?.showChild){
          updateVisibilityForLayer(TypeLayer.Geofence, TypeObjectMap.Geofence, TypeObjectMap.Polygon, mapBoundsToValidate, isPolygonInBounds, zoomValues, currentBounds);
          updateVisibilityForLayer(TypeLayer.Geofence, TypeObjectMap.Geofence, TypeObjectMap.Circle, mapBoundsToValidate, isCircleInBounds, zoomValues, currentBounds);
        }

      });
    }
  }

  /*******************************************************
   * Function: updateClusterMarkers
   * Description: Updates the markers in the cluster by filtering visible markers 
   *              on the map and adding them to the cluster instance.
   * Parameters:
   *  - clusterId: The ID of the cluster object to update.
   *******************************************************/
  const updateClusterMarkers = (clusterId) => {
    // Retrieve the cluster object by its ID
    const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
    
    if (objectCluster && objectCluster.instance) {
      // Collect ALL enabled markers — MarkerClusterer handles viewport culling
      // internally via SuperClusterAlgorithm. Do NOT filter by getMap() because
      // that would exclude markers hidden by prior setMap(null) calls.
      const allMarkers = mapObjects
        .filter((item) =>
          item.type === TypeObjectMap.Marker &&
          item.layerName === TypeLayer.Units &&
          item?.enable &&
          !item?.hideMarker &&
          item.instance
        )
        .map((item) => item.instance);

      // noDraw=true suppresses the intermediate render with 0 markers.
      // addMarkers() then triggers a single render pass.
      objectCluster.instance.clearMarkers(true);
      objectCluster.instance.addMarkers(allMarkers);
    }
  };

  /*******************************************************
   * Function: updateWebGLMarkers
   * Description: Pushes all visible unit markers to the WebGL layer.
   *              Called from the same places as updateClusterMarkers.
   *******************************************************/
  const updateWebGLMarkers = () => {
    if (!USE_WEBGL_MARKERS || !webglLayer) return;

    const webglObjects = mapObjects.filter((item) =>
      item.type === TypeObjectMap.Marker &&
      (item.layerName === TypeLayer.Units || item.layerName === TypeLayer.FSMRoute) &&
      item?.enable &&
      !item?.hideMarker
    );

    webglLayer.updateMarkers(webglObjects);

    // Keep label settings in sync from the very first render.
    // This prevents an initial state where markers show but labels
    // don't appear until the user interacts with the map.
    addLabelMarkers(TypeLayer.UnitLabel, true, false);
  };

  /*******************************************************
   * Function: ensureWebGLVisible
   * Description: Guarantees the WebGL overlay is rendering.
   *              Safe to call multiple times — no-ops if already visible.
   *              Centralises the "restore WebGL" logic so every caller
   *              (showUnitsByPreferences, showVehicleTrails, etc.) gets
   *              correct behaviour without duplicating the 3-step chain.
   *******************************************************/
  const ensureWebGLVisible = () => {
    if (!USE_WEBGL_MARKERS || !webglLayer) return;
    // Don't restore WebGL markers while Vehicle Trails is active —
    // showVehicleTrails(false) will restore them when trails are dismissed.
    if (showVehicleTrailsRef.current) return;
    // Don't reset _hideUnitsOnly — route viewing keeps the overlay
    // active but suppresses unit markers.  setVisible(true) would
    // clear that flag and break route-stop-only rendering.
    if (webglLayer._hideUnitsOnly) return;
    if (webglLayer._hidden) {
      webglLayer.setVisible(true);
      updateWebGLMarkers();
    }
  };

  /*******************************************************
   * Function: isPolygonInBounds
   * Description: Checks if a polygon is within the current map bounds.
   * Parameters:
   *  - polygonObject: The polygon object to evaluate.
   *  - currentBounds: The current visible bounds of the map.
   *  - boundsValue: Is used only for circle objects, to check if the center is within bounds.
   * Returns:
   *  - Boolean indicating whether the polygon is within bounds.
   *******************************************************/
  const isPolygonInBounds = (polygonMapObject, currentBounds, boundsValue) => {
    const polygonObject = polygonMapObject?.childInstance || polygonMapObject?.instance;
    if (!polygonObject || !currentBounds) return false;

    if (!(polygonObject instanceof google.maps.Polygon)) {
      //console.error("The provided object is not a valid polygon.");
      return false;
    }

    // Create the bounding box of the polygon
    const envelop = polygonMapObject?.envelop;

    // Check if the polygon's bounding box intersects with the map bounds
    return !(
      envelop.north < currentBounds.south ||
      envelop.south > currentBounds.north ||
      envelop.east < currentBounds.west ||
      envelop.west > currentBounds.east
    );
  };

/*******************************************************
 * Function: isCircleInBounds
 * Description: Checks if a circle is within the current map bounds.
 * Parameters:
 *  - circleObject: The circle object to evaluate.
 *  - currentBounds: The current visible bounds of the map.
 * Returns:
 *  - Boolean indicating whether the circle is within bounds.
 *******************************************************/
  const isCircleInBounds = (circleMapObject, currentBounds, boundsValue) => {
    const circleObject = circleMapObject?.childInstance || circleMapObject?.instance;
    if (!(circleObject instanceof google.maps.Circle)) {
      //console.error("The provided object is not a valid circle.");
      return false;
    }

    // Create the bounding box of the polygon
    const envelop = circleMapObject?.envelop;
    const center = circleMapObject?.objectCenter;

    // Check if the center is within the bounds
    if (boundsValue.contains(center)) {
      return true;
    }


    // Check if the circle's bounding box intersects with the map bounds
    return !(
      envelop.north < currentBounds.south ||
      envelop.south > currentBounds.north ||
      envelop.east < currentBounds.west ||
      envelop.west > currentBounds.east
    );
  };

  const hiddenMarkersOnMap = useCallback((currentBounds) => {
    // When MarkerClusterer is active it manages marker visibility internally
    // via SuperClusterAlgorithm. Skip all unit marker management to avoid
    // conflicting setMap() calls which strip markers before the clusterer
    // can collect them.
    const objectCluster = getObject(TypeObjectMap.MarkerCluster, clusterId);
    if (objectCluster?.instance) return;

    // In WebGL mode, deck.gl handles viewport culling natively on the GPU.
    // Individual unit markers must stay hidden (setMap(null)) to avoid
    // double-rendering on top of the WebGL layer.
    if (USE_WEBGL_MARKERS) return;

    if (mapItem && currentBounds) {
      let isValidPosition = false, latInstance, lngInstance;
      //filter markers objects
      mapObjects.forEach((item) => {
        if (item.type == TypeObjectMap.Marker && item.layerName == TypeLayer.Units && item.instance) {
          isValidPosition = false;
          latInstance = item?.instance?.getPosition()?.lat() || item?.position?.lat;
          lngInstance = item?.instance?.getPosition()?.lng() || item?.position?.lng;

          const position = new google.maps.LatLng(latInstance, lngInstance); // eslint-disable-line no-undef

          isValidPosition = currentBounds.contains(position);

          item?.instance?.setMap(isValidPosition && item?.enable && !item?.hideMarker ? mapItem : null);
          if (item?.hasChild) {
            item?.childInstance?.setMap(isValidPosition && item?.enable && !item?.hideMarker ? mapItem : null)
          }
          //if show label
          const objectLabel = getObject(TypeObjectMap.Label, `label_${item.id}`)
          if(objectLabel){
            if(isValidPosition && item?.enable && !item?.hideMarker){
              objectLabel.instance.onDisplayObject(true, mapItem);
            }else{
              objectLabel?.instance?.onDisplay(false);
              objectLabel?.instance?.setMap(null);
            }            
          }
        }
      })
    }
  }, [mapItem])

  /*******************************************************
   * Remove the cluster event 
  *******************************************************/
  const removeMapBoundsChangedListener = useCallback(() => {
    if (mapBoundsChangedEvent) {
      google.maps.event.removeListener(mapBoundsChangedEvent); // eslint-disable-line no-undef
      mapBoundsChangedEvent = null;
    }
    if (mapZoomChangedEvent) {
      google.maps.event.removeListener(mapZoomChangedEvent); // eslint-disable-line no-undef
      mapZoomChangedEvent = null;
    }
    if (zoomChangedDebounceTimer) {
      clearTimeout(zoomChangedDebounceTimer);
      zoomChangedDebounceTimer = null;
    }
  })

  /*******************************************************
   * Remove the map cluster Event
  *******************************************************/
  const removeMapClusterUpdateEvent = useCallback(() => {
    if (mapClusterUpdateEvent) {
      clearInterval(mapClusterUpdateEvent);
      mapClusterUpdateEvent = null;
    }
  }, [mapClusterUpdateEvent, mapItem])

  /*******************************************************
   * Remove the cuslter event 
  *******************************************************/
  const removeMapDraw = useCallback(() => {
    if (mapMouseUpEvent && mapMouseDownEvent) {
      google.maps.event.removeListener(mapMouseUpEvent); // eslint-disable-line no-undef
      google.maps.event.removeListener(mapMouseDownEvent); // eslint-disable-line no-undef
      google.maps.event.clearListeners(mapItem.getDiv(), 'mousedown');
      enable();
      mapMouseUpEvent = null;
      mapMouseDownEvent = null;
    }
  })

  /*******************************************************
   * Center the vehicles into the map
  *******************************************************/
  const centerVehicles = useCallback(() => {
    const vehicles = mapObjects.filter(
      item => item.type == TypeObjectMap.Marker &&
        item.layerName == TypeLayer.Units &&
        item.position.lat &&
        item.position.lng &&
        item?.enable)
    if (vehicles && vehicles.length > 0) {
      const latLng = vehicles.map(item => {
        return { lat: item.position.lat, lng: item.position.lng }
      })
      centerByFitBounds(latLng)
    }
  })
  /*******************************************************
   * Get the projection of the map    
   * @param {Object} map
   * @return {Object} projection
   * *******************************************************/
    const getOverlayProjection = (map) => {
      const overlay = new google.maps.OverlayView();
      overlay.onAdd = () => {};
      overlay.draw = () => {};
      overlay.setMap(map);
      return overlay.getProjection();
    };

    /*******************************************************
    * Center the map animated
    *******************************************************/
    const centerMapAnimated = useCallback((lat, lng) => {

      //is not follow unit, is not necessary to center the map
      if(!mapItem || !followUnitRef.current) return;

      //validate is number
      if (!(!isNaN(lat) && !isNaN(lng))) return;

      const center = new google.maps.LatLng(lat, lng);

      // During trail animation, use instant setCenter to avoid panTo animation
      // fighting with requestAnimationFrame updates (causes marker flickering)
      const moveCenter = _animatingFollowUnit != null
        ? (c) => mapItem.setCenter(c)
        : (c) => mapItem.panTo(c);

      if (openInfoWindowRef.current) {
        const projection = getOverlayProjection(mapItem);
        if (!projection) {
          moveCenter(center);
          return;
        }

        const point = projection.fromLatLngToContainerPixel(center);
        if (!point) {
          moveCenter(center);
          return;
        }

        // Match the same visual offset used when opening a unit from click,
        // so the followed marker stays a bit below center and the modal has room.
        point.y -= 190;
        const adjustedCenter = projection.fromContainerPixelToLatLng(point);
        moveCenter(adjustedCenter);
        return;
      }

      moveCenter(center);
    }, [mapItem]);

  /*******************************************************
  * Center map by lat lng positions
  *******************************************************/
  const centerByFitBounds = useCallback((arrayLatLng, radius = 0) => {
    let center = null

    if (arrayLatLng) {
      const LatLngList = []
      try {
        arrayLatLng.forEach((element) => {
          if (!isNaN((element.lat || element.latitude)) && !isNaN((element.lng || element.longitude))) {
            LatLngList.push(new google.maps.LatLng(element.lat ? element.lat : element.latitude, // eslint-disable-line no-undef
              element.lng ? element.lng : element.longitude))
          }
        })
      } catch (error) {
        console.log('error ***', error.toString())
      }

      if (LatLngList && LatLngList.length == 0) return;

      let latlngbounds = new google.maps.LatLngBounds(); // eslint-disable-line no-undef

      LatLngList.forEach((latLng) => {
        latlngbounds.extend(latLng);
      });

      mapItem.setCenter(latlngbounds.getCenter());
      center = latlngbounds.getCenter();
      if (radius == 0) {
        mapItem.fitBounds(latlngbounds);
      } else {
        var circle = new google.maps.Circle({ // eslint-disable-line no-undef
          map: mapItem,
          radius: radius,
          center: LatLngList[0]
        });
        mapItem.fitBounds(circle.getBounds());
        circle.setMap(null);
        circle = null;
      }
    }
    return center;
  })

  /*******************************************************
   * Draw Route into the realtime maps.
  *******************************************************/
  const drawRoute = useCallback((propsMap, mapObjectRoute, points, errorDrawRoute, otherProps = {}) => {
    
    const directionsRenderer = mapObjectRoute.instance
    let updatePointers = false;
    let labelOnMarker = ''

    const isRouteModule = otherProps && otherProps?.routeModule

    if (points && points.length > 1) {
      var directionsService = new google.maps.DirectionsService(); // eslint-disable-line no-undef

      //start route
      const start = isRouteModule ? getPositionMarker(points[0].id, points[0]?.layerName) : getPositionMarker(points[0].id);
      //end route
      const end = isRouteModule ? getPositionMarker(points[points.length - 1].id, points[0]?.layerName) : getPositionMarker(points[points.length - 1].id);

      if (!start || !end) {
        console.warn('[drawRoute] Aborted — missing position.', {
          startId: points[0]?.id,
          endId: points[points.length - 1]?.id,
          layerName: points[0]?.layerName,
          start: !!start,
          end: !!end,
          fsmRouteMarkers: mapObjects.filter(o => o.layerName === TypeLayer.FSMRoute && o.type === TypeObjectMap.Marker).length,
        });
        return;
      }

      const arrayWaypoints = [];

      if (points.length == 2) {
        points.forEach((item) => {
          labelOnMarker = !labelOnMarker ? item?.label : labelOnMarker
          if (!updatePointers)
            updatePointers = item?.updatePointers ? true : false
        })
      }
      
      if (points.length > 2) {
        points.forEach((item, index) => {
          if (index != 0 && index != points.length - 1) {
            const position = isRouteModule ? getPositionMarker(item.id, points[0]?.layerName || null) : getPositionMarker(item.id);
            if (position) {
              arrayWaypoints.push({
                location: position,
                stopover: true
              })
            }
          }
        })
      }

      let propsToRoute = {
        ...otherProps
      }

      //get the limit
      let limit = 0;
      if (propsToRoute.limit) {
        limit = propsToRoute.limit;
        //remove the props no valid to the structure
        delete propsToRoute['limit'];
      }
      //get the color
      let color = "";
      if (propsToRoute.color) {
        color = propsToRoute.color;
        //remove the props no valid to the structure
        delete propsToRoute['color'];
      }

      //exclude this properties because can be generate error in route driving directions api
      delete propsToRoute['draggable'];
      delete propsToRoute['hideLabelsOnMarkers'];
      delete propsToRoute['onErrorRoute'];
      delete propsToRoute['saveCoordinates'];
      delete propsToRoute['drawSpeedLimits'];
      delete propsToRoute['isSpeedOverride'];
      delete propsToRoute['startPointColor'];
      delete propsToRoute['endPointColor'];      
      delete propsToRoute['callbackWayPoints'];
      delete propsToRoute['showDirectionsPanel'];
      delete propsToRoute['callbackDistancePoints'];
      delete propsToRoute['noDrawMap'];
      delete propsToRoute['preserveViewport'];
      delete propsToRoute['drawLineRoute'];
      delete propsToRoute['driverId'];

      const routeLanguage = propsToRoute?.language ?? otherProps?.language ?? otherProps?.locale ?? props?.language ?? props?.locale;      
      var request = {
        ...propsToRoute,
        origin: start,
        destination: end,
        waypoints: arrayWaypoints,
        provideRouteAlternatives: false,
        travelMode: google.maps.TravelMode.DRIVING, // eslint-disable-line no-undef
        unitSystem: props?.isKph ? google.maps.UnitSystem.METRIC : google.maps.UnitSystem.IMPERIAL,
        ...(routeLanguage ? { language: routeLanguage } : {})
      };

      //implements new route module
      if(isRouteModule){

        if(mapObjectRoute?.mapRoute){
          mapObjectRoute.mapRoute.removeRoute()
          mapObjectRoute.mapRoute = null;
        }

        let newProps = {}
        if(otherProps){
          newProps = {
            ...otherProps,
            opUpdateDirectionRenderer : (instanceDirectionRenderer) => {
              const validateInstanceRouteOnMap = getObject(TypeObjectMap.Routes, mapObjectRoute.id)
              if(validateInstanceRouteOnMap){
                validateInstanceRouteOnMap.instance = instanceDirectionRenderer; 
              }else if(instanceDirectionRenderer){
                //clear old instances for direction renderer
                instanceDirectionRenderer.setMap(null);
              }
            }
          }
          
        }

        const instanceMapRoute = new MapRoute({ 
          mapItem, 
          propsMap, 
          directionsRenderer, 
          points, 
          errorDrawRoute, 
          otherProps : newProps,
          request,
          googleProviderReference: instance
        })
        
        instanceMapRoute.drawRoute();

        //set the instance into mapObject
        mapObjectRoute.mapRoute = instanceMapRoute; 
        return;
      }

      directionsService.route(request, function (response, status) {
        if (status == 'OK') {

          //validate limit in miles
          if (response && limit > 0) {
            const toMiles = (response.routes[0].legs[0].distance.value / 1609).toFixed(1);
            if (toMiles > limit) {
              //remove the last point on map
              otherProps?.onErrorRoute && otherProps?.onErrorRoute(points)
              //no draw route
              const existMarker = getObject(TypeObjectMap.Marker, `temporalRouteMarker_startMarker`)

              if (!existMarker) {
                var startMarker = new MarkerCompat({
                  position: response.routes[0].legs[0].start_location,
                  map: mapItem,
                  draggable: false,
                  icon: {
                    url: otherProps?.isSpeedOverride ? `/assets/icons/pin_destination.png` : `/assets/icons/pin_vehicle.png`,
                    scaledSize: new google.maps.Size(30, 30),
                    size: new google.maps.Size(30, 30),
                    anchor: new google.maps.Point(15, 15)
                  }
                });
                //create temporal marker
                tempMapObjectRoute('startMarker', startMarker, { lat: response.routes[0].legs[0].start_location.lat(), lng: response.routes[0].legs[0].start_location.lng() });
              }

              return false
            }
          }

          //into the draw Speed Limits on map
          if(otherProps?.drawSpeedLimits && response && response?.routes && response?.routes[0]){
            const route = response.routes[0];
            const polyline = route?.overview_polyline;
            const path = google.maps.geometry.encoding.decodePath(polyline);
            //const encodedPath = google.maps.geometry.encoding.encodePath(path);

            dispatch(getMapSpeedLimitsForPaths({ path, callback: onDrawSpeedLimits }))
          } else {
            directionsRenderer.setDirections(response);
          }

          if (otherProps?.saveCoordinates) {
            // get coordinates from the route
            const routeCoordinates = getRouteCoordinatesFromDirectionService(response)
            //delete old object route
            removeMapObject('routeCoordinates', TypeObjectMap.Routes)
            //create the object again
            tempMapObjectRoute('', null, routeCoordinates[0], 'routeCoordinates', routeCoordinates);
          }



          if (color != "") {
            directionsRenderer.setOptions({
              polylineOptions: {
                strokeColor: color,
                strokeOpacity: 0.7,
                strokeWeight: 5
              }
            })
          }

          if (updatePointers) // draw ETA
            drawCustomRoute(directionsRenderer, response, labelOnMarker, points, otherProps)

        }
        if (status == 'ZERO_RESULTS') {
          if (errorDrawRoute) {
            errorDrawRoute(propsMap, 'realtimemaps_route_error')
          }
        }
        otherProps?.callbackWayPoints && returnOptimizeRoutePoints(response, otherProps?.callbackWayPoints)
      });

      //Remove the points into the map
      if (!updatePointers) {
        removeLayer(TypeLayer.Pointers)
      }

      return true
    }
  })

  const onDrawSpeedLimits = (data, googleSpeedLimits) => {
    let speed_limit = null;
    let pathCoordinates = [];
    let firstChange = false;

    let speedInMiles = 0
    data.map((item, index) => {

      if (speed_limit == null) {
        speed_limit = item.speedLimit;
      }

      if (speed_limit == item.speedLimit) {

        if (index > 0 && firstChange) {
          pathCoordinates.push({
            lat: data[index - 1].lat,
            lng: data[index - 1].lng
          })
        }

        pathCoordinates.push({
          lat: item.lat,
          lng: item.lng
        });
      } else {
        firstChange = true;

        pathCoordinates.push({
          lat: item.lat,
          lng: item.lng
        });
        speedInMiles = speed_limit * 0.621371;
        addMapObject(new MapObject().buildPolyLine({
          id: `drawSpeedRoute${index}`,
          coordinates: pathCoordinates,
          layerName: TypeLayer.RouteLayer,
          heading: 0,
          color: getRouteColorFromSpeed(Math.round(speedInMiles)).color,
          fillOpacity: 0.8,
          strokeWeight: 6
        }));

        const lastElement = pathCoordinates.pop()
        pathCoordinates = [];
        pathCoordinates.push(lastElement)
        speed_limit = item.speedLimit;
      }
    });

    //add the last element before the map
    if (pathCoordinates && pathCoordinates.length > 0) {
      speedInMiles = speed_limit * 0.621371;
      addMapObject(new MapObject().buildPolyLine({
        id: `drawSpeedRoute${data.length}`,
        coordinates: pathCoordinates,
        layerName: TypeLayer.RouteLayer,
        heading: 0,
        color: getRouteColorFromSpeed(Math.round(speedInMiles))?.color,
        fillOpacity: 0.8,
        strokeWeight: 6
      }));

    }

    const keyOfSettingsRoute = 'drawSpeedRoute_dataForGoogleSpeed';
    removeMapObject(keyOfSettingsRoute, TypeObjectMap.Settings)
    //saved the speed limits into map objects
    const objectSettings = new MapObject().buildSettings({
      id: keyOfSettingsRoute,
      enable: true,
      layerName: TypeLayer.RouteLayer,
      value: JSON.stringify(googleSpeedLimits)
    });
    mapObjects.push(objectSettings);
    _indexAdd(objectSettings);

  }



  /*******************************************************
   * Add the options to draw the custom markers and colors
   * associated to the route using the directionsRenderer object
  *******************************************************/
  const drawCustomRoute = (directionsRenderer, response, labelOnMarker, points, otherProps = {}) => {

    directionsRenderer.setOptions({
      suppressMarkers: true,
      preserveViewport: true
    });

    const { draggable, hideLabelsOnMarkers, drawSpeedLimits, isSpeedOverride } = otherProps

    const toMiles = (response.routes[0].legs[0].distance.value / 1609).toFixed(1);
    const toKilometers = (response.routes[0].legs[0].distance.value / 1000).toFixed(1);

    const distance = props.isKph ? toKilometers : toMiles;
    const speedFormat = props.isKph ? messages['kilometers'] : messages['miles'];

    const valueTime = convertSecondsToTime(response.routes[0].legs[0].duration.value || 0)

    const timeFormatToShow = `
          ${valueTime?.days > 0 ? `${valueTime?.days} ${valueTime?.days == 1 ? props?.messages['Day'] : props?.messages['Days']}` : ''} 
          ${valueTime?.hour > 0 ? `${valueTime?.hour} ${valueTime?.hour == 1 ? props?.messages['hour'] : props?.messages['hours']}` : ''} 
          ${valueTime?.min > 0 ? `${valueTime?.min} ${valueTime?.min == 1 ? props?.messages['min'] : props?.messages['mins']}` : ''} `

    removeRouteListener()

    durationRoute = response.routes[0].legs[0].duration.value;

    if(points[0]?.isUnit){
      if(points.length){
        const position = validateLatLng(points[0]);
        addMapObject(new MapObject().buildMarker({
          id: '1',
          deviceId: points[0]?.id,
          position,
          layerName: TypeLayer.Units,
          label: points[0]?.label,
          heading: points[0]?.heading,
          color: validateEventColor(points[0]),
          eventCode: points[0]?.eventCode,
          eventName: points[0]?.eventName,
          draggable: points[0]?.draggable || false,
          updatePointers: false,
          hasVideo: false,
          hideMarker: false,
          value: '',
          hasChild: false,
          isRouteTracker: false,
          trail:  null,
          trailTimestamp: 0,
          timeFormatToShow: timeFormatToShow,
          distance: distance,
          speedFormat: speedFormat
        }))
      }
    }else {
      var startPoint = new MarkerCompat({
        position: response.routes[0].legs[0].start_location,
        map: mapItem,
        icon: {
          url: `${pathFile}/icons/maps/trip_origin/253858.png`,
          scaledSize: new google.maps.Size(15, 15),
          size: new google.maps.Size(15, 15),
          anchor: new google.maps.Point(8, -4)
        }
      });
  
      eventMouseOverFirstPoint = startPoint.addListener('mouseover', function () {
        startPoint.setLabel({
          color: '#FFFFFF',
          className: 'labelRouteMarker',
          text: labelOnMarker ? labelOnMarker.toString() : '',
        });
      });
  
      // assuming you also want to hide the infowindow when user mouses-out
      eventMouseOutFirstPoint = startPoint.addListener('mouseout', function () {
        startPoint.setLabel("");
      });
  
      tempMapObjectRoute('startPoint', startPoint, { lat: response.routes[0].legs[0].start_location.lat(), lng: response.routes[0].legs[0].start_location.lng() });
  
      var startMarker = new MarkerCompat({
        position: response.routes[0].legs[0].start_location,
        map: mapItem,
        draggable: draggable || false,
        icon: {
          url: isSpeedOverride ? `/assets/icons/pin_destination.png` : `/assets/icons/pin_vehicle.png`,
          scaledSize: new google.maps.Size(30, 30),
          size: new google.maps.Size(30, 30),
          anchor: new google.maps.Point(15, 15)
        }
      });
  
      if (draggable) {
        startPointMarkerDragend = startMarker.addListener('dragend', function (event) {
          const { lat, lng } = event.latLng.toJSON();
          let tempPointers = [...points];
          tempPointers[0].lat = parseFloat(lat || 0)
          tempPointers[0].lng = parseFloat(lng || 0)
          tempPointers[0].label = "" //clear label to search address again
          //dispatch(addPointers([]));
          dispatch(addPointers([...tempPointers]));
        });
      }
  
      if (hideLabelsOnMarkers) {
        eventMouseOverFirstMarker = startMarker.addListener('mouseover', function () {
          startPoint.setLabel({
            color: '#FFFFFF',
            className: 'labelRouteMarker',
            text: labelOnMarker ? labelOnMarker.toString() : '',
          });
        });
  
        // assuming you also want to hide the infowindow when user mouses-out
        eventMouseOutFirstMarker = startMarker.addListener('mouseout', function () {
          startPoint.setLabel("");
        });
      }
  
      if (!drawSpeedLimits) {
        infoWindowMarker = new google.maps.InfoWindow({
          content: `<div ${props?.isMobile ? 'style="margin: 3px;"' : ''}>
                      <p style="color: green; font-family: Outfit, Arial, sans-serif; font-size: 16px; font-weight: 700; margin: 0px;">
                        ${timeFormatToShow}
                      </p>
                      <p style="color: black; font-family: Outfit, Arial, sans-serif; font-size: 12px; font-weight: 500; margin: 0px;">
                        ${distance} ${speedFormat}
                      </p>
                      </div>`,
          shouldFocus: false,
        });
  
        eventInfoWindow = startMarker.addListener('click', () => {
          infoWindowMarker && infoWindowMarker.open({ anchor: startMarker, mapItem, shouldFocus: false });
        });
      }
  
  
  
      tempMapObjectRoute('startMarker', startMarker, { lat: response.routes[0].legs[0].start_location.lat(), lng: response.routes[0].legs[0].start_location.lng() });
    }

    

    var endPoint = new MarkerCompat({
      position: response.routes[0].legs[0].end_location,
      map: mapItem,
      icon: {
        url: `${pathFile}/icons/maps/trip_origin/253858.png`,
        scaledSize: new google.maps.Size(15, 15),
        size: new google.maps.Size(15, 15),
        anchor: new google.maps.Point(8, -4)
      }
    });
    tempMapObjectRoute('endPoint', endPoint, { lat: response.routes[0].legs[0].end_location.lat(), lng: response.routes[0].legs[0].end_location.lng() });

    var endMarker = new MarkerCompat({
      position: response.routes[0].legs[0].end_location,
      map: mapItem,
      draggable: draggable || false,
      icon: {
        url: isSpeedOverride ? `/assets/icons/pin_vehicle.png` : `/assets/icons/pin_destination.png`,
        scaledSize: new google.maps.Size(30, 30),
        size: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15)
      }
    });
    tempMapObjectRoute('endMarker', endMarker, { lat: response.routes[0].legs[0].end_location.lat(), lng: response.routes[0].legs[0].end_location.lng() });

    if (draggable) {
      endPointMarkerDragend = endMarker.addListener('dragend', function (event) {
        const { lat, lng } = event.latLng.toJSON();
        let tempPointers = [...points];
        tempPointers[1].lat = parseFloat(lat || 0)
        tempPointers[1].lng = parseFloat(lng || 0)
        tempPointers[1].label = "" //clear label to search address again
        //dispatch(addPointers([]));
        dispatch(addPointers([...tempPointers]));
      });
    }

    if (!drawSpeedLimits) {
      if (props?.isMobile) {
        infoWindowMarker && infoWindowMarker.open({ anchor: startMarker, mapItem, shouldFocus: false });
        setTimeout(() => {
          infoWindowMarker && infoWindowMarker.open({ anchor: startMarker, mapItem, shouldFocus: false });
        }, 500);
      } else {
        infoWindowMarker && infoWindowMarker.open({ anchor: startMarker, mapItem, shouldFocus: false });
      }
    }

  }

  /*******************************************************
   * add the instance markers objects into the map Objects 
   * associated to the route
  *******************************************************/
  const tempMapObjectRoute = (id, instance, position, completeId = '', coordinates = []) => {
    const newMapObject = new MapObject().buildRoute({
      id: completeId ? completeId : `temporalRouteMarker_${id}`,
      position: {
        lat: parseFloat(position?.lat || 0),
        lng: parseFloat(position?.lng || 0)
      },
      layerName: TypeLayer.RouteLayer,
      heading: 0,
      color: '#B0B0B0',
      instance: instance,
      coordinates: coordinates
    })
    mapObjects.push(newMapObject)
    _indexAdd(newMapObject);
  }

  /*******************************************************
   * Update the color at the route
  *******************************************************/
  const updateRouteStyles = useCallback((routeProperties, id = "") => {
    let objectRoute = null;
    if (id) {
      objectRoute = mapObjects.find((item) => item.id == id && item.type == TypeObjectMap.Routes && item.instance)
    } else {
      objectRoute = mapObjects.find((item) => item.type == TypeObjectMap.Routes && item.instance)
    }

    if (!objectRoute) return

    //remove the route at the map
    objectRoute.instance.setMap(null)

    objectRoute.instance.setOptions({
      polylineOptions: {
        strokeColor: routeProperties?.color,
        strokeOpacity: 0.7,
        strokeWeight: 5
      }
    })

    //repaint route on map
    objectRoute.instance.setMap(mapItem)

  })

  /*******************************************************
   * Get the positions at the marker
  *******************************************************/
  const getPositionMarker = useCallback((id, typeLayer = TypeLayer.Pointers) => {
    let pos = null;
    let mapObjectMarker = mapObjects.find((item) => item.id == id && item.layerName == typeLayer && item.type == TypeObjectMap.Marker);
    if (mapObjectMarker && mapObjectMarker.instance && mapObjectMarker.instance.getMap()) {
      pos = new google.maps.LatLng(mapObjectMarker.instance.getPosition().lat(), // eslint-disable-line no-undef
        mapObjectMarker.instance.getPosition().lng());
    }else if(mapObjectMarker?.instance?.getPosition()){
      //when the unit is not show on map
      pos = new google.maps.LatLng(mapObjectMarker.instance.getPosition().lat(), // eslint-disable-line no-undef
        mapObjectMarker.instance.getPosition().lng());
    }
    return pos;
  })

  /*******************************************************
   * Return the eta for the last route
  *******************************************************/
  const getDurationRoute = useCallback(() => {
    return durationRoute || 0;
  })

  /*******************************************************
   * Get the current zoom of the map
  *******************************************************/
  const getCurrentZoom = useCallback(() => {
    return mapItem?.getZoom();
  });

  /*******************************************************
   * Draw a polygon
  *******************************************************/
  const drawPolygon = useCallback(() => {
    if (!mapItem) return

    disable();
    mapMouseDownEvent = mapItem.addListener('mousedown', function (e) {
      removeMapObject('drawn_polygon', TypeObjectMap.Polygon);
      dispatch(setListUnits([], false));
      let poly = new google.maps.Polyline({ map: mapItem, clickable: false, strokeColor: '#00acfb', color: "#00acfb" });

      let move = google.maps.event.addListener(mapItem, 'mousemove', function (e) {
        poly.getPath().push(e.latLng);
      });

      mapMouseUpEvent = google.maps.event.addListenerOnce(mapItem, 'mouseup', function (e) {
        google.maps.event.removeListener(move);
        let path = poly.getPath();

        let bounds = [], coordinates = [];
        for (var i = 0; i < path.length; i++) {
          var point = [
            path.getAt(i).lat(),
            path.getAt(i).lng()
          ];
          coordinates.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() })
          bounds.push(point);
        }

        let units = getObjectsByTypeByLayer(TypeObjectMap.Marker, TypeLayer.Units); //lat, lng, id. 

        let unitSelection = []
        units.map((unit) => {
          if (pointInPolygon([unit.position.lat, unit.position.lng], bounds)) {
            unitSelection.push(+unit.id);
          }
        })
        if (unitSelection.length > 0) dispatch(setListUnits(unitSelection, false));
        else {
          dispatch(setListUnits([], true))
        }

        poly.setMap(null);

        addMapObject(new MapObject().buildPolygon({
          id: 'drawn_polygon',
          coordinates: coordinates,
          layerName: TypeLayer.UnitSelection,
          label: 'Create Label...',
          heading: 0,
          color: "#00acfb",
          fillOpacity: 0.01,
          strokeWeight: 2,
        }))
      });
    })
  });

  /*******************************************************
   * Get the geometry encoding for the object with coordinates in map
  *******************************************************/
  const getGeometryEncoded = useCallback((id = 'routeCoordinates', type = TypeObjectMap.Routes, layerName = TypeLayer.RouteLayer) => {
    const routeObject = mapObjects.find((item) => item.id == id && item.type == type && item.layerName == layerName)
    if (routeObject) {
      var encodedPolygon = google.maps.geometry.encoding.encodePath(routeObject.coordinates);
      return encodedPolygon
    }
    return null
  })

  /*******************************************************
   * draw the route asociated to the encoded Path
  *******************************************************/
  const drawPolygonWithEncodedPath = useCallback((encodedPath, color, startPoint, endPoint) => {

    if (!encodedPath) return;

    removeMapObject('polyline_decodedPath', TypeObjectMap.Polyline)
    removeLayer(TypeLayer.RouteLayer);

    var decodedPath = google.maps.geometry.encoding.decodePath(encodedPath);

    addMapObject(new MapObject().buildPolyLine({
      id: 'polyline_decodedPath',
      coordinates: decodedPath,
      layerName: TypeLayer.RouteLayer,
      heading: 0,
      color: color,
      fillOpacity: 0.7,
      strokeWeight: 5
    }));

    const coordinates = []
    decodedPath.forEach((item) => {
      coordinates.push({
        latitude: item.lat(),
        longitude: item.lng(),
      })
    })

    //center route on map
    centerByFitBounds(coordinates)

    //create start Route Marker Point
    var startMarker = new MarkerCompat({
      position: startPoint,
      map: mapItem,
      draggable: false,
      icon: {
        url: `/assets/icons/pin_destination.png`,
        scaledSize: new google.maps.Size(30, 30),
        size: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15)
      }
    });
    tempMapObjectRoute('temp_startMarker', startMarker, startPoint);

    //create end Route Marker Point
    var endMarker = new MarkerCompat({
      position: endPoint,
      map: mapItem,
      draggable: false,
      icon: {
        url: `/assets/icons/pin_vehicle.png`,
        scaledSize: new google.maps.Size(30, 30),
        size: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15)
      }
    });
    tempMapObjectRoute('temp_endMarker', endMarker, endPoint);

  })

  const drawHistoryTrailOnMap = (trailArray, layer = TypeLayer.VehicleTrailsTrailHistory) => {
    
    if(!isArray(trailArray) || trailArray.length < 0 ) return;

    let newIcon = IconMarkerVehicleTrailsPoint({ color: '#808080'}); //increase the number because start in 1
    let iconToRoutePoint = {
      // Convierte SVG to URL
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(newIcon), 
      scaledSize: new google.maps.Size(20, 20), // size of the icon
      anchor: new google.maps.Point(10, 10),
    };

    let mapObjectMarker = [];
    const arrayPositions = [];
    trailArray.forEach((item, index) => {
      const position = validateLatLng(item);
      arrayPositions.push(position)

      const mapObject = new MapObject().buildMarker({
        id: 'trailMarker' + index,
        position,
        layerName: layer,
        label: index,
        draggable: false,
        hideMarker: false,
        icon: iconToRoutePoint,
        optimized: true,
      })
      mapObjectMarker.push(mapObject)


    })
    addMapObjects(mapObjectMarker);

    //center the trails objects on the map
    //centerByFitBounds(arrayPositions);
  }

  const updateMarkersVisible = (markersIds, visible = false) => {

    if(!isArray(markersIds) || !markersIds.length) return;
    mapObjects.forEach((item) => {
      if(markersIds.includes(item.id.toString())){
        item.instance.setVisible(visible)
        item.hideMarker = !visible;
      }
    })
  }

  const enable = () => {
    mapItem.setOptions({
      draggable: true,
    });
  }

  const disable = () => {
    mapItem.setOptions({
      draggable: false,
    });
  }

  return (
    <Fragment>
      <div style={{ height: '100%', width: '100%' }} ref={divMap} className='map-container'></div>

      {/* Animation-zone indicator — visible when zoom >= threshold + animation setting is ON */}
      <div ref={animZoneIndicatorRef} className="anim-zone-indicator" style={{ display: 'none' }}>
        <span className="anim-zone-dot" />
        <span className="anim-zone-label">{messages['map_animation_zone'] || 'ANIMATION ZONE'}</span>
      </div>

      {/* Follow-active button — visible when follow mode is ON */}
      <div ref={followActiveIndicatorRef} className="follow-active-button mapButton" style={{ display: 'none' }}>
        <Tooltip placement="right" title={messages['map_stop_following'] || 'Stop following'}>
          <button
            type="button"
            onClick={() => dispatch(setFollowUnit(false))}
          >
            {/* MUI Visibility (eye) icon — follow is active */}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* Live-tracking indicator — visible when a followed unit is animating */}
      <div ref={liveIndicatorRef} className="live-tracking-indicator" style={{ display: 'none' }}>
        <span className="live-tracking-dot" />
        <span className="live-tracking-label">{messages['map_live'] || 'LIVE'}</span>
      </div>

      {/* zoom button */}
      <div className={!props.generatePrint ? "zoom-buttons-group" : "control-hidden"}>
        <div className="zoomButtonsIn mapButton">
          <Tooltip placement="left" title={messages['zoomIn']}>
            <button type="button" className='mapButtonZoom' id={'zoomIn-idcontrol'}>+</button>
          </Tooltip>
        </div>

        <div className="zoomButtonsOut mapButton">
          <Tooltip placement="left" title={messages['zoomOut']}>
            <button type="button" className='mapButtonZoom' id={'zoomOut-idcontrol'}>−</button>
          </Tooltip>
        </div>
      </div>
      {/* Map Settings button */}
      {
        <div id={'mapTagFilterApplied-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? 'mapButton' : "control-hidden"}>
          {props.tagFilterApplied}
        </div>
      }
      {
        HasPermission(user, "", [5]) && // TODO submodule-approved
        <div id={'mapSettings-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? 'mapButton' : "control-hidden"}>
          {props.mapSettings}
        </div>
      }
      {/* Draw button */}
      <div id={'draw-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? "mapButton" : "control-hidden"}>
        {props.drawPolygonMap}
      </div>
      {/* Layer button */}
      <div id={'layer-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? "mapButton" : "control-hidden"}>
        {props.layer}
      </div>
      {/* legends button */}
      {!props.hiddenLegends && (
        <div id={'legends-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink && !props.enableVehicleTrails ? 'controls legends-control' : "control-hidden"}>
          {props.legends}
        </div>
      )}
      {/* filter events map button */}
      <div 
        id={'filter-events-map-idcontrol'}
        style={{ 
          display: props?.showVehicleTrails ? 'flex' : 'none', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100px',
          height: '30px'
        }}
        className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? 'controls filter-events-map-control' : "control-hidden"}>
        {props?.showVehicleTrails && props.filterEventsMap}
      </div>
      <div style={{
        display: clientWithVideo && props?.showVehicleTrails ? '' : 'none',
        position: 'absolute',
        top: '0px',
        right: '0px',
        padding: '4px 42px',
      }} id={'clip-requests-drawer-idcontrol'}>
        {props?.clipRequestsDrawer}
      </div>
      {/* traffic button */}
      {!props.hiddenTraffic && (
        <div id={'traffic-idcontrol'} className={!props.generatePrint ? "controls traffic-control" : "control-hidden"}>
          <TrafficToggle
            onShowTraffic={showTraffic}
            onHideTraffic={hideTraffic}
            isTrafficEnabled={traffic}
          />
        </div>
      )}
      
      <div id={'weather-idcontrol'} className={!props.generatePrint ? "controls traffic-control" : "control-hidden"}>
      {(!props.noRealtime && !props.hiddenWeather && weatherPermision) &&
          <WeatherToggle
            onShowWeather={showWeather}
            onHideWeather={hideWeather}
            isWeatherEnabled={isWeatherEnabled}
          />
      }
      </div>

      {/* legends button */}
      <div id={'speed-override-idcontrol'} className={props.showSpeedOverride ? 'controls speed-override-control' : "control-hidden"}>
        {props.speedOverrideControl}
      </div>

      <div id={'route-info-idcontrol'} className={props.showRoutesInfoMap ? 'controls route-info-control' : "control-hidden"}>
        {props.routesInfoMapControl}
      </div>

      {/* Input search information*/}
      {!props.hiddenSearchMap && (
        <div id={'input_SearchMap'} className={!props.generatePrint ? "controlSearch inputsearch-control" : "control-hidden"}>
          {props.inputSearchMap}
        </div>
      )}
      {/* Map Pagination Control*/}
      <div id={'input_Pagination'} className={props.enableVehicleTrails ? 'controls pagination-control' : 'controls pagination-controlhidden'}>
        {props.mPagination}
      </div>

      {/* Map Settings button */}
      <div id={'draw_LayerMap'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? 'mapButton' : "control-hidden"}>
        {props.drawLayerMap}
      </div>

      {/* Map button Center Vehicles */}
      <div id={'center_vehicles'} className={!props.generatePrint && props.showButtonZoomVehicles ? 'mapButton' : "control-hidden"}>
        {props.zoomVehicles}
      </div>

      {/* Custom Fullscreen button — replaces native control (shadow DOM in vector maps) */}
      <div id={'fullscreen-idcontrol'} className={!props.generatePrint && !props.isMobile && !props.showEtaLink ? 'mapButton' : "control-hidden"}>
        <Tooltip placement="left" title={messages['map_fullscreen']}>
          <button type="button" id={'fullscreen-idcontrol-btn'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </Tooltip>
      </div>

      {props.showEtaLink && (
        <div id={'div_etaLink'} className={props.showEtaLink ? props.isMobile ? "etaLink-control etaLink-control-mobile" : "etaLink-control" : "control-hidden"}>
          {props.panelEtaLink}
        </div>
      )}

      {/* Units Label */}
      {/* <div>
        {props.unitLabel}
      </div> */}

      <div id={'info-bubble'}>
        <InfoWindow
          mapInstance={mapItem}
          openRef={openInfoWindowRef}
          overlayRef={overlayRef}
          contentRef={infoBubbleContentRef}
          loadingRef={infoBubbleLoadingRef}
          registerUpdate={(fn) => { infoBubbleUpdateRef.current = fn; }}
          messages={props.messages}
          isClipCardRef={props?.isClipCardRef}
          close={() => {
            hideMarkerModal();
          }}
        />
      </div>
    </Fragment>
  )
})


export default GoogleProvider